import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import Lottie from "lottie-react";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import {
    FaSearch,
    FaPlus,
    FaTimes,
    FaEdit,
    FaCheck,
    FaPause,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleLeft,
    FaAngleDoubleRight,
    FaTrash,
    FaUndoAlt,
    FaBan,
    FaExclamationTriangle,
    FaLink,
    FaEye,
    FaEyeSlash,
    FaPencilAlt,
    FaUpload,
} from "react-icons/fa";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import trailLoadingAnimation from "../assets/animations/trail-loading.json";

import useConfig from '../hooks/useConfig';

import { notifyUser } from "../utils/notifyUser";

const normalizeLegalDocumentCollection = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === "string") {
        return value
            .split(/[\n,]+/)
            .map((item) => item.trim())
            .filter(Boolean);
    }
    return value ? [value] : [];
};

const normalizeCampaignLegalUploads = (campaign) => {
    if (!campaign) return campaign;

    let legalUploads = campaign.legal_uploads;
    if (typeof legalUploads === "string") {
        try {
            legalUploads = JSON.parse(legalUploads);
        } catch {
            legalUploads = {};
        }
    }

    legalUploads = legalUploads && typeof legalUploads === "object"
        ? legalUploads
        : {};

    return {
        ...campaign,
        legal_uploads: {
            ...legalUploads,
            form_c: normalizeLegalDocumentCollection(legalUploads.form_c),
            subscription_agreement: normalizeLegalDocumentCollection(
                legalUploads.subscription_agreement,
            ),
            financials: normalizeLegalDocumentCollection(legalUploads.financials),
        },
    };
};

const CampaignApproval = ({ user }) => {
    const { getConfig } = useConfig();
    const isDocuSignIntegrated =
        getConfig('INTEGRATED_DOCUSIGN')?.toLowerCase() === 'true';
    const [showActionsDropdown, setShowActionsDropdown] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [filteredCampaigns, setFilteredCampaigns] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("all");
    const [draftFilter, setDraftFilter] = useState("all");
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [campaignDetails, setCampaignDetails] = useState(null);
    const [campaignDraft, setCampaignDraft] = useState(null);
    const [showPurchaseHistoryModal, setShowPurchaseHistoryModal] =
        useState(false);
    const [purchaseHistoryLoading, setPurchaseHistoryLoading] = useState(false);
    const [purchaseHistoryModalData, setPurchaseHistoryModalData] = useState({
        offeringId: "",
        offeringDetail: {},
        purchaseHistoryDetails: {},
        purchaseHistory: [],
    });
    const [subscriptionSendModalData, setSubscriptionSendModalData] = useState({
        isOpen: false,
        offeringId: "",
        trade: null,
        templates: [],
        isEmpty: false,
    });
    const [subscriptionSendLoading, setSubscriptionSendLoading] =
        useState(false);
    const [subscriptionSendSubmitting, setSubscriptionSendSubmitting] =
        useState(false);
    const [showManageDocumentsModal, setShowManageDocumentsModal] =
        useState(false);
    const [manageDocumentsLoading, setManageDocumentsLoading] = useState(false);
    const [manageDocumentsMode, setManageDocumentsMode] = useState("documents");
    const [manageDocumentsData, setManageDocumentsData] = useState({
        offeringId: "",
        documents: [],
        isEmpty: false,
    });
    const [manageDocumentPreviewDoc, setManageDocumentPreviewDoc] =
        useState(null);
    const [manageDocumentNoPreviewDoc, setManageDocumentNoPreviewDoc] =
        useState(null);
    const [manageDocumentDownloading, setManageDocumentDownloading] =
        useState(false);
    const [replacementDocumentDragActive, setReplacementDocumentDragActive] =
        useState(false);
    const [manageDocumentUpdatingId, setManageDocumentUpdatingId] =
        useState(null);
    const [replacementTargetDocument, setReplacementTargetDocument] =
        useState(null);
    const [showDocSelectModal, setShowDocSelectModal] = useState(false);
    const [availableDocsForOffering, setAvailableDocsForOffering] = useState(
        [],
    );
    const [selectedDocsForOffering, setSelectedDocsForOffering] = useState({});
    const [docOfferingSubmitting, setDocOfferingSubmitting] = useState(false);
    const [docModalMode, setDocModalMode] = useState("documents");
    // Internal notes state
    const [internalNotes, setInternalNotes] = useState([]);
    const [internalNote, setInternalNote] = useState("");
    const [, setEditingNoteId] = useState(null);
    const [editingNoteText, setEditingNoteText] = useState("");
    const [, setNotesLoading] = useState(false);
    const [showNoteBox, setShowNoteBox] = useState(false);
    const [imageErrorMap, setImageErrorMap] = useState({});

    // Cancel investments inline panel state
    const [showCancelInvestmentsBox, setShowCancelInvestmentsBox] =
        useState(false);
    const [cancelInvestmentReason, setCancelInvestmentReason] = useState("");
    const [cancelInvestmentNotes, setCancelInvestmentNotes] = useState("");

    // Form C upload state
    const [formCFiles, setFormCFiles] = useState([]);
    const [formCUploading, setFormCUploading] = useState(false);
    const [showFormCUploadBox, setShowFormCUploadBox] = useState(false);

    // Subscription Agreement upload state
    const [subAgreementFiles, setSubAgreementFiles] = useState([]);
    const [subAgreementUploading, setSubAgreementUploading] = useState(false);
    const [showSubAgreementUploadBox, setShowSubAgreementUploadBox] =
        useState(false);

    // North Capital IDs state
    const [showNCForm, setShowNCForm] = useState(false);
    const [, setNCIssuerId] = useState("");
    const [, setNCOfferingId] = useState("");
    const replacementDocumentInputRef = useRef(null);
    const updateDocumentModalWidth = "36rem";
    const updateDocumentModalPadding = "1.75rem";

    // Inline edit state for Investment Details fields
    const [editingInvestmentField, setEditingInvestmentField] = useState(null);
    const [editingInvestmentValue, setEditingInvestmentValue] = useState("");
    const [adminUpdateTitle, setAdminUpdateTitle] = useState("");
    const [adminUpdateDescription, setAdminUpdateDescription] = useState("");
    const [adminUpdateUrl, setAdminUpdateUrl] = useState("");
    const [adminUpdateMedia, setAdminUpdateMedia] = useState("");
    const [adminUpdateFileName, setAdminUpdateFileName] = useState("");
    const [adminUpdateSaving, setAdminUpdateSaving] = useState(false);
    const [showAdminUpdateForm, setShowAdminUpdateForm] = useState(false);
    const adminUpdateFileInputRef = useRef(null);

    // Helper to close campaign modal and clear any inline edit state
    const closeCampaignModal = () => {
        setShowModal(false);
        setEditingInvestmentField(null);
        setEditingInvestmentValue("");
        // clear internal note editing state to avoid leakage between modals
        setEditingNoteId(null);
        setEditingNoteText("");
        setAdminUpdateTitle("");
        setAdminUpdateDescription("");
        setAdminUpdateUrl("");
        setAdminUpdateMedia("");
        setAdminUpdateFileName("");
        setShowAdminUpdateForm(false);
    };

    // Show admin warning if there are sandbox nc_record_status campaigns and API is in production
    useEffect(() => {
        const checkSandboxNCRecords = async () => {
            try {
                const { data } = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/sandbox-nc-record-count`,
                    { withCredentials: true },
                );
                // Print the backend count result
                if (data.count > 0) {
                    notifyUser(null, "warning", `Warning: There are ${data.count} active campaigns with nc_record_status set to 'sandbox' while the North Capital API is in production mode. Please review and update these campaigns to 'production' to avoid issues.`, "toast", null, null, 10000, null, null, false, false, "top-center");
                }
            } catch (err) {
                console.error("[NC Sandbox Check] API call failed:", err);
            }
        };
        checkSandboxNCRecords();
    }, []);
    useEffect(() => {
        fetchCampaignSummaries();
    }, []);

    useEffect(() => {
        filterAndPaginate();
    }, [
        campaigns,
        searchTerm,
        perPage,
        currentPage,
        statusFilter,
        draftFilter,
    ]);

    // Effect to update displayedOnHoldNote whenever campaignDetails changes
    useEffect(() => {
        if (campaignDetails && campaignDetails.status === "onhold") {
            console.error("Campaign status is onhold, checking for note");
            if (campaignDetails.on_hold_note) {
                console.error(
                    "Setting displayedOnHoldNote from campaignDetails.on_hold_note:",
                    campaignDetails.on_hold_note,
                );
                setDisplayedOnHoldNote(campaignDetails.on_hold_note);
            }
        }
    }, [campaignDetails]);

    // Fetch campaign data with user info
    const fetchCampaignSummaries = async () => {
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns-summary`,
                { withCredentials: true },
            );
            setCampaigns(sortByAuditUpdatedOn(data));
        } catch {
            notifyUser(null, "error", "Failed to load campaign summaries", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const [showOnHoldNote, setShowOnHoldNote] = useState(false);
    const [onHoldNote, setOnHoldNote] = useState("");
    const [displayedOnHoldNote, setDisplayedOnHoldNote] = useState("");

    // Campaign Internal Notes API (new endpoints)
    const fetchInternalNotes = async (campaignId) => {
        setNotesLoading(true);
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaign-notes?campaignId=${campaignId}`,
                { withCredentials: true },
            );
            setInternalNotes(data.notes || []);
        } catch {
            setInternalNotes([]);
        }
        setNotesLoading(false);
    };

    const handleSubmitInternalNote = async () => {
        if (!internalNote.trim()) return;
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaign-notes`,
                {
                    note: internalNote,
                    campaignId: campaignDetails.id,
                },
                { withCredentials: true },
            );
            setInternalNote("");
            setShowNoteBox(false);
            fetchInternalNotes(campaignDetails.id);
            notifyUser(null, "success", "Internal Note added successfully", "toast", null, null, null, null, null, false, false, "top-center");
        } catch {
            notifyUser(null, "error", "Failed to add internal note", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Handle Form C file upload
    const handleFormCUpload = async (e) => {
        e.preventDefault();
        if (formCFiles.length === 0) {
            notifyUser(null, "warning", "Please select at least one file to upload", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setFormCUploading(true);
        const formData = new FormData();

        // Append all selected files
        for (let i = 0; i < formCFiles.length; i++) {
            formData.append("legalDoc", formCFiles[i]);
        }

        // Add docType parameter
        formData.append("docType", "form_c");

        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignDetails.id}/upload-legal`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );
            // Reset form state
            setFormCFiles([]);
            setShowFormCUploadBox(false);
            setFormCUploading(false);

            // Refresh campaign details to show the new uploads
            await fetchCampaignDetails(campaignDetails.id);
            notifyUser(null, "success", "Form C documents uploaded successfully", "toast", null, null, null, null, null, false, false, "top-center");
        } catch (error) {
            console.error("Error uploading Form C documents:", error);
            setFormCUploading(false);
            notifyUser(null, "error", error.response?.data?.error ||
                    "Failed to upload Form C documents", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleEditNote = (noteId, currentText) => {
        setEditingNoteId(noteId);
        setEditingNoteText(currentText);
    };

    // Handle to cancel edit
    const handleCancelEdit = () => {
        setEditingNoteId(null);
        setEditingNoteText("");
    };

    // Save a single Investment Details field inline
    const handleSaveInvestmentField = async (field) => {
        try {
            await axios.patch(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/campaigns/${campaignDetails.id}/field`,
                { field, value: editingInvestmentValue },
                { withCredentials: true },
            );
            // Update local state so the UI reflects the new value immediately
            setCampaignDetails((prev) => ({
                ...prev,
                [field]: editingInvestmentValue,
            }));
            setEditingInvestmentField(null);
            notifyUser(null, "success", "Field updated successfully", "toast", null, null, null, null, null, false, false, "top-center");
        } catch {
            notifyUser(null, "error", "Failed to update field", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const getCampaignUpdates = (source = campaignDetails) => {
        const updates = source?.updates;
        if (!updates) return [];
        if (Array.isArray(updates)) return updates;
        try {
            const parsed = JSON.parse(updates);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    const getUpdateDescription = (update) => {
        if (typeof update === "string") return update;
        return update?.description || update?.text || "";
    };

    const normalizeExternalUrl = (url) => {
        if (!url) return "";
        return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    };

    const handleAdminUpdateMediaChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !campaignDetails?.id) return;

        const formData = new FormData();
        formData.append("updateMedia", file);
        setAdminUpdateSaving(true);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignDetails.id}/upload-update-media`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );

            setAdminUpdateMedia(
                `${process.env.REACT_APP_BACKEND_URL}/uploads/${res.data.path}`,
            );
            setAdminUpdateFileName(file.name);
            notifyUser(null, "success", "Update media uploaded.", "toast", null, null, null, null, null, false, false, "top-center");
        } catch (error) {
            console.error("Failed to upload update media:", error);
            notifyUser(null, "error", "Failed to upload update media.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setAdminUpdateSaving(false);
            event.target.value = "";
        }
    };

    const handleAddAdminUpdate = async () => {
        const hasContent =
            adminUpdateTitle.trim() ||
            adminUpdateDescription.trim() ||
            adminUpdateUrl.trim() ||
            adminUpdateMedia;

        if (!campaignDetails?.id || !campaignDetails?.application_id) {
            notifyUser(null, "error", "Campaign details are missing.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if (!hasContent) {
            notifyUser(null, "error", "Add a title, description, URL, or media before saving.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const update = {
            title: adminUpdateTitle.trim(),
            description: adminUpdateDescription.trim(),
            text: adminUpdateDescription.trim(),
            url: adminUpdateUrl.trim(),
            media: adminUpdateMedia || null,
            timestamp: new Date().toISOString(),
            created_by: user?.email || user?.id || "admin",
        };
        const updatedList = [update, ...getCampaignUpdates()];

        setAdminUpdateSaving(true);
        try {
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignDetails.id}`,
                {
                    application_id: campaignDetails.application_id,
                    user_id: user?.id,
                    updates: updatedList,
                },
                { withCredentials: true },
            );

            setCampaignDetails((prev) => ({
                ...prev,
                updates: updatedList,
            }));
            setAdminUpdateTitle("");
            setAdminUpdateDescription("");
            setAdminUpdateUrl("");
            setAdminUpdateMedia("");
            setAdminUpdateFileName("");
            setShowAdminUpdateForm(false);
            notifyUser(null, "success", "Campaign update added.", "toast", null, null, null, null, null, false, false, "top-center");
        } catch (error) {
            console.error("Failed to add campaign update:", error);
            notifyUser(null, "error", error.response?.data?.error || "Failed to add campaign update.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setAdminUpdateSaving(false);
        }
    };

    // Function to fetch campaign details
    const fetchCampaignDetails = async (campaignId) => {
        try {
            console.error("Fetching campaign details for ID:", campaignId);
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/with-draft`,
                { withCredentials: true },
            );
            console.error("Campaign data received:", res.data.campaign);

            // Log complete campaign details with on_hold_note specifically
            console.error(
                "Campaign on_hold_note from main response:",
                res.data.campaign.on_hold_note,
            );

            // Normalize legacy strings and newer document objects into arrays.
            setCampaignDetails(
                normalizeCampaignLegalUploads(res.data.campaign),
            );
            setCampaignDraft(normalizeCampaignLegalUploads(res.data.draft));
            setAdminUpdateTitle("");
            setAdminUpdateDescription("");
            setAdminUpdateUrl("");
            setAdminUpdateMedia("");
            setAdminUpdateFileName("");
            setShowAdminUpdateForm(false);

            // Check if the campaign already has on_hold_note directly from the campaign details response
            if (res.data.campaign.on_hold_note) {
                console.error(
                    "Setting displayedOnHoldNote from campaign details:",
                    res.data.campaign.on_hold_note,
                );
                setDisplayedOnHoldNote(res.data.campaign.on_hold_note);
            } else {
                // If not found in campaign details, make a dedicated API call
                console.error(
                    "No on_hold_note found in campaign details, fetching separately",
                );
                try {
                    // Make a dedicated API call to get the on-hold note
                    const noteRes = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/on-hold-note`,
                        { withCredentials: true },
                    );
                    console.error(
                        "On-hold note dedicated API response:",
                        noteRes.data,
                    );

                    if (noteRes.data && noteRes.data.on_hold_note) {
                        console.error(
                            "Setting displayedOnHoldNote from dedicated API call:",
                            noteRes.data.on_hold_note,
                        );
                        setDisplayedOnHoldNote(noteRes.data.on_hold_note);
                    } else {
                        console.error(
                            "No on-hold note found in dedicated API response",
                        );
                        setDisplayedOnHoldNote("");
                    }
                } catch (noteErr) {
                    console.error("Error fetching on-hold note:", noteErr);
                    setDisplayedOnHoldNote("");
                }
            }
        } catch (err) {
            console.error("Error fetching campaign details:", err);
            notifyUser(null, "error", "Failed to refresh campaign details", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Handle Form C document deletion
    const handleDeleteFormCDocument = async (filePath) => {
        if (
            !window.confirm(
                "Are you sure you want to delete this Form C document?",
            )
        ) {
            return;
        }

        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignDetails.id}/legal/form_c`,
                {
                    data: { filePath },
                    withCredentials: true,
                },
            );
            // Refresh campaign details to update the UI
            fetchCampaignDetails(campaignDetails.id);
            notifyUser(null, "success", "Form C document deleted successfully", "toast", null, null, null, null, null, false, false, "top-center");
        } catch (error) {
            console.error("Error deleting Form C document:", error);
            notifyUser(null, "error", error.response?.data?.error ||
                    "Failed to delete Form C document", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Handle Subscription Agreement file upload
    const handleSubAgreementUpload = async (e) => {
        e.preventDefault();
        if (subAgreementFiles.length === 0) {
            notifyUser(null, "warning", "Please select at least one file to upload", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        setSubAgreementUploading(true);
        const formData = new FormData();
        for (let i = 0; i < subAgreementFiles.length; i++) {
            formData.append("legalDoc", subAgreementFiles[i]);
        }
        formData.append("docType", "subscription_agreement");
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignDetails.id}/upload-legal`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );
            setSubAgreementFiles([]);
            setShowSubAgreementUploadBox(false);
            setSubAgreementUploading(false);
            await fetchCampaignDetails(campaignDetails.id);
            notifyUser(null, "success", "Subscription Agreement documents uploaded successfully", "toast", null, null, null, null, null, false, false, "top-center");
        } catch (error) {
            console.error(
                "Error uploading Subscription Agreement documents:",
                error,
            );
            setSubAgreementUploading(false);
            notifyUser(null, "error", error.response?.data?.error ||
                    "Failed to upload Subscription Agreement documents", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Handle Subscription Agreement file delete
    const handleDeleteSubAgreementDocument = async (filePath) => {
        if (
            !window.confirm(
                "Are you sure you want to delete this Subscription Agreement document?",
            )
        ) {
            return;
        }
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignDetails.id}/legal/subscription_agreement`,
                {
                    data: { filePath },
                    withCredentials: true,
                },
            );
            fetchCampaignDetails(campaignDetails.id);
            notifyUser(null, "success", "Subscription Agreement document deleted successfully", "toast", null, null, null, null, null, false, false, "top-center");
        } catch (error) {
            console.error(
                "Error deleting Subscription Agreement document:",
                error,
            );
            notifyUser(null, "error", error.response?.data?.error ||
                    "Failed to delete Subscription Agreement document", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Handle saving edited note
    const handleSaveEditedNote = async (noteId) => {
        if (!editingNoteText.trim()) return;
        try {
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaign-notes/${noteId}`,
                {
                    note: editingNoteText,
                },
                { withCredentials: true },
            );
            setEditingNoteId(null);
            setEditingNoteText("");
            fetchInternalNotes(campaignDetails.id);
            notifyUser(null, "success", "Internal Note updated successfully", "toast", null, null, null, null, null, false, false, "top-center");
        } catch (err) {
            notifyUser(null, "error", "Failed to update internal note", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Withdraw campaign handler
    const handleWithdrawCampaign = async (campaignId) => {
        const hasOffering = !!campaignDetails?.north_capital_offering_id;
        const result = await Swal.fire({
            title: "Withdraw Campaign?",
            text: hasOffering
                ? "This will withdraw the campaign and cancel the offering in North Capital. This action cannot be undone."
                : "This will withdraw the campaign and set its status to withdrawn.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, withdraw it!",
        });
        if (result.isConfirmed) {
            try {
                // If offering exists, cancel it in NC first — only proceed if successful
                const offeringId = campaignDetails?.north_capital_offering_id;
                if (offeringId) {
                    await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/api/offering/cancel-offering`,
                        { offeringId },
                        { withCredentials: true },
                    );
                }

                // Only set status to withdrawn if cancelOffering succeeded (or no offering)
                await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/withdraw-admin`,
                    {},
                    { withCredentials: true },
                );

                Swal.fire(
                    "Withdrawn!",
                    "Campaign has been withdrawn.",
                    "success",
                );
                closeCampaignModal();
                fetchCampaignSummaries();
            } catch (err) {
                Swal.fire(
                    "Error",
                    err.response?.data?.error || "Failed to withdraw campaign",
                    "error",
                );
            }
        }
    };

    // Approve entrepreneur's withdrawal request - triggers cancelOffering + sets status withdrawn
    const handleApproveWithdrawal = async (campaignId) => {
        const result = await Swal.fire({
            title: "Approve Withdrawal Request?",
            text: "This will approve the entrepreneur's withdrawal request, cancel the offering in North Capital, and set the campaign status to withdrawn.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d97706",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, approve withdrawal!",
        });
        if (result.isConfirmed) {
            try {
                await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/approve-withdrawal`,
                    {},
                    { withCredentials: true },
                );
                notifyUser(null, "success", "Withdrawal request approved. Campaign has been withdrawn.", "toast", null, null, null, null, null, false, false, "top-center");
                closeCampaignModal();
                fetchCampaignSummaries();
            } catch (err) {
                Swal.fire(
                    "Error",
                    err.response?.data?.error ||
                        "Failed to approve withdrawal request",
                    "error",
                );
            }
        }
    };

    // Cancel all investments for a withdrawn campaign
    const handleCancelCampaignInvestments = async (
        campaignId,
        reason,
        notes,
    ) => {
        if (!reason || !reason.trim()) {
            notifyUser(null, "warning", "Cancellation reason is required.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        if (!notes || !notes.trim()) {
            notifyUser(null, "warning", "Cancellation notes are required.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/cancel-investments`,
                { reason: reason.trim(), notes: notes.trim() },
                { withCredentials: true },
            );
            const { cancelled = 0, failed = 0 } = res.data || {};
            if (cancelled > 0) {
                notifyUser(null, "success", `${cancelled} investor${cancelled !== 1 ? "s" : ""} refunded successfully${failed > 0 ? `, ${failed} failed` : ""}.`, "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "info", res.data?.message || "No active investments to cancel.", "toast", null, null, null, null, null, false, false, "top-center");
            }
            setShowCancelInvestmentsBox(false);
            setCancelInvestmentReason("");
            setCancelInvestmentNotes("");
            fetchCampaignSummaries();
            if (campaignDetails?.id) fetchCampaignDetails(campaignDetails.id);
        } catch (err) {
            notifyUser(null, "error", err.response?.data?.error || "Failed to cancel investments", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Delete campaign handler
    const handleDeleteCampaign = async (campaignId) => {
        const hasOffering = !!campaignDetails?.north_capital_offering_id;
        const result = await Swal.fire({
            title: "Are you sure?",
            text: hasOffering
                ? "This will delete the campaign and also permanently delete the offering in North Capital. This action cannot be undone."
                : "This will delete the campaign. You can recover it from the database if needed.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });
        if (result.isConfirmed) {
            try {
                await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/delete`,
                    { is_deleted: 1 },
                    { withCredentials: true },
                );

                // Trigger deleteOffering NC API if offering ID exists (non-blocking)
                const offeringId = campaignDetails?.north_capital_offering_id;
                if (offeringId) {
                    try {
                        await axios.post(
                            `${process.env.REACT_APP_BACKEND_URL}/api/offering/delete-offering`,
                            { offeringId },
                            { withCredentials: true },
                        );
                    } catch (ncErr) {
                        console.warn(
                            "deleteOffering NC API failed (non-blocking):",
                            ncErr?.response?.data?.error || ncErr.message,
                        );
                    }
                }

                Swal.fire("Deleted!", "Campaign has been deleted.", "success");
                closeCampaignModal();
                fetchCampaignSummaries();
            } catch (err) {
                Swal.fire(
                    "Error",
                    err.response?.data?.error || "Failed to delete campaign",
                    "error",
                );
            }
        }
    };

    // Close Offering handler with status check
    const handleCloseOffering = async (campaignId) => {
        const offeringId = campaignDetails?.north_capital_offering_id;
        if (!offeringId) {
            notifyUser(null, "error", "No offering ID found for this campaign.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/offering/get-trade-status-for-offering`,
                { offeringId },
                { withCredentials: true },
            );
            // Debug: log the full backend response
            // Extract and log tradeDetails immediately
            let tradeDetails = [];
            // Fix: handle backend response shape { data: { tradeDetails: [...] } }
            if (data && data.data && Array.isArray(data.data.tradeDetails)) {
                tradeDetails = data.data.tradeDetails;
            } else if (Array.isArray(data.tradeDetails)) {
                tradeDetails = data.tradeDetails;
            } else if (Array.isArray(data["tradeDetails"])) {
                tradeDetails = data["tradeDetails"];
            } else if (Array.isArray(data["trade_details"])) {
                tradeDetails = data["trade_details"];
            } else if (
                data.fullResponse &&
                Array.isArray(data.fullResponse["Offering purchased details"])
            ) {
                tradeDetails = data.fullResponse["Offering purchased details"];
            }

            const allowedStatuses = [
                "Funded",
                "Settled",
                "Canceled",
                "Unwind Settled",
            ];
            const allStatuses = tradeDetails.map(
                (td) => td.tradeStatus || td.status || td.trade_status,
            );
            const hasDisallowed = allStatuses.some(
                (status) => !allowedStatuses.includes(status),
            );
            if (!hasDisallowed && allStatuses.length > 0) {
                // Confirm with user before closing
                const result = await Swal.fire({
                    title: "Close Offering?",
                    text: `Are you sure you want to close this offering? This action cannot be undone.`,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#d33",
                    cancelButtonColor: "#3085d6",
                    confirmButtonText: "Yes, close it!",
                });
                if (result.isConfirmed) {
                    try {
                        await axios.post(
                            `${process.env.REACT_APP_BACKEND_URL}/api/offering/close-offering`,
                            { offeringId },
                            { withCredentials: true },
                        );
                        notifyUser(null, "success", "Offering closed successfully.", "toast", null, null, null, null, null, false, false, "top-center");
                        closeCampaignModal();
                        fetchCampaignSummaries();
                    } catch (err) {
                        notifyUser(null, "error", err.response?.data?.error ||
                                "Failed to close offering", "toast", null, null, null, null, null, false, false, "top-center");
                    }
                }
            } else {
                notifyUser(null, "info", `This campaign cannot be closed as at least one of the trade statuses is not Funded, Settled, Canceled, or Unwind Settled. Statuses: "${allStatuses.join(", ")}"`, "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch (err) {
            notifyUser(null, "error", err.response?.data?.error || "Failed to get offering status", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Undo delete campaign handler
    const handleUndoDeleteCampaign = async (campaignId) => {
        const result = await Swal.fire({
            title: "Undo Deletion?",
            text: "Do you want to undo the deletion of this campaign?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, Undo Delete!",
        });
        if (result.isConfirmed) {
            try {
                await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/undo-delete`,
                    {},
                    { withCredentials: true },
                );
                Swal.fire(
                    "Restored!",
                    "Campaign deletion has been undone.",
                    "success",
                );
                closeCampaignModal();
                fetchCampaignSummaries();
            } catch (err) {
                Swal.fire(
                    "Error",
                    err.response?.data?.error || "Failed to undo delete",
                    "error",
                );
            }
        }
    };

    const handleDisableCampaign = async (campaignId) => {
        const result = await Swal.fire({
            title: "Disable Campaign?",
            text: "Describe the change associated with disabling this campaign.",
            icon: "warning",
            input: "textarea",
            inputLabel: "Summary of Change",
            inputPlaceholder:
                "Describe why the campaign is being disabled...",
            inputAttributes: {
                "aria-label": "Summary of change",
            },
            inputValidator: (value) => {
                if (!value?.trim()) {
                    return "Summary of change is required.";
                }

                return null;
            },
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, Disable",
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/disable`,
                { changeDescription: result.value.trim() },
                { withCredentials: true },
            );
            notifyUser(null, "success", "Campaign disabled successfully.", "toast", null, null, null, null, null, false, false, "top-center");
            setShowModal(false);
            fetchCampaignSummaries();
        } catch (err) {
            notifyUser(null, "error", err.response?.data?.error || "Failed to disable campaign.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleCompletedCampaignVisibility = async (campaign) => {
        const isHidden = Number(campaign.is_hidden_from_live_site) === 1;
        const action = isHidden ? "restore" : "hide";
        const result = await Swal.fire({
            title: isHidden
                ? "Restore Campaign to Live Site?"
                : "Hide Campaign from Live Site?",
            text: isHidden
                ? "This completed campaign will appear on the public site again."
                : "This completed campaign will be removed from the public site but remain in the admin dashboard.",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: isHidden ? "#3085d6" : "#d33",
            cancelButtonColor: "#6b7280",
            confirmButtonText: isHidden ? "Yes, Restore" : "Yes, Hide",
        });

        if (!result.isConfirmed) return;

        try {
            const { data } = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaign.id}/live-site-visibility`,
                { hidden: !isHidden },
                { withCredentials: true },
            );
            setCampaignDetails((previous) =>
                previous
                    ? {
                          ...previous,
                          is_hidden_from_live_site:
                              data.is_hidden_from_live_site,
                      }
                    : previous,
            );
            await fetchCampaignSummaries();
            notifyUser(
                null,
                "success",
                `Campaign ${action === "hide" ? "hidden from" : "restored to"} the live site.`,
                "toast",
                null,
                null,
                null,
                null,
                null,
                false,
                false,
                "top-center",
            );
        } catch (err) {
            notifyUser(
                null,
                "error",
                err.response?.data?.error ||
                    "Failed to update campaign visibility.",
                "toast",
                null,
                null,
                null,
                null,
                null,
                false,
                false,
                "top-center",
            );
        }
    };

    const handleEnableCampaign = async (campaignId) => {
        const result = await Swal.fire({
            title: "Enable Campaign?",
            text: "Do you want to re-enable this campaign and restore its previous status?",
            icon: "question",
            input: "textarea",
            inputLabel: "Summary of Change",
            inputPlaceholder:
                "Describe the material change investors must review...",
            inputAttributes: {
                "aria-label": "Summary of change",
            },
            inputValidator: (value) => {
                if (!value?.trim()) {
                    return "Summary of change is required.";
                }

                return null;
            },
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, Enable!",
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/enable`,
                    { changeDescription: result.value.trim() },
                    { withCredentials: true },
                );

                setCampaignDetails((prevDetails) =>
                    prevDetails
                        ? {
                              ...prevDetails,
                              status: response.data?.status || "pending",
                              is_disabled: 0,
                              prev_status: null,
                          }
                        : prevDetails,
                );
                await fetchCampaignSummaries();
                if (campaignDetails?.id) {
                    await fetchCampaignDetails(campaignDetails.id);
                }
                Swal.fire(
                    "Enabled!",
                    `Campaign restored to ${response.data?.status || "pending"}.`,
                    "success",
                );
            } catch (err) {
                Swal.fire(
                    "Error",
                    err.response?.data?.error || "Failed to enable campaign",
                    "error",
                );
            }
        }
    };

    const handleEnableDisabledEdit = async (campaignId) => {
        try {
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/enable-edit`,
                {},
                { withCredentials: true },
            );
            notifyUser(null, "success", "Edit access enabled for this disabled campaign.", "toast", null, null, null, null, null, false, false, "top-center");
            setCampaignDetails((prevDetails) =>
                prevDetails
                    ? {
                          ...prevDetails,
                          disabled_edit_enabled: 1,
                      }
                    : prevDetails,
            );
            await fetchCampaignSummaries();
            await fetchCampaignDetails(campaignId);
        } catch (err) {
            notifyUser(null, "error", err.response?.data?.error ||
                    "Failed to enable edit access.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Handle Campaign Approval
    const handleCampaignApproval = async (campaignId, action) => {
        // Check if trying to approve a campaign that's on hold
        if (
            action === "approve" &&
            campaignDetails &&
            campaignDetails.status === "onhold"
        ) {
            notifyUser(null, "warning", "This campaign application is on-hold, so you cannot approve this campaign", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if (action === "on-hold" && !showOnHoldNote) {
            setShowOnHoldNote(true);
            return;
        }

        // Show confirmation for reject action
        if (action === "reject") {
            const result = await Swal.fire({
                title: "Are you sure?",
                text: "Once you reject an application, you cannot undo this change. This action is permanent.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, reject it!",
                cancelButtonText: "Cancel",
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        try {
            const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}`;
            const isDraft = !!campaignDraft;
            const endpoint = isDraft
                ? `${BASE_URL}/api/campaigns/${campaignId}/${action}-draft`
                : `${BASE_URL}/api/campaigns/${campaignId}/${action}`;

            const axiosMethod = isDraft ? axios.post : axios.put;
            const payload = { user_id: user?.id };
            const isDisabledDraft =
                isDraft && campaignDetails?.status === "disabled";

            if (action === "on-hold") {
                payload.on_hold_note = onHoldNote;
            }

            // Custom logic for approve: create offering first, then add documents, then approve
            if (action === "approve") {
                // Declare userId at the very top so it's available everywhere in this block
                const ownerUserId = campaignDetails?.owner_user_id;

                if (!ownerUserId) {
                    console.error(
                        "Missing owner_user_id in campaignDetails:",
                        campaignDetails,
                    );
                    notifyUser(null, "error", "Owner user ID is missing for this campaign.", "toast", null, null, null, null, null, false, false, "top-center");
                    return;
                }
                // Format dates as MM-DD-YYYY if needed
                const formatDate = (dateStr) => {
                    if (!dateStr) return "";
                    const d = new Date(dateStr);
                    if (isNaN(d)) return dateStr;
                    const mm = String(d.getMonth() + 1).padStart(2, "0");
                    const dd = String(d.getDate()).padStart(2, "0");
                    const yyyy = d.getFullYear();
                    return `${mm}-${dd}-${yyyy}`;
                };

                const offeringPayload = {
                    issuerId: campaignDetails?.north_capital_issuer_id,
                    issueName:
                        campaignDetails?.campaign_name ||
                        campaignDetails?.company_name ||
                        "",
                    issueType: campaignDetails?.issue_type || "Equity",
                    targetAmount: campaignDetails?.goal_amount,
                    minAmount: campaignDetails?.investment_minimum || "",
                    maxAmount: campaignDetails?.max_amount,
                    unitPrice: campaignDetails?.unit_price,
                    startDate: formatDate(campaignDetails?.campaign_start_date),
                    endDate: formatDate(campaignDetails?.end_date),
                    offeringText:
                        campaignDetails?.offering_text ||
                        campaignDetails?.description ||
                        "SampleOfferingText",
                    stampingText:
                        campaignDetails?.stamping_text || "Confidential",
                    escrowAccountNumber:
                        campaignDetails?.escrow_account_number || "",
                    field1: campaignDetails?.field1 || "",
                    field2: campaignDetails?.field2 || "",
                    field3: campaignDetails?.field3 || "",
                    campaignId,
                };
                try {
                    // 1. Create Offering (skip if already created)
                    let offeringId = campaignDetails?.north_capital_offering_id;

                    if (offeringId) {
                        // When approving a draft, check if any NC offering-related fields changed
                        // and sync them to North Capital via updateOffering
                        if (isDraft && campaignDraft) {
                            const offeringRelatedFields = [
                                "campaign_name",
                                "company_name",
                                "issue_type",
                                "goal_amount",
                                "fundRaisingGoal",
                                "investment_minimum",
                                "max_amount",
                                "unit_price",
                                "campaign_start_date",
                                "end_date",
                                "offering_text",
                                "description",
                                "stamping_text",
                            ];
                            const hasOfferingChanges =
                                offeringRelatedFields.some(
                                    (field) =>
                                        campaignDraft[field] !== undefined &&
                                        String(campaignDraft[field]) !==
                                            String(
                                                campaignDetails?.[field] ?? "",
                                            ),
                                );

                            if (hasOfferingChanges || isDisabledDraft) {
                                const src = campaignDraft;
                                const updateOfferingPayload = {
                                    offeringId,
                                    issueName:
                                        src.campaign_name ||
                                        src.company_name ||
                                        campaignDetails?.campaign_name ||
                                        campaignDetails?.company_name ||
                                        "",
                                    issueType:
                                        src.issue_type ||
                                        campaignDetails?.issue_type ||
                                        "Equity",
                                    targetAmount:
                                        src.goal_amount ||
                                        src.fundRaisingGoal ||
                                        campaignDetails?.goal_amount ||
                                        "",
                                    minAmount:
                                        src.investment_minimum ||
                                        campaignDetails?.investment_minimum ||
                                        "",
                                    maxAmount:
                                        src.max_amount ||
                                        campaignDetails?.max_amount ||
                                        "",
                                    unitPrice:
                                        src.unit_price ||
                                        campaignDetails?.unit_price ||
                                        "",
                                    startDate: formatDate(
                                        src.campaign_start_date ||
                                            campaignDetails?.campaign_start_date,
                                    ),
                                    endDate: formatDate(
                                        src.end_date ||
                                            campaignDetails?.end_date,
                                    ),
                                    offeringStatus: "Approved",
                                    offeringText:
                                        src.offering_text ||
                                        src.description ||
                                        campaignDetails?.offering_text ||
                                        campaignDetails?.description ||
                                        "SampleOfferingText",
                                    stampingText:
                                        src.stamping_text ||
                                        campaignDetails?.stamping_text ||
                                        "Confidential",
                                    campaignId,
                                };
                                try {
                                    const updateRes = await axios.post(
                                        `${BASE_URL}/api/offering/update-offering`,
                                        updateOfferingPayload,
                                        { withCredentials: true },
                                    );
                                    if (!updateRes.data?.data?.success) {
                                        console.warn(
                                            "updateOffering returned non-success:",
                                            updateRes.data,
                                        );
                                        if (isDisabledDraft) {
                                            notifyUser(null, "error", "North Capital update was not successful. Campaign changes were not applied.", "toast", null, null, null, null, null, false, false, "top-center");
                                            return;
                                        }
                                    }
                                    if (isDisabledDraft) {
                                        payload.disabled_update_completed = true;
                                    }
                                } catch (updateErr) {
                                    console.error(
                                        "updateOffering failed:",
                                        updateErr,
                                    );
                                    if (isDisabledDraft) {
                                        notifyUser(null, "error", "North Capital update was not successful. Campaign changes were not applied.", "toast", null, null, null, null, null, false, false, "top-center");
                                        return;
                                    }
                                    notifyUser(null, "warning", "Offering update in North Capital failed, but draft approval will continue.", "toast", null, null, null, null, null, false, false, "top-center");
                                }
                            }
                        }
                    } else {
                        const offeringRes = await axios.post(
                            `${BASE_URL}/api/offering/create-offering`,
                            offeringPayload,
                            { withCredentials: true },
                        );
                        if (
                            offeringRes.data?.data?.success &&
                            offeringRes.data?.data?.offeringId
                        ) {
                            offeringId = offeringRes.data.data.offeringId;
                            if (isDisabledDraft) {
                                payload.disabled_update_completed = true;
                            }
                        } else {
                            notifyUser(null, "error", "Failed to create offering. Campaign remains pending.", "toast", null, null, null, null, null, false, false, "top-center");
                            return;
                        }
                    }

                    // 2. Approve campaign
                    const response = await axiosMethod(endpoint, payload, {
                        withCredentials: true,
                    });
                    notifyUser(null, "success", "Campaign approved and offering created successfully.", "toast", null, null, null, null, null, false, false, "top-center");
                    closeCampaignModal();
                    setShowOnHoldNote(false);
                    setOnHoldNote("");
                    fetchCampaignSummaries();
                    if (process.env.NODE_ENV === "development") {
                        await fetchCampaignDetails(campaignId);
                    }
                } catch (err) {
                    console.error("Offering creation failed:", err);
                    notifyUser(null, "error", "Failed to create offering. Campaign remains pending.", "toast", null, null, null, null, null, false, false, "top-center");
                }
                return;
            }

            // Default flow for reject/on-hold
            const response = await axiosMethod(endpoint, payload, {
                withCredentials: true,
            });
            notifyUser(null, "success", isDraft
                    ? `Edit changes ${
                          action === "approve"
                              ? "approved"
                              : action === "on-hold"
                                ? "put on hold"
                                : "rejected"
                      } successfully`
                    : `Campaign ${
                          action === "approve"
                              ? "approved"
                              : action === "on-hold"
                                ? "put on hold"
                                : "rejected"
                      } successfully`, "toast", null, null, null, null, null, false, false, "top-center");

            if (action === "on-hold") {
                setDisplayedOnHoldNote(onHoldNote);
                setCampaignDetails((prevDetails) => ({
                    ...prevDetails,
                    status: "onhold",
                    on_hold_note: onHoldNote,
                }));
            }
            if (action === "approve" || action === "reject") {
                setDisplayedOnHoldNote("");
            }
            closeCampaignModal();
            setShowOnHoldNote(false);
            setOnHoldNote("");
            fetchCampaignSummaries();
            if (process.env.NODE_ENV === "development") {
                await fetchCampaignDetails(campaignId);
            }
        } catch (error) {
            console.error(`${action} failed:`, error);
            notifyUser(null, "error", `Failed to ${action} campaign`, "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Open NC Form with fresh data
    const openNCForm = () => {
        setNCIssuerId(campaignDetails?.north_capital_issuer_id || "");
        setNCOfferingId(campaignDetails?.north_capital_offering_id || "");
        setShowNCForm(true);
    };

    const collectCampaignDocumentsForOffering = () => {
        const ownerUserId = campaignDetails?.owner_user_id;
        const offeringId = campaignDetails?.north_capital_offering_id;

        if (!offeringId) {
            notifyUser(null, "error", "No offering ID found. Please approve the campaign first.", "toast", null, null, null, null, null, false, false, "top-center");
            return null;
        }

        if (!ownerUserId) {
            notifyUser(null, "error", "Owner user ID is missing for this campaign.", "toast", null, null, null, null, null, false, false, "top-center");
            return null;
        }

        let uploadedFiles = campaignDetails.uploaded_files;
        if (typeof uploadedFiles === "string") {
            try {
                uploadedFiles = JSON.parse(uploadedFiles);
            } catch {
                uploadedFiles = {};
            }
        }

        let legalUploads = campaignDetails.legal_uploads;
        if (typeof legalUploads === "string") {
            try {
                legalUploads = JSON.parse(legalUploads);
            } catch {
                legalUploads = {};
            }
        }

        let additionalUploads = campaignDetails.additional_uploads;
        if (typeof additionalUploads === "string") {
            try {
                additionalUploads = JSON.parse(additionalUploads);
            } catch {
                additionalUploads = [];
            }
        }

        if (additionalUploads && !Array.isArray(additionalUploads)) {
            additionalUploads = [];
        }

        const getFileName = (file) =>
            typeof file === "object" && file !== null
                ? (file.fileName || file.fullPath || JSON.stringify(file))
                      .split(/[\\/]/)
                      .pop()
                : String(file).split(/[\\/]/).pop();

        const getFilePath = (file, fallbackPath) =>
            typeof file === "object" && file !== null
                ? file.fullPath || file.fileName || ""
                : fallbackPath;

        const allDocs = [];

        if (uploadedFiles) {
            Object.entries(uploadedFiles).forEach(([key, arr]) => {
                if (Array.isArray(arr)) {
                    arr.forEach((file) => {
                        const name = getFileName(file);
                        const defaultPath = `${ownerUserId}/${key}/${name}`;
                        allDocs.push({
                            source: "uploaded_files",
                            key,
                            fileName: name,
                            path: getFilePath(file, defaultPath),
                            label: key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase()),
                        });
                    });
                }
            });
        }

        if (legalUploads) {
            Object.entries(legalUploads).forEach(([key, arr]) => {
                if (Array.isArray(arr)) {
                    arr.forEach((file) => {
                        const name = getFileName(file);
                        const defaultPath = name.startsWith(
                            `campaigns/${campaignDetails.id}/legal/`,
                        )
                            ? name
                            : `campaigns/${campaignDetails.id}/legal/${name}`;
                        allDocs.push({
                            source: "legal_uploads",
                            key,
                            fileName: name,
                            path: getFilePath(file, defaultPath),
                            label: key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/_/g, " ")
                                .replace(/^./, (str) => str.toUpperCase()),
                        });
                    });
                }
            });
        }

        if (Array.isArray(additionalUploads) && additionalUploads.length > 0) {
            additionalUploads.forEach((file) => {
                let name, filePath;

                if (typeof file === "object" && file !== null) {
                    name = file.fileName || file.fullPath || "";
                    filePath = file.fullPath || file.fileName || "";
                } else {
                    const pathStr = String(file);
                    name = pathStr.split("/").pop();
                    filePath = pathStr.startsWith("uploads/")
                        ? `/${pathStr}`
                        : pathStr;
                }

                allDocs.push({
                    source: "additional_uploads",
                    key: "additional",
                    fileName: name,
                    path: filePath,
                    label: "Additional Uploads",
                });
            });
        }

        return { allDocs, offeringId, ownerUserId };
    };

    const handleAddDocumentsForOffering = (showAllDocuments = false) => {
        const documentData = collectCampaignDocumentsForOffering();
        if (!documentData) return;

        const { allDocs } = documentData;

        const existingDocumentNames = new Set(
            (manageDocumentsData.documents || [])
                .flatMap((doc) => [doc?.fileName, doc?.url])
                .map(normalizeDocumentName)
                .filter(Boolean),
        );

        const existingDocumentTitles = new Set(
            (manageDocumentsData.documents || [])
                .map((doc) => normalizeDocumentName(doc?.title))
                .filter(Boolean),
        );

        const availableDocs = showAllDocuments
            ? allDocs
            : allDocs.filter((doc) => {
                  const normalizedName = normalizeDocumentName(doc.fileName);
                  const normalizedLabel = normalizeDocumentName(doc.label);

                  return (
                      !existingDocumentNames.has(normalizedName) &&
                      !existingDocumentTitles.has(normalizedLabel)
                  );
              });

        if (allDocs.length === 0) {
            notifyUser(null, "warning", "No uploaded documents found for this campaign.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if (availableDocs.length === 0) {
            notifyUser(null, "info", "All available campaign documents are already uploaded for this offering.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setAvailableDocsForOffering(availableDocs);
        setSelectedDocsForOffering({});
        setDocModalMode("documents");
        setShowDocSelectModal(true);
    };

    const handleSubmitSelectedDocsForOffering = async () => {
        const selected = availableDocsForOffering.filter(
            (_, idx) => selectedDocsForOffering[idx],
        );

        if (selected.length === 0) {
            notifyUser(null, "warning", "Please select at least one document.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const documentData = collectCampaignDocumentsForOffering();
        if (!documentData) return;

        const { offeringId, ownerUserId } = documentData;
        const BASE_URL = process.env.REACT_APP_BACKEND_URL;

        setDocOfferingSubmitting(true);

        try {
            const docsRes = await axios.post(
                `${BASE_URL}/api/offering/add-campaign-documents`,
                {
                    offeringId,
                    campaign_id: campaignDetails.id,
                    application_id: campaignDetails.application_id,
                    ownerUserId,
                    documents: selected,
                },
                {
                    withCredentials: true,
                },
            );

            if (docsRes.data?.data?.success) {
                notifyUser(null, "success", "Documents uploaded successfully for offering.", "toast", null, null, null, null, null, false, false, "top-center");
                setShowDocSelectModal(false);
                await handleManageDocuments();
            } else {
                notifyUser(null, "error", docsRes.data?.message ||
                        "Failed to upload documents for offering.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch (error) {
            console.error(
                "addDocumentsForOffering failed:",
                error.response?.data || error,
            );
            const missingFiles = error.response?.data?.missing;
            const missingMessage =
                Array.isArray(missingFiles) && missingFiles.length > 0
                    ? ` Missing: ${missingFiles
                          .map((item) => item.fileName || item)
                          .join(", ")}`
                    : "";
            notifyUser(null, "error", `${
                    error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Failed to upload documents for offering."
                }${missingMessage}`, "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setDocOfferingSubmitting(false);
        }
    };

    const handleAddSubscriptionsForOffering = async () => {
        const BASE_URL = process.env.REACT_APP_BACKEND_URL;

        setDocModalMode("subscriptions");
        setAvailableDocsForOffering([]);
        setSelectedDocsForOffering({});
        setDocOfferingSubmitting(true);
        setShowDocSelectModal(true);

        try {
            const { data } = await axios.post(
                `${BASE_URL}/api/offering/fetch-subscription-documents`,
                {},
                { withCredentials: true },
            );

            const templateDetails = Array.isArray(data?.data?.document_details)
                ? data.data.document_details
                : [];

            const templates = templateDetails.map((template, idx) => {
                const templateId = template?.templateId || "";
                const templateName =
                    template?.name ||
                    template?.emailSubject ||
                    template?.description ||
                    templateId ||
                    `Template ${idx + 1}`;

                return {
                    source: "subscription_templates",
                    key: templateId || `template-${idx}`,
                    templateId,
                    templateName,
                    fileName: `templatename0=${templateId}--${templateName}`,
                    label: templateName,
                    path: template?.uri || "",
                    ownerEmail: template?.owner?.email || "",
                    lastModified: template?.lastModified || "",
                };
            });

            if (templates.length === 0) {
                notifyUser(null, "info", "No subscription templates available.", "toast", null, null, null, null, null, false, false, "top-center");
            }

            setAvailableDocsForOffering(templates);
        } catch (error) {
            console.error("fetchSubscriptionDocuments failed:", error);
            notifyUser(null, "error", error.response?.data?.error ||
                    "Failed to fetch subscription templates.", "toast", null, null, null, null, null, false, false, "top-center");
            setShowDocSelectModal(false);
        } finally {
            setDocOfferingSubmitting(false);
        }
    };

    const handleSubmitSelectedSubscriptions = async () => {
        const selected = availableDocsForOffering.filter(
            (_, idx) => selectedDocsForOffering[idx],
        );

        if (selected.length === 0) {
            notifyUser(null, "warning", "Please select at least one template.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const offeringId = campaignDetails?.north_capital_offering_id;
        const BASE_URL = process.env.REACT_APP_BACKEND_URL;

        setDocOfferingSubmitting(true);
        let successCount = 0;

        try {
            for (const doc of selected) {
                const res = await axios.post(
                    `${BASE_URL}/api/offering/addSubscriptionsforOffering`,
                    { offeringId, file_name: doc.fileName },
                    { withCredentials: true },
                );

                if (res.data?.data?.success) {
                    successCount++;
                } else {
                    notifyUser(null, "error", `Failed to add subscription: ${doc.label}`, "toast", null, null, null, null, null, false, false, "top-center");
                }
            }

            if (successCount > 0) {
                notifyUser(null, "success", `${successCount} subscription(s) added for offering successfully.`, "toast", null, null, null, null, null, false, false, "top-center");
                setShowDocSelectModal(false);
                await handleManageSubscriptions();
            }
        } catch (error) {
            console.error("addSubscriptionsForOffering failed:", error);
            notifyUser(null, "error", "Failed to add subscriptions for offering.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setDocOfferingSubmitting(false);
        }
    };

    const handleManageDocumentUpdate = (doc) => {
        if (!doc?.documentId && !doc?.id) {
            notifyUser(null, "error", "Document ID not found for this offering document.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setReplacementTargetDocument(doc);
        setReplacementDocumentDragActive(false);
    };

    const processReplacementDocument = async (selectedFile, targetDoc) => {
        if (!selectedFile || !targetDoc) {
            setReplacementTargetDocument(null);
            return;
        }

        const documentId = targetDoc.documentId || targetDoc.id;
        const offeringId = campaignDetails?.north_capital_offering_id;

        if (!documentId) {
            notifyUser(null, "error", "Document ID not found for this offering document.", "toast", null, null, null, null, null, false, false, "top-center");
            setReplacementTargetDocument(null);
            return;
        }

        if (!offeringId) {
            notifyUser(null, "error", "No offering ID found.", "toast", null, null, null, null, null, false, false, "top-center");
            setReplacementTargetDocument(null);
            return;
        }

        const currentFileName = targetDoc.fileName || "";
        const currentBaseName = currentFileName.includes(".")
            ? currentFileName.replace(/\.[^.]+$/, "")
            : currentFileName;
        const selectedExtension = selectedFile.name.includes(".")
            ? selectedFile.name.split(".").pop()
            : "";
        const uploadFileName = currentBaseName
            ? selectedExtension
                ? `${currentBaseName}.${selectedExtension}`
                : currentBaseName
            : selectedFile.name;
        const confirmation = await Swal.fire({
            title: "Replace document?",
            text: `This will delete ${currentFileName || selectedFile.name} and upload the selected file as ${uploadFileName}.`,
            icon: "warning",
            showCancelButton: true,
            width: updateDocumentModalWidth,
            padding: updateDocumentModalPadding,
            confirmButtonColor: "var(--navy)",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, replace it",
        });

        if (!confirmation.isConfirmed) {
            setReplacementTargetDocument(null);
            return;
        }

        setManageDocumentUpdatingId(documentId);

        try {
            const deleteResponse = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/offering/delete-documents-for-offering`,
                { documentId },
                { withCredentials: true },
            );

            if (!deleteResponse.data?.data?.success) {
                throw new Error(
                    deleteResponse.data?.error ||
                        "Failed to delete document for offering.",
                );
            }

            const formData = new FormData();
            formData.append("offeringId", offeringId);
            formData.append("campaign_id", campaignDetails.id);

            if (campaignDetails.application_id) {
                formData.append(
                    "application_id",
                    campaignDetails.application_id,
                );
            }

            if (campaignDetails.owner_user_id) {
                formData.append("user_id", campaignDetails.owner_user_id);
            }

            formData.append("userfiles", selectedFile, uploadFileName);
            formData.append("documentTitle", targetDoc.title || uploadFileName);
            formData.append("documentFileReferenceCode", String(Date.now()));
            formData.append("file_name", uploadFileName);

            const uploadResponse = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/offering/add-documents`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );

            if (!uploadResponse.data?.data?.success) {
                throw new Error(
                    uploadResponse.data?.error ||
                        uploadResponse.data?.data?.statusDesc ||
                        "Failed to upload replacement document.",
                );
            }

            notifyUser(null, "success", "Document updated successfully.", "toast", null, null, null, null, null, false, false, "top-center");
            handleAddDocumentsForOffering(true);
        } catch (error) {
            console.error("replaceOfferingDocument failed:", error);
            notifyUser(null, "error", error.response?.data?.error ||
                    error.message ||
                    "Failed to update document for offering.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setManageDocumentUpdatingId(null);
            setReplacementTargetDocument(null);
            setReplacementDocumentDragActive(false);
        }
    };

    const handleReplacementDocumentChange = async (event) => {
        const selectedFile = event.target.files?.[0];
        const targetDoc = replacementTargetDocument;

        event.target.value = "";
        await processReplacementDocument(selectedFile, targetDoc);
    };

    const openReplacementDocumentPicker = () => {
        if (replacementDocumentInputRef.current) {
            replacementDocumentInputRef.current.value = "";
            replacementDocumentInputRef.current.click();
        }
    };

    const handleReplacementDocumentDrop = async (event) => {
        event.preventDefault();
        setReplacementDocumentDragActive(false);

        const selectedFile = event.dataTransfer?.files?.[0];
        await processReplacementDocument(
            selectedFile,
            replacementTargetDocument,
        );
    };

    const handleManageDocumentDelete = async (doc) => {
        const documentId = doc?.documentId || doc?.id;

        if (!documentId) {
            notifyUser(null, "error", "Document ID not found for this offering document.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const result = await Swal.fire({
            title: "Delete document?",
            text: `This will remove ${doc?.fileName || "this document"} from the offering.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it",
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/offering/delete-documents-for-offering`,
                { documentId },
                { withCredentials: true },
            );

            if (data?.data?.success) {
                notifyUser(null, "success", "Document deleted successfully.", "toast", null, null, null, null, null, false, false, "top-center");
                handleAddDocumentsForOffering(true);
            } else {
                notifyUser(null, "error", data?.error || "Failed to delete document for offering.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch (error) {
            console.error("deleteDocumentsForOffering failed:", error);
            notifyUser(null, "error", error.response?.data?.error ||
                    "Failed to delete document for offering.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const normalizeDocumentName = (value) => {
        if (!value) return "";

        return String(value)
            .split("?")[0]
            .split("#")[0]
            .split("/")
            .pop()
            .trim()
            .toLowerCase();
    };

    const getDocumentExtension = (doc) => {
        const source = doc?.fileName || doc?.url || "";
        const normalized = String(source).split("?")[0].split("#")[0];
        const extension = normalized.includes(".")
            ? normalized.split(".").pop()
            : "";

        return extension.toLowerCase();
    };

    const isInlinePreviewableDocument = (doc) => {
        const extension = getDocumentExtension(doc);

        return ["pdf", "png", "jpg", "jpeg", "gif", "webp"].includes(extension);
    };

    const isImageDocument = (doc) => {
        const extension = getDocumentExtension(doc);

        return ["png", "jpg", "jpeg", "gif", "webp"].includes(extension);
    };

    const handleManageDocumentView = (doc) => {
        if (!doc?.url) {
            notifyUser(null, "error", "No document URL available.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if (!isInlinePreviewableDocument(doc)) {
            setManageDocumentPreviewDoc(null);
            setManageDocumentNoPreviewDoc(doc);
            return;
        }

        setManageDocumentNoPreviewDoc(null);
        setManageDocumentPreviewDoc(doc);
    };

    const handleManageDocumentDownload = async (doc) => {
        if (!doc?.url) {
            notifyUser(null, "error", "No document URL available.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setManageDocumentDownloading(true);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/offering/download-document`,
                {
                    url: doc.url,
                    fileName: doc.fileName || doc.title || "document",
                },
                {
                    responseType: "blob",
                    withCredentials: true,
                },
            );

            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement("a");

            link.href = blobUrl;
            link.download = doc.fileName || doc.title || "document";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            setManageDocumentNoPreviewDoc(null);
        } catch (error) {
            console.error("handleManageDocumentDownload failed:", error);
            notifyUser(null, "error", error.response?.data?.message || "Failed to download document.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setManageDocumentDownloading(false);
        }
    };

    // Cancel On-Hold (set status to pending)
    const handleCancelOnHold = (campaignId) => {
        Swal.fire({
            title: "Cancel On-Hold?",
            text: "Are you sure you want to move this campaign back to pending?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, move to pending",
            cancelButtonText: "No, keep on-hold",
        }).then((result) => {
            if (result.isConfirmed) {
                // Call the backend endpoint
                axios
                    .put(
                        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/cancel-onhold`,
                        { updated_by: user?.email },
                        { withCredentials: true },
                    )
                    .then(() => {
                        Swal.fire(
                            "Success",
                            "Campaign status updated to pending.",
                            "success",
                        );
                        closeCampaignModal();
                        fetchCampaignSummaries();
                    })
                    .catch(() => {
                        Swal.fire(
                            "Error",
                            "Failed to update campaign status.",
                            "error",
                        );
                    });
            }
        });
    };

    const filterAndPaginate = () => {
        const lower = searchTerm.toLowerCase();
        const filtered = campaigns.filter((c) => {
            // Text search filter
            const matchesSearch =
                (c.campaign_name || "").toLowerCase().includes(lower) ||
                (c.company_name || "").toLowerCase().includes(lower) ||
                (c.first_name || "").toLowerCase().includes(lower) ||
                (c.last_name || "").toLowerCase().includes(lower);

            // Status filter
            const matchesStatus =
                statusFilter === "all" || c.status === statusFilter;

            // Draft filter
            const matchesDraft =
                draftFilter === "all" ||
                (draftFilter === "with-drafts" && c.draft_submitted === 1) ||
                (draftFilter === "no-drafts" && c.draft_submitted !== 1);

            return matchesSearch && matchesStatus && matchesDraft;
        });
        // Sort by created_at descending (most recent first)
        const sorted = filtered.sort((a, b) => {
            const aDate = a.created_at ? new Date(a.created_at) : new Date(0);
            const bDate = b.created_at ? new Date(b.created_at) : new Date(0);
            return bDate - aDate;
        });
        const start = (currentPage - 1) * perPage;
        const paginated = sorted.slice(start, start + perPage);
        setFilteredCampaigns(paginated);
    };

    const handleExportCSV = () => {
        const lower = searchTerm.toLowerCase();
        const exportList = campaigns.filter((c) => {
            const matchesSearch =
                (c.campaign_name || "").toLowerCase().includes(lower) ||
                (c.company_name || "").toLowerCase().includes(lower) ||
                (c.first_name || "").toLowerCase().includes(lower) ||
                (c.last_name || "").toLowerCase().includes(lower);

            const matchesStatus =
                statusFilter === "all" || c.status === statusFilter;

            const matchesDraft =
                draftFilter === "all" ||
                (draftFilter === "with-drafts" && c.draft_submitted === 1) ||
                (draftFilter === "no-drafts" && c.draft_submitted !== 1);

            return matchesSearch && matchesStatus && matchesDraft;
        });

        if (!exportList.length) {
            Swal.fire({ icon: "info", title: "No data to export" });
            return;
        }

        const rows = exportList.map((s) => ({
            CAMPAIGN_NAME: s.campaign_name || "",
            COMPANY_NAME: s.company_name || "",
            OWNER: `${s.first_name || ""} ${s.last_name || ""}`.trim(),
            STATUS: s.status || "",
            LIVE_SITE:
                s.status === "completed"
                    ? Number(s.is_hidden_from_live_site) === 1
                        ? "hidden"
                        : "visible"
                    : "not applicable",
            DRAFT_SUBMITTED: s.draft_submitted === 1 ? "yes" : "no",
            CREATED_AT: s.created_at
                ? new Date(s.created_at).toLocaleString()
                : "",
        }));

        const header = Object.keys(rows[0]);
        const csv = [
            header.join(","),
            ...rows.map((r) =>
                header
                    .map((h) => `"${String(r[h]).replace(/"/g, '""')}"`)
                    .join(","),
            ),
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "campaigns_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const totalPages = Math.ceil(
        campaigns.filter((c) => {
            const lower = searchTerm.toLowerCase();
            const matchesSearch =
                (c.campaign_name || "").toLowerCase().includes(lower) ||
                (c.company_name || "").toLowerCase().includes(lower) ||
                (c.first_name || "").toLowerCase().includes(lower) ||
                (c.last_name || "").toLowerCase().includes(lower);

            // Status filter
            const matchesStatus =
                statusFilter === "all" || c.status === statusFilter;

            // Draft filter
            const matchesDraft =
                draftFilter === "all" ||
                (draftFilter === "with-drafts" && c.draft_submitted === 1) ||
                (draftFilter === "no-drafts" && c.draft_submitted !== 1);

            return matchesSearch && matchesStatus && matchesDraft;
        }).length / perPage,
    );

    const columns = [
        {
            name: "CAMPAIGN NAME",
            selector: (row) => row.campaign_name,
            sortable: true,
        },
        {
            name: "COMPANY NAME",
            selector: (row) => row.company_name,
            sortable: true,
        },
        {
            name: "NAME",
            selector: (row) => `${row.first_name} ${row.last_name}`,
            sortable: true,
        },
        {
            name: "EMAIL",
            selector: (row) => `${row.email}`,
            sortable: true,
        },
        {
            name: "STATUS",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <span
                    className={
                        row.status === "approved"
                            ? "text-green-600 font-semibold"
                            : row.status === "rejected"
                              ? "text-red-500 font-semibold"
                              : row.status === "completed"
                                ? "text-emerald-600 font-semibold"
                              : row.status === "closed"
                                ? "text-gray-500 font-semibold"
                              : row.status === "ended"
                                ? "text-gray-500 font-semibold"
                                : "text-yellow-600 font-semibold"
                    }
                >
                    {row.status}
                </span>
            ),
        },
        {
            name: "NC Record Status",
            selector: (row) => row.nc_record_status,
            sortable: true,
            cell: (row) => {
                const status = row.nc_record_status || "-";
                let statusClass = "font-semibold";
                if (status.toLowerCase() === "production") {
                    statusClass += " text-green-600";
                } else if (status.toLowerCase() === "sandbox") {
                    statusClass += " text-yellow-600";
                }
                return <span className={statusClass}>{status}</span>;
            },
        },
        {
            name: "DRAFT EDITS",
            selector: (row) => row.draft_submitted,
            sortable: true,
            cell: (row) => (
                <div className="flex items-center justify-center">
                    {row.draft_submitted === 1 ? (
                        <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-semibold flex items-center">
                            <FaEdit className="mr-1" />
                            Pending Review
                        </span>
                    ) : (
                        <span className="text-gray-400 text-xs">
                            No Changes
                        </span>
                    )}
                </div>
            ),
        },
        {
            name: "LIVE SITE",
            selector: (row) => row.is_hidden_from_live_site,
            sortable: true,
            cell: (row) =>
                row.status === "completed" ? (
                    Number(row.is_hidden_from_live_site) === 1 ? (
                        <span className="text-gray-500 font-semibold">
                            Hidden
                        </span>
                    ) : (
                        <span className="text-green-600 font-semibold">
                            Visible
                        </span>
                    )
                ) : (
                    <span className="text-gray-400">—</span>
                ),
        },
        {
            name: "CREATED AT",
            selector: (row) => new Date(row.created_at).toLocaleString(),
            sortable: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f1f8ff" },
            },
        },
    };

    const getNorthCapitalDisplayText = (value) =>
        String(value || "")
            .replace(/<[^>]*>/g, " ")
            .replace(/\[[^\]]+\]\([^)]+\)/g, (match) => {
                const labelMatch = match.match(/^\[([^\]]+)\]/);
                return labelMatch ? labelMatch[1] : match;
            })
            .replace(/\s+/g, " ")
            .trim();

    const getNorthCapitalTradeId = (value) => {
        const displayText = getNorthCapitalDisplayText(value);
        const tradeIdMatch = displayText.match(/\b\d{6,}\b/);
        return tradeIdMatch ? tradeIdMatch[0] : displayText;
    };

    const normalizeNorthCapitalESignStatus = (value) => {
        const status = getNorthCapitalDisplayText(value);
        const normalizedStatus = status.toLowerCase().replace(/\s+/g, "");

        if (!status) return "";
        if (normalizedStatus === "signed") return "SIGNED";
        if (
            normalizedStatus === "notsigned" ||
            normalizedStatus === "not-signed" ||
            normalizedStatus === "unsigned"
        ) {
            return "NOTSIGNED";
        }

        return "";
    };

    const getNorthCapitalField = (record, names) => {
        if (!record || typeof record !== "object") return "";

        for (const name of names) {
            if (record[name] !== undefined && record[name] !== null) {
                return record[name];
            }
        }

        const normalizedNames = names.map((name) =>
            String(name)
                .toLowerCase()
                .replace(/[^a-z0-9]/g, ""),
        );
        const matchingKey = Object.keys(record).find((key) =>
            normalizedNames.includes(
                String(key)
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, ""),
            ),
        );

        return matchingKey ? record[matchingKey] : "";
    };

    const getTradeESignStatus = (trade) =>
        normalizeNorthCapitalESignStatus(
            getNorthCapitalField(trade, [
                "esignStatus",
                "esignstatus",
                "eSignStatus",
                "eSignstatus",
                "eSign Status",
                "esign status",
                "ESIGNSTATUS",
            ]),
        );

    const getSubscriptionTemplatesFromPayload = (payload) => {
        const data = payload?.data || payload || {};
        const subscriptionDetails =
            data.documentDetails ||
            data.document_details ||
            data.fullResponse?.["document details"] ||
            data["document details"] ||
            [];

        return Array.isArray(subscriptionDetails)
            ? subscriptionDetails.map((doc, index) => ({
                  id: doc?.templateId || `subscription-${index}`,
                  title:
                      doc?.templateName ||
                      doc?.name ||
                      doc?.emailSubject ||
                      "Untitled Subscription",
                  fileName:
                      doc?.templateKey ||
                      doc?.templateId ||
                      `Subscription ${index + 1}`,
                  url: doc?.templateUrl || doc?.url || doc?.uri || "",
                  createdDate: doc?.createdDate || doc?.lastModified || "",
              }))
            : [];
    };

    const getTradeDetailsFromPayload = (payload) => {
        const data = payload?.data || payload || {};

        if (Array.isArray(data.tradeDetails)) return data.tradeDetails;
        if (Array.isArray(data.trade_details)) return data.trade_details;
        if (Array.isArray(data["tradeDetails"])) return data["tradeDetails"];
        if (Array.isArray(data["trade_details"])) return data["trade_details"];
        if (Array.isArray(data.fullResponse?.["Offering purchased details"])) {
            return data.fullResponse["Offering purchased details"];
        }
        if (Array.isArray(data["Offering purchased details"])) {
            return data["Offering purchased details"];
        }

        return [];
    };

    const getSignedDocumentSummary = (payload, tradeId, tradeESignStatus) => {
        const data = payload?.data || payload || {};
        const fullResponse = data.fullResponse || {};
        const signedDocumentDetails =
            data.signedDocumentDetails ||
            data.SignedDocumentDetails ||
            data.document_details ||
            data["document details"] ||
            fullResponse.signedDocumentDetails ||
            fullResponse.SignedDocumentDetails ||
            fullResponse.document_details ||
            fullResponse["document details"] ||
            [];
        const signedDocuments = Array.isArray(signedDocumentDetails)
            ? signedDocumentDetails
            : [];
        const signedDocument =
            signedDocuments.find(
                (document) =>
                    getNorthCapitalTradeId(document.tradeId || "") ===
                    getNorthCapitalTradeId(tradeId),
            ) ||
            signedDocuments[0] ||
            {};
        const statusText = String(
            signedDocument.esignstatus ||
                signedDocument.esignStatus ||
                signedDocument.eSignStatus ||
                signedDocument.status ||
                data.esignstatus ||
                data.esignStatus ||
                data.eSignStatus ||
                fullResponse.esignstatus ||
                fullResponse.esignStatus ||
                fullResponse.eSignStatus ||
                "",
        ).trim();
        const normalizedStatus = statusText.toLowerCase();
        const documentESignStatus =
            normalizeNorthCapitalESignStatus(statusText);
        const signedDocumentUrl =
            signedDocument.documentUrl ||
            signedDocument.documentURL ||
            signedDocument.url ||
            "";
        const esignStatus =
            tradeESignStatus ||
            documentESignStatus ||
            (signedDocumentUrl || normalizedStatus === "signed"
                ? "SIGNED"
                : "");

        return {
            esignStatus,
            signedDocumentUrl,
        };
    };

    const purchaseHistoryColumns = [
        {
            name: "Trade ID",
            selector: (row) => row.tradeId || "N/A",
            sortable: true,
            grow: 1.1,
        },
        {
            name: "Account Name",
            selector: (row) => row.accountName || "N/A",
            sortable: true,
            grow: 1.4,
        },
        {
            name: "Account ID",
            selector: (row) => row.accountId || "N/A",
            sortable: true,
            grow: 1.1,
        },
        {
            name: "Amount",
            selector: (row) => row.totalAmount || "N/A",
            sortable: true,
            right: true,
        },
        {
            name: "Shares",
            selector: (row) => row.totalShares || "N/A",
            sortable: true,
            right: true,
        },
        {
            name: "eSign Status",
            cell: (row) =>
                row.esignStatus ? (
                    <span>{row.esignStatus}</span>
                ) : (
                    <button
                        type="button"
                        className="inline-flex items-center rounded-md border border-navy px-2 py-1 text-xs font-medium text-navy hover:bg-[#eef6fb] disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={!row.tradeId || !row.accountId}
                        onClick={() => handleOpenSubscriptionSendModal(row)}
                        title={
                            row.tradeId && row.accountId
                                ? "Send subscription document"
                                : "Missing trade or account ID"
                        }
                    >
                        Send
                    </button>
                ),
            grow: 1.1,
        },
        {
            name: "Document",
            cell: (row) =>
                row.signedDocumentUrl ? (
                    <a
                        href={row.signedDocumentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-navy hover:underline"
                    >
                        View
                    </a>
                ) : (
                    "N/A"
                ),
            grow: 1.2,
        },
        {
            name: "Purchase Date",
            selector: (row) => row.purchaseDate || "N/A",
            sortable: true,
            grow: 1.4,
        },
    ];

    // Helper to resolve nested/array paths like 'updates.2'
    function getByPath(obj, path) {
        return path.split(".").reduce((acc, key) => {
            if (acc == null) return undefined;
            if (!isNaN(Number(key))) key = Number(key);
            return acc[key];
        }, obj);
    }

    // Handle to highlight the changes between campaign and drafts
    const highlightChange = (field) => {
        if (!campaignDraft || !campaignDetails) return "";
        const original = getByPath(campaignDetails, field);
        const updated = getByPath(campaignDraft, field);

        // If both undefined, no highlight
        if (original === undefined && updated === undefined) return "";
        // If only in draft, it's added
        if (original === undefined && updated !== undefined)
            return "bg-[#C2D7E2]";
        // If only in original, it's removed (for before column, if you want)
        if (original !== undefined && updated === undefined)
            return "bg-red-100";
        // If changed
        if (original !== updated) return "bg-[#C2D7E2]";
        return "";
    };

    // Utility function to detect changes in media files (images, PDFs, etc.)
    const highlightMediaChange = (field, originalIndex, draftIndex) => {
        if (!campaignDraft || !campaignDetails) return "";

        // Get the arrays from both objects
        const originalArray = getByPath(campaignDetails, field);
        const draftArray = getByPath(campaignDraft, field);

        // If arrays don't exist, no highlighting
        if (!originalArray || !draftArray) return "";

        // If we're looking at elements that don't exist in one array, it's a change
        if (
            originalIndex >= originalArray.length ||
            draftIndex >= draftArray.length
        )
            return "bg-[#C2D7E2]";

        // Get the items to compare
        const originalItem = originalArray[originalIndex];
        const draftItem = draftArray[draftIndex];

        // For media items, typically check the file path and any associated metadata
        if (JSON.stringify(originalItem) !== JSON.stringify(draftItem)) {
            return "bg-[#C2D7E2]";
        }

        return "";
    };

    // Utility function to detect media file type
    const getMediaType = (fileUrl) => {
        if (!fileUrl) return "unknown";
        if (/\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl)) return "image";
        if (/\.mp4$/i.test(fileUrl)) return "video";
        if (/\.pdf$/i.test(fileUrl)) return "pdf";
        return "unknown";
    };

    const normalizeAssetUrl = (url) => {
        if (!url || typeof url !== "string") return "";
        const cleaned = url.replace(/\\/g, "/");
        if (cleaned.startsWith("http")) {
            const uploadsMatch = cleaned.match(/\/uploads\/(.+)/);
            if (uploadsMatch) {
                return `${process.env.REACT_APP_BACKEND_URL}/uploads/${uploadsMatch[1]}`;
            }
            // No /uploads/ — extract path after domain and add /uploads/
            const pathAfterDomain = cleaned.replace(/^https?:\/\/[^/]+\/?/, "");
            if (pathAfterDomain) {
                return `${process.env.REACT_APP_BACKEND_URL}/uploads/${pathAfterDomain}`;
            }
            return cleaned;
        }
        // Relative path — prepend backend URL + /uploads/
        const path = cleaned.replace(/^\/?uploads\//, "");
        return `${process.env.REACT_APP_BACKEND_URL}/uploads/${path}`;
    };

    const getLegalDocumentPath = (document) => {
        if (typeof document === "string") return document;
        if (!document || typeof document !== "object") return "";

        return (
            document.file ||
            document.path ||
            document.fullPath ||
            document.filePath ||
            document.url ||
            document.fileName ||
            ""
        );
    };

    const getLegalDocumentUrl = (document) =>
        normalizeAssetUrl(getLegalDocumentPath(document));

    const getLegalDocumentName = (document, fallback) => {
        if (document?.name) return document.name;

        const path = getLegalDocumentPath(document);
        return path.split(/[\\/]/).pop()?.split(/[?#]/)[0] || fallback;
    };

    const hasMatchingLegalDocument = (documents, target) => {
        const targetPath = getLegalDocumentPath(target);
        return normalizeLegalDocumentCollection(documents).some(
            (document) => getLegalDocumentPath(document) === targetPath,
        );
    };

    const renderCampaignUpdate = (update) => {
        const isObject = typeof update === "object" && update !== null;
        const title = isObject ? update.title : "";
        const description = getUpdateDescription(update);
        const url = isObject ? update.url : "";
        const media = isObject ? update.media : "";

        return (
            <div className="space-y-2">
                {title && (
                    <p className="font-semibold text-navy break-words">
                        {title}
                    </p>
                )}
                {description && (
                    <p className="break-words whitespace-pre-wrap">
                        {description}
                    </p>
                )}
                {url && (
                    <a
                        href={normalizeExternalUrl(url)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-navy underline break-all"
                    >
                        <FaLink className="shrink-0" /> {url}
                    </a>
                )}
                {!description && !title && !url && !media && "Empty update"}
                {media && (
                    <div className="mt-2 p-2 border rounded">
                        {getMediaType(media) === "image" ? (
                            <img
                                src={normalizeAssetUrl(media)}
                                alt="Update media"
                                className="max-h-40 max-w-full object-contain"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = "none";
                                }}
                            />
                        ) : getMediaType(media) === "video" ? (
                            <video
                                src={normalizeAssetUrl(media)}
                                controls
                                className="max-h-40 max-w-full"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = "none";
                                }}
                            />
                        ) : getMediaType(media) === "pdf" ? (
                            <iframe
                                src={normalizeAssetUrl(media)}
                                title="PDF document"
                                width="100%"
                                height="150"
                                className="border rounded"
                            />
                        ) : (
                            <a
                                href={normalizeAssetUrl(media)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-navy underline"
                            >
                                Open media
                            </a>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const hasDraftChanges = () => {
        if (!campaignDraft || !campaignDetails) return false;

        const keys = Object.keys(campaignDetails);
        return keys.some((key) => {
            if (typeof campaignDetails[key] === "object") {
                return (
                    JSON.stringify(campaignDetails[key]) !==
                    JSON.stringify(campaignDraft[key])
                );
            }
            return campaignDetails[key] !== campaignDraft[key];
        });
    };

    const handleManageDocuments = async () => {
        const offeringId = campaignDetails?.north_capital_offering_id;

        if (!offeringId) {
            notifyUser(null, "error", "No offering ID found.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setManageDocumentsMode("documents");
        setManageDocumentsData({
            offeringId,
            documents: [],
            isEmpty: false,
        });
        setManageDocumentsLoading(true);
        setShowManageDocumentsModal(true);

        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/offering/get-documents-for-offering`,
                { offeringId },
                { withCredentials: true },
            );

            const documentDetails = Array.isArray(data?.data?.document_details)
                ? data.data.document_details
                : [];

            const documents = documentDetails.map((doc, index) => ({
                id: doc?.documentId || `document-${index}`,
                documentId: doc?.documentId || "",
                title: doc?.documentTitle || "Untitled Document",
                fileName: doc?.documentName || `Document ${index + 1}`,
                url: doc?.url || "",
            }));

            setManageDocumentsData({
                offeringId,
                documents,
                isEmpty: documents.length === 0,
            });
        } catch (error) {
            console.error("Error fetching offering documents:", error);

            if (error.response?.status === 400) {
                setManageDocumentsData({
                    offeringId,
                    documents: [],
                    isEmpty: true,
                });
            } else {
                setShowManageDocumentsModal(false);
                notifyUser(null, "error", error.response?.data?.error ||
                        "Failed to fetch documents for offering.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } finally {
            setManageDocumentsLoading(false);
        }
    };

    const handleManageSubscriptions = async () => {
        const offeringId = campaignDetails?.north_capital_offering_id;

        if (!offeringId) {
            notifyUser(null, "error", "No offering ID found.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setManageDocumentsMode("subscriptions");
        setManageDocumentsData({
            offeringId,
            documents: [],
            isEmpty: false,
        });
        setManageDocumentsLoading(true);
        setShowManageDocumentsModal(true);

        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/offering/getSubscriptionsforOffering`,
                { offeringId },
                { withCredentials: true },
            );

            const documents = getSubscriptionTemplatesFromPayload(data?.data);

            setManageDocumentsData({
                offeringId,
                documents,
                isEmpty: documents.length === 0,
            });
        } catch (error) {
            console.error("Error fetching offering subscriptions:", error);

            if (error.response?.status === 400) {
                setManageDocumentsData({
                    offeringId,
                    documents: [],
                    isEmpty: true,
                });
            } else {
                setShowManageDocumentsModal(false);
                notifyUser(null, "error", error.response?.data?.error ||
                        "Failed to fetch subscriptions for offering.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } finally {
            setManageDocumentsLoading(false);
        }
    };

    const handleOpenSubscriptionSendModal = async (trade) => {
        const offeringId =
            purchaseHistoryModalData.offeringId ||
            campaignDetails?.north_capital_offering_id;

        if (!offeringId) {
            notifyUser(null, "error", "No offering ID found.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        if (!trade?.tradeId || !trade?.accountId) {
            notifyUser(null, "error", "Trade ID and account ID are required.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setSubscriptionSendModalData({
            isOpen: true,
            offeringId,
            trade,
            templates: [],
            isEmpty: false,
        });
        setSubscriptionSendLoading(true);

        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/offering/getSubscriptionsforOffering`,
                { offeringId },
                { withCredentials: true },
            );

            const templates = getSubscriptionTemplatesFromPayload(data?.data);

            setSubscriptionSendModalData({
                isOpen: true,
                offeringId,
                trade,
                templates,
                isEmpty: templates.length === 0,
            });
        } catch (error) {
            console.error("Error fetching subscriptions for sending:", error);

            if (error.response?.status === 400) {
                setSubscriptionSendModalData({
                    isOpen: true,
                    offeringId,
                    trade,
                    templates: [],
                    isEmpty: true,
                });
            } else {
                setSubscriptionSendModalData({
                    isOpen: false,
                    offeringId: "",
                    trade: null,
                    templates: [],
                    isEmpty: false,
                });
                notifyUser(null, "error", error.response?.data?.error ||
                        "Failed to fetch subscriptions for offering.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } finally {
            setSubscriptionSendLoading(false);
        }
    };

    const handleSendSubscriptionDocument = async () => {
        const { offeringId, trade } = subscriptionSendModalData;

        if (!offeringId || !trade?.accountId || !trade?.tradeId) {
            notifyUser(null, "error", "Offering ID, account ID, and trade ID are required.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setSubscriptionSendSubmitting(true);

        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/offering/sendSubscriptionDocument`,
                {
                    offeringId,
                    accountId: trade.accountId,
                    tradeId: trade.tradeId,
                },
                { withCredentials: true },
            );

            if (!data?.data?.success) {
                throw new Error(
                    data?.error || "Failed to send subscription document.",
                );
            }

            notifyUser(null, "success", "Subscription document sent successfully.", "toast", null, null, null, null, null, false, false, "top-center");
            setPurchaseHistoryModalData((prev) => ({
                ...prev,
                purchaseHistory: prev.purchaseHistory.map((purchase) =>
                    purchase.tradeId === trade.tradeId
                        ? {
                              ...purchase,
                              esignStatus: "SENT",
                          }
                        : purchase,
                ),
            }));
            setSubscriptionSendModalData({
                isOpen: false,
                offeringId: "",
                trade: null,
                templates: [],
                isEmpty: false,
            });
        } catch (error) {
            console.error("sendSubscriptionDocument failed:", error);
            notifyUser(null, "error", error.response?.data?.error ||
                    error.message ||
                    "Failed to send subscription document.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setSubscriptionSendSubmitting(false);
        }
    };

    const handleViewPurchaseHistory = async () => {
        const offeringId = campaignDetails?.north_capital_offering_id;

        if (!offeringId) {
            notifyUser(null, "error", "No offering ID found.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        try {
            setPurchaseHistoryModalData({
                offeringId,
                offeringDetail: {},
                purchaseHistoryDetails: {},
                purchaseHistory: [],
            });
            setPurchaseHistoryLoading(true);
            setShowPurchaseHistoryModal(true);

            const [
                purchaseHistoryResponse,
                offeringDetailsResponse,
                tradeStatusesResponse,
            ] = await Promise.all([
                axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/offering/get-offering-purchase-history`,
                    { offeringId },
                    { withCredentials: true },
                ),
                axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/offering/get-offering-details`,
                    { offeringId },
                    { withCredentials: true },
                ),
                axios
                    .post(
                        `${process.env.REACT_APP_BACKEND_URL}/api/offering/get-trade-status-for-offering`,
                        { offeringId },
                        { withCredentials: true },
                    )
                    .catch((error) => {
                        console.warn(
                            "Error fetching trade statuses for offering:",
                            error,
                        );
                        return null;
                    }),
            ]);

            const purchaseHistoryPayload =
                purchaseHistoryResponse?.data?.data || {};
            const offeringDetailsPayload =
                offeringDetailsResponse?.data?.data || {};

            const purchaseHistoryDetails =
                purchaseHistoryPayload.purchaseHistoryDetails ||
                purchaseHistoryPayload.fullResponse?.[
                    "Offering purchased details"
                ] ||
                {};

            const rawOfferingDetails =
                offeringDetailsPayload.offeringDetails ||
                offeringDetailsPayload.fullResponse?.[
                    "Offering purchased details"
                ] ||
                [];

            const offeringDetails = Array.isArray(rawOfferingDetails)
                ? rawOfferingDetails
                : rawOfferingDetails
                  ? [rawOfferingDetails]
                  : [];
            const offeringDetail = offeringDetails[0] || {};
            const purchaseHistory = Array.isArray(
                purchaseHistoryDetails?.offering_purchase_history,
            )
                ? purchaseHistoryDetails.offering_purchase_history
                : [];
            const tradeDetails = getTradeDetailsFromPayload(
                tradeStatusesResponse?.data,
            );
            const tradeESignStatusById = tradeDetails.reduce(
                (lookup, trade) => {
                    const tradeId = getNorthCapitalField(trade, [
                        "tradeId",
                        "tradeID",
                        "trade_id",
                        "TradeID",
                        "Trade ID",
                    ]);
                    const normalizedTradeId = getNorthCapitalTradeId(tradeId);
                    if (normalizedTradeId) {
                        lookup[normalizedTradeId] = getTradeESignStatus(trade);
                    }
                    return lookup;
                },
                {},
            );
            const purchaseHistoryWithSignedDocuments = await Promise.all(
                purchaseHistory.map(async (purchase) => {
                    const tradeId = purchase.tradeId;
                    const tradeESignStatus =
                        tradeESignStatusById[getNorthCapitalTradeId(tradeId)] ||
                        "";

                    if (!tradeId) {
                        return {
                            ...purchase,
                            esignStatus: "",
                            signedDocumentUrl: "",
                        };
                    }

                    try {
                        const signedDocumentResponse = await axios.post(
                            `${process.env.REACT_APP_BACKEND_URL}/api/offering/getTradeSignedDocument`,
                            { tradeId },
                            { withCredentials: true },
                        );
                        const signedDocumentSummary = getSignedDocumentSummary(
                            signedDocumentResponse?.data?.data,
                            tradeId,
                            tradeESignStatus,
                        );

                        return {
                            ...purchase,
                            ...signedDocumentSummary,
                        };
                    } catch (error) {
                        console.warn(
                            "Error fetching signed document for trade:",
                            tradeId,
                            error,
                        );
                        const signedDocumentSummary = getSignedDocumentSummary(
                            error.response?.data?.data || error.response?.data,
                            tradeId,
                            tradeESignStatus,
                        );
                        return {
                            ...purchase,
                            ...signedDocumentSummary,
                        };
                    }
                }),
            );

            if (
                purchaseHistory.length === 0 &&
                Object.keys(offeringDetail).length === 0
            ) {
                setShowPurchaseHistoryModal(false);
                Swal.fire({
                    icon: "info",
                    title: "Offering Details",
                    text: "No purchase history available as there are no investments for this campaign.",
                });
                return;
            }

            setPurchaseHistoryModalData({
                offeringId,
                offeringDetail,
                purchaseHistoryDetails,
                purchaseHistory: purchaseHistoryWithSignedDocuments,
            });
        } catch (error) {
            setShowPurchaseHistoryModal(false);
            console.error(
                "Error fetching offering details and purchase history:",
                error,
            );
            notifyUser(null, "error", error.response?.data?.error ||
                    "Failed to fetch offering details and purchase history.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setPurchaseHistoryLoading(false);
        }
    };

    return (
        <div
            className="aqi-grid-page font-poppins"
            style={{
                padding: "30px",
                width: "90%",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                position: "relative",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "20px",
                    fontSize: "large",
                    fontFamily: "large",
                }}
            >
                CAMPAIGNS
            </h2>

            {/* Filters Section */}
            <div className="aqi-grid-toolbar">
                {/* Rows per page */}
                <div>
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={perPage}
                        onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        {GRID_CONFIG.pageSizeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>

                {/* Status Filter */}
                <div>
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                            width: "clamp(130px, 16vw, 250px)",
                            minWidth: "120px",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="onhold">On Hold</option>
                        <option value="withdrawn">Withdrawn</option>
                        <option value="closed">Closed</option>
                        <option value="ended">Ended</option>
                        <option value="completed">Completed</option>
                    </select>
                </div>

                {/* Draft Edits Filter */}
                <div>
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                            width: "clamp(130px, 16vw, 250px)",
                            minWidth: "120px",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={draftFilter}
                        onChange={(e) => {
                            setDraftFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="all">All Draft Status</option>
                        <option value="with-drafts">With Draft Edits</option>
                        <option value="no-drafts">No Draft Edits</option>
                    </select>
                </div>

                <div 
                    className="aqi-grid-search"
                    style={{
                    width: "clamp(140px, 18vw, 280px)",
                    minWidth: "130px",
                    flexShrink: 1,
                    }}>
                    <FaSearch
                        className="absolute left-3 top-2.5"
                        style={{ color: "var(--navy)" }}
                    />
                    <input
                        type="text"
                        placeholder="Search campaigns..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        style={{
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            borderRadius: "0.375rem",
                            border: "2px solid #C2D7E2",
                            backgroundColor: "#F5F7FA",
                            transition: "all 0.2s ease",
                            outline: "none",
                            color: "var(--navy)",
                            width: "100%"
                        }}
                        className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                    />
                </div>

                <div className="aqi-grid-toolbar-actions">
                    <button
                        onClick={handleExportCSV}
                        title="Export CSV"
                        className="aqi-grid-button aqi-grid-button--primary"
                    >
                        Export
                    </button>
                </div>
            </div>

            <div
                style={{
                    borderRadius: "6px",
                    width: "100%",
                }}
            >
                {showPurchaseHistoryModal && (
                    <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center px-2">
                        <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] p-6 md:p-8 relative overflow-y-auto font-poppins shadow-2xl">
                            <button
                                className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold transition"
                                onClick={() =>
                                    setShowPurchaseHistoryModal(false)
                                }
                            >
                                ×
                            </button>

                            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-navy pr-10 justify-center flex">
                                Purchase History - Offering{" "}
                                {purchaseHistoryModalData.offeringId}
                            </h3>
                            {purchaseHistoryLoading ? (
                                <div className="flex min-h-[72vh] flex-col items-center justify-center text-navy">
                                    <Lottie
                                        animationData={trailLoadingAnimation}
                                        loop={true}
                                        className="h-44 w-44"
                                    />
                                    <p className="mt-4 text-sm md:text-base font-medium text-gray-600">
                                        Loading purchase history...
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <section className="mb-6">
                                        <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                                            Offering Details
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                            <p>
                                                <strong>
                                                    Committed Amount:
                                                </strong>{" "}
                                                {purchaseHistoryModalData
                                                    .offeringDetail
                                                    .committedAmount || "N/A"}
                                            </p>
                                            <p>
                                                <strong>Funded Amount:</strong>{" "}
                                                {purchaseHistoryModalData
                                                    .offeringDetail
                                                    .fundedAmount || "N/A"}
                                            </p>
                                            <p>
                                                <strong>Pending Amount:</strong>{" "}
                                                {purchaseHistoryModalData
                                                    .offeringDetail
                                                    .pendingAmount || "N/A"}
                                            </p>
                                            <p>
                                                <strong>
                                                    Escrow Account Number:
                                                </strong>{" "}
                                                {purchaseHistoryModalData
                                                    .offeringDetail
                                                    .escrowAccountNumber ||
                                                    "N/A"}
                                            </p>
                                        </div>
                                    </section>

                                    <section className="mb-6">
                                        <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                                            Purchase History
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed mb-4">
                                            <p>
                                                <strong>Total Raised:</strong>{" "}
                                                {purchaseHistoryModalData
                                                    .purchaseHistoryDetails
                                                    .totalAmountRaised || "N/A"}
                                            </p>
                                            <p>
                                                <strong>Total Shares:</strong>{" "}
                                                {purchaseHistoryModalData
                                                    .purchaseHistoryDetails
                                                    .totalShares || "N/A"}
                                            </p>
                                            <p>
                                                <strong>
                                                    Shares Purchased:
                                                </strong>{" "}
                                                {purchaseHistoryModalData
                                                    .purchaseHistoryDetails
                                                    .totalSharesPurchased ||
                                                    "N/A"}
                                            </p>
                                            <p>
                                                <strong>Shares Left:</strong>{" "}
                                                {purchaseHistoryModalData
                                                    .purchaseHistoryDetails
                                                    .totalSharesLeft || "N/A"}
                                            </p>
                                        </div>

                                        <DataTable
                                            columns={purchaseHistoryColumns}
                                            data={
                                                purchaseHistoryModalData.purchaseHistory
                                            }
                                            customStyles={{
                                                ...customStyles,
                                                rows: {
                                                    style: {
                                                        cursor: "default",
                                                        minHeight: "52px",
                                                    },
                                                },
                                            }}
                                            pagination
                                            responsive
                                            highlightOnHover
                                            persistTableHead
                                            noDataComponent={
                                                <div className="py-6 text-sm text-gray-500">
                                                    No purchase history
                                                    available as there are no
                                                    investments for this
                                                    campaign.
                                                </div>
                                            }
                                        />
                                    </section>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {subscriptionSendModalData.isOpen && (
                    <div className="fixed inset-0 z-[80] bg-black bg-opacity-50 flex items-center justify-center px-4">
                        <div className="bg-white rounded-2xl w-full max-w-3xl p-6 md:p-8 relative overflow-y-auto max-h-[90vh] font-poppins shadow-2xl">
                            <button
                                className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold transition disabled:opacity-50"
                                onClick={() => {
                                    if (!subscriptionSendSubmitting) {
                                        setSubscriptionSendModalData({
                                            isOpen: false,
                                            offeringId: "",
                                            trade: null,
                                            templates: [],
                                            isEmpty: false,
                                        });
                                    }
                                }}
                                disabled={subscriptionSendSubmitting}
                            >
                                x
                            </button>

                            <h3 className="text-2xl font-bold mb-2 text-navy pr-10">
                                Send Subscription Document
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-700 mb-6">
                                <p>
                                    <strong>Offering:</strong>{" "}
                                    {subscriptionSendModalData.offeringId ||
                                        "N/A"}
                                </p>
                                <p>
                                    <strong>Trade:</strong>{" "}
                                    {subscriptionSendModalData.trade?.tradeId ||
                                        "N/A"}
                                </p>
                                <p>
                                    <strong>Account:</strong>{" "}
                                    {subscriptionSendModalData.trade
                                        ?.accountId || "N/A"}
                                </p>
                            </div>

                            {subscriptionSendLoading ? (
                                <div className="flex min-h-[240px] flex-col items-center justify-center text-navy">
                                    <Lottie
                                        animationData={trailLoadingAnimation}
                                        loop={true}
                                        className="h-32 w-32"
                                    />
                                    <p className="mt-4 text-sm md:text-base font-medium text-gray-600">
                                        Loading DocuSign templates...
                                    </p>
                                </div>
                            ) : subscriptionSendModalData.isEmpty ? (
                                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
                                    <h4 className="text-lg font-semibold text-navy mb-2">
                                        No subscriptions for offering yet
                                    </h4>
                                    <p className="text-sm text-gray-600 mb-5">
                                        Please manage the subscriptions for this
                                        offering before sending a subscription
                                        document.
                                    </p>
                                    {isDocuSignIntegrated && (
                                    <button
                                        type="button"
                                        className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy-2 transition-colors"
                                        onClick={() => {
                                            setSubscriptionSendModalData({
                                                isOpen: false,
                                                offeringId: "",
                                                trade: null,
                                                templates: [],
                                                isEmpty: false,
                                            });
                                            handleManageSubscriptions();
                                        }}
                                    >
                                        <FaEye size={12} />
                                        Manage Subscriptions
                                    </button>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="mb-6">
                                        <h4 className="text-lg font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                                            DocuSign Template
                                        </h4>
                                        <div className="space-y-3">
                                            {subscriptionSendModalData.templates.map(
                                                (template) => (
                                                    <div
                                                        key={template.id}
                                                        className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3"
                                                    >
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-semibold text-gray-800 truncate">
                                                                {
                                                                    template.title
                                                                }
                                                            </p>
                                                            <p className="text-xs text-gray-500 truncate">
                                                                {
                                                                    template.fileName
                                                                }
                                                            </p>
                                                            {template.createdDate && (
                                                                <p className="text-xs text-gray-400 truncate">
                                                                    Updated:{" "}
                                                                    {
                                                                        template.createdDate
                                                                    }
                                                                </p>
                                                            )}
                                                        </div>
                                                        {template.url ? (
                                                            <a
                                                                href={
                                                                    template.url
                                                                }
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center rounded-md px-2 py-1 text-sm text-navy hover:bg-[#eef6fb] whitespace-nowrap"
                                                            >
                                                                <FaEye className="mr-1" />
                                                                View
                                                            </a>
                                                        ) : (
                                                            <span className="text-xs text-gray-400 whitespace-nowrap">
                                                                No preview
                                                            </span>
                                                        )}
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-3">
                                        <button
                                            type="button"
                                            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                                            onClick={() =>
                                                setSubscriptionSendModalData({
                                                    isOpen: false,
                                                    offeringId: "",
                                                    trade: null,
                                                    templates: [],
                                                    isEmpty: false,
                                                })
                                            }
                                            disabled={
                                                subscriptionSendSubmitting
                                            }
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy-2 transition-colors disabled:opacity-60"
                                            onClick={
                                                handleSendSubscriptionDocument
                                            }
                                            disabled={
                                                subscriptionSendSubmitting
                                            }
                                        >
                                            <FaCheck size={12} />
                                            {subscriptionSendSubmitting
                                                ? "Sending..."
                                                : "Send Subscription Document"}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {showManageDocumentsModal && (
                    <div className="fixed inset-0 z-[70] bg-black bg-opacity-50 flex items-center justify-center px-2">
                        <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] p-6 md:p-8 relative overflow-y-auto font-poppins shadow-2xl">
                            <input
                                ref={replacementDocumentInputRef}
                                type="file"
                                className="hidden"
                                onChange={handleReplacementDocumentChange}
                            />
                            <button
                                className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold transition"
                                onClick={() =>
                                    setShowManageDocumentsModal(false)
                                }
                            >
                                ×
                            </button>

                            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-navy pr-10">
                                {manageDocumentsMode === "subscriptions"
                                    ? "Manage Subscriptions"
                                    : "Manage Documents"}{" "}
                                - Offering {manageDocumentsData.offeringId}
                            </h3>

                            {manageDocumentsMode === "documents" && (
                                <div className="flex flex-wrap justify-end gap-3 mb-6">
                                    <button
                                        className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy-2 transition-colors"
                                        onClick={() =>
                                            handleAddDocumentsForOffering(true)
                                        }
                                    >
                                        <FaPlus size={12} />
                                        Manage Documents
                                    </button>
                                    {isDocuSignIntegrated && (
                                    <button
                                        className="inline-flex items-center gap-2 border border-navy text-navy px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy hover:text-white transition-colors"
                                        onClick={handleManageSubscriptions}
                                    >
                                        <FaEye size={12} />
                                        Manage Subscriptions
                                    </button>
                                    )}
                                </div>
                            )}

                            {manageDocumentsLoading ? (
                                <div className="flex min-h-[280px] flex-col items-center justify-center text-navy">
                                    <Lottie
                                        animationData={trailLoadingAnimation}
                                        loop={true}
                                        className="h-36 w-36"
                                    />
                                    <p className="mt-4 text-sm md:text-base font-medium text-gray-600">
                                        Loading offering{" "}
                                        {manageDocumentsMode === "subscriptions"
                                            ? "subscriptions"
                                            : "documents"}
                                        ...
                                    </p>
                                </div>
                            ) : manageDocumentsData.isEmpty ? (
                                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
                                    <h4 className="text-lg font-semibold text-navy mb-2">
                                        {manageDocumentsMode === "subscriptions"
                                            ? "No subscriptions found"
                                            : "No documents uploaded"}
                                    </h4>
                                    <p className="text-sm text-gray-600 mb-5">
                                        {manageDocumentsMode === "subscriptions"
                                            ? "No subscriptions are linked to this offering yet."
                                            : "No documents are uploaded for this offering yet. Upload here using Add Documents."}
                                    </p>
                                    {manageDocumentsMode ===
                                        "subscriptions" && (
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy-2 transition-colors"
                                            onClick={
                                                handleAddSubscriptionsForOffering
                                            }
                                        >
                                            <FaPlus size={12} />
                                            Assign Subscription
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <p className="text-sm text-gray-600 mb-4">
                                        {manageDocumentsMode === "subscriptions"
                                            ? "Review the subscriptions currently linked to this offering."
                                            : "Review the documents currently uploaded for this offering."}
                                    </p>
                                    <div className="space-y-3">
                                        {manageDocumentsData.documents.map(
                                            (doc) => (
                                                <div
                                                    key={doc.id}
                                                    className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3"
                                                >
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-semibold text-gray-800 truncate">
                                                            {doc.title}
                                                        </p>
                                                        <p className="text-xs text-gray-500 truncate">
                                                            {doc.fileName}
                                                        </p>
                                                        {manageDocumentsMode ===
                                                            "subscriptions" &&
                                                            doc.createdDate && (
                                                                <p className="text-xs text-gray-400 truncate">
                                                                    Created:{" "}
                                                                    {
                                                                        doc.createdDate
                                                                    }
                                                                </p>
                                                            )}
                                                    </div>
                                                    <div className="flex items-center gap-2 whitespace-nowrap">
                                                        {doc.url ? (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleManageDocumentView(
                                                                        doc,
                                                                    )
                                                                }
                                                                className="inline-flex items-center rounded-md px-2 py-1 text-sm text-navy hover:bg-[#eef6fb]"
                                                                title="View"
                                                            >
                                                                <FaEye className="mr-1" />
                                                                View
                                                            </button>
                                                        ) : (
                                                            <span className="text-xs text-gray-400 whitespace-nowrap px-2 py-1">
                                                                No preview
                                                                available
                                                            </span>
                                                        )}
                                                        {manageDocumentsMode ===
                                                            "documents" && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="inline-flex items-center rounded-md px-2 py-1 text-sm text-amber-600 hover:bg-amber-50"
                                                                    disabled={
                                                                        manageDocumentUpdatingId ===
                                                                        (doc.documentId ||
                                                                            doc.id)
                                                                    }
                                                                    onClick={() =>
                                                                        handleManageDocumentUpdate(
                                                                            doc,
                                                                        )
                                                                    }
                                                                    title="Update"
                                                                >
                                                                    <FaEdit className="mr-1" />
                                                                    {manageDocumentUpdatingId ===
                                                                    (doc.documentId ||
                                                                        doc.id)
                                                                        ? "Updating..."
                                                                        : "Update"}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="inline-flex items-center rounded-md px-2 py-1 text-sm text-red-600 hover:bg-red-50"
                                                                    onClick={() =>
                                                                        handleManageDocumentDelete(
                                                                            doc,
                                                                        )
                                                                    }
                                                                    title="Delete"
                                                                >
                                                                    <FaTrash className="mr-1" />
                                                                    Delete
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {replacementTargetDocument && (
                    <div className="fixed inset-0 z-[86] bg-black bg-opacity-50 flex items-center justify-center px-4">
                        <div
                            className="bg-white rounded-2xl w-full min-h-[27rem] p-6 md:p-7 relative shadow-2xl font-poppins flex flex-col justify-center"
                            style={{
                                maxWidth: updateDocumentModalWidth,
                                padding: updateDocumentModalPadding,
                            }}
                        >
                            <button
                                className="absolute top-3 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold transition disabled:opacity-50"
                                onClick={() => {
                                    if (!manageDocumentUpdatingId) {
                                        setReplacementTargetDocument(null);
                                        setReplacementDocumentDragActive(false);
                                    }
                                }}
                                disabled={!!manageDocumentUpdatingId}
                            >
                                ×
                            </button>

                            <h3 className="text-xl font-bold text-navy pr-8 mb-3">
                                Update Document
                            </h3>

                            <p className="text-sm text-gray-600 leading-6 mb-3">
                                Replace{" "}
                                {replacementTargetDocument.fileName ||
                                    "this document"}{" "}
                                by dragging a file here or selecting one from
                                your device.
                            </p>

                            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 mb-6">
                                <p className="text-sm font-medium text-gray-800 break-all">
                                    {replacementTargetDocument.fileName ||
                                        replacementTargetDocument.title ||
                                        "Document"}
                                </p>
                            </div>

                            <div
                                role="button"
                                tabIndex={0}
                                className={`rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
                                    replacementDocumentDragActive
                                        ? "border-navy bg-[#eef6fb]"
                                        : "border-gray-300 bg-gray-50"
                                } ${manageDocumentUpdatingId ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                                onClick={() => {
                                    if (!manageDocumentUpdatingId) {
                                        openReplacementDocumentPicker();
                                    }
                                }}
                                onKeyDown={(event) => {
                                    if (
                                        !manageDocumentUpdatingId &&
                                        (event.key === "Enter" ||
                                            event.key === " ")
                                    ) {
                                        event.preventDefault();
                                        openReplacementDocumentPicker();
                                    }
                                }}
                                onDragOver={(event) => {
                                    event.preventDefault();
                                    if (!manageDocumentUpdatingId) {
                                        setReplacementDocumentDragActive(true);
                                    }
                                }}
                                onDragLeave={() =>
                                    setReplacementDocumentDragActive(false)
                                }
                                onDrop={(event) => {
                                    if (!manageDocumentUpdatingId) {
                                        handleReplacementDocumentDrop(event);
                                    }
                                }}
                            >
                                <p className="text-base font-semibold text-navy mb-2">
                                    Drag and drop a file here
                                </p>
                                <p className="text-sm text-gray-600 mb-4">
                                    or click to upload a replacement file
                                </p>
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy text-white text-sm font-medium hover:bg-navy-2 transition-colors disabled:opacity-50"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        openReplacementDocumentPicker();
                                    }}
                                    disabled={!!manageDocumentUpdatingId}
                                >
                                    <FaPlus size={12} />
                                    {manageDocumentUpdatingId
                                        ? "Updating..."
                                        : "Upload File"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showDocSelectModal && (
                    <div className="fixed inset-0 z-[80] bg-black bg-opacity-50 flex items-center justify-center px-2">
                        <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] p-6 md:p-8 relative overflow-y-auto font-poppins shadow-2xl">
                            <button
                                className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold transition"
                                onClick={() => {
                                    setShowDocSelectModal(false);
                                    setDocOfferingSubmitting(false);
                                }}
                            >
                                ×
                            </button>

                            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-navy pr-10">
                                {docModalMode === "subscriptions"
                                    ? "Assign Subscription Offerings"
                                    : "Add Documents for Offering"}
                            </h3>

                            <p className="text-sm text-gray-600 mb-5">
                                {docModalMode === "subscriptions"
                                    ? "Select from the available subscription templates."
                                    : "Select from the campaign documents available in uploaded files, legal uploads, and additional uploads."}
                            </p>

                            <div className="space-y-3 mb-6">
                                {availableDocsForOffering.length === 0 &&
                                    !docOfferingSubmitting && (
                                        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center text-sm text-gray-600">
                                            {docModalMode === "subscriptions"
                                                ? "No subscription templates available."
                                                : "No campaign documents available."}
                                        </div>
                                    )}
                                {availableDocsForOffering.map((doc, idx) => {
                                    const BASE_URL =
                                        process.env.REACT_APP_BACKEND_URL;
                                    let previewUrl =
                                        docModalMode === "subscriptions"
                                            ? ""
                                            : doc?.path || "";

                                    if (
                                        previewUrl &&
                                        !/^https?:\/\//.test(previewUrl)
                                    ) {
                                        if (
                                            previewUrl.startsWith(
                                                "/uploads/",
                                            ) ||
                                            previewUrl.startsWith("/")
                                        ) {
                                            previewUrl = `${BASE_URL}${previewUrl}`;
                                        } else {
                                            previewUrl = `${BASE_URL}/uploads/${previewUrl}`;
                                        }
                                    }

                                    previewUrl = previewUrl.replace(/\\/g, "/");

                                    return (
                                        <div
                                            key={`${doc.source}-${doc.key}-${doc.fileName}-${idx}`}
                                            className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3"
                                        >
                                            <input
                                                type="checkbox"
                                                id={`doc-select-${idx}`}
                                                checked={
                                                    !!selectedDocsForOffering[
                                                        idx
                                                    ]
                                                }
                                                onChange={() =>
                                                    setSelectedDocsForOffering(
                                                        (prev) => ({
                                                            ...prev,
                                                            [idx]: !prev[idx],
                                                        }),
                                                    )
                                                }
                                                className="accent-navy w-4 h-4 cursor-pointer"
                                            />

                                            <label
                                                htmlFor={`doc-select-${idx}`}
                                                className="flex-1 min-w-0 cursor-pointer"
                                            >
                                                <p className="text-sm font-semibold text-gray-800 truncate">
                                                    {doc.label}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">
                                                    {doc.fileName}
                                                </p>
                                                {docModalMode ===
                                                    "subscriptions" &&
                                                    doc.ownerEmail && (
                                                        <p className="text-xs text-gray-400 truncate">
                                                            Owner:{" "}
                                                            {doc.ownerEmail}
                                                        </p>
                                                    )}
                                            </label>

                                            {previewUrl ? (
                                                <a
                                                    href={previewUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center rounded-md px-2 py-1 text-sm text-navy hover:bg-[#eef6fb]"
                                                >
                                                    <FaEye className="mr-1" />
                                                    View
                                                </a>
                                            ) : (
                                                <span className="text-xs text-gray-400">
                                                    No preview available
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                                    onClick={() => {
                                        setShowDocSelectModal(false);
                                        setDocOfferingSubmitting(false);
                                    }}
                                    disabled={docOfferingSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="px-4 py-2 text-sm text-white bg-navy rounded-md hover:bg-navy-2 transition disabled:opacity-50"
                                    onClick={
                                        docModalMode === "subscriptions"
                                            ? handleSubmitSelectedSubscriptions
                                            : handleSubmitSelectedDocsForOffering
                                    }
                                    disabled={
                                        docOfferingSubmitting ||
                                        Object.values(
                                            selectedDocsForOffering,
                                        ).every((value) => !value)
                                    }
                                >
                                    {docOfferingSubmitting
                                        ? "Submitting..."
                                        : docModalMode === "subscriptions"
                                          ? "Assign Subscription"
                                          : "Upload Selected Documents"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {manageDocumentPreviewDoc && (
                    <div className="fixed inset-0 z-[85] bg-black bg-opacity-50 flex items-center justify-center px-4">
                        <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] p-6 md:p-7 relative shadow-2xl font-poppins overflow-y-auto">
                            <button
                                className="absolute top-3 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold transition"
                                onClick={() =>
                                    setManageDocumentPreviewDoc(null)
                                }
                            >
                                ×
                            </button>

                            <h3 className="text-xl font-bold text-navy pr-8 mb-3">
                                {manageDocumentPreviewDoc.title ||
                                    "Document Preview"}
                            </h3>

                            <p className="text-sm text-gray-500 mb-5 break-all">
                                {manageDocumentPreviewDoc.fileName ||
                                    manageDocumentPreviewDoc.url}
                            </p>

                            {isImageDocument(manageDocumentPreviewDoc) ? (
                                <img
                                    src={manageDocumentPreviewDoc.url}
                                    alt={
                                        manageDocumentPreviewDoc.fileName ||
                                        "Document preview"
                                    }
                                    className="max-w-full max-h-[65vh] mx-auto rounded-lg border border-gray-200"
                                />
                            ) : (
                                <iframe
                                    src={manageDocumentPreviewDoc.url}
                                    title={
                                        manageDocumentPreviewDoc.fileName ||
                                        "Document Preview"
                                    }
                                    className="w-full h-[65vh] rounded-lg border border-gray-200"
                                />
                            )}

                            <div className="flex justify-end gap-3 mt-5">
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    onClick={() =>
                                        setManageDocumentPreviewDoc(null)
                                    }
                                >
                                    Close
                                </button>
                                <a
                                    href={manageDocumentPreviewDoc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-4 py-2 rounded-lg bg-navy text-white text-sm font-medium hover:bg-navy-2 transition-colors"
                                >
                                    Open in New Tab
                                </a>
                            </div>
                        </div>
                    </div>
                )}

                {manageDocumentNoPreviewDoc && (
                    <div className="fixed inset-0 z-[85] bg-black bg-opacity-50 flex items-center justify-center px-4">
                        <div className="bg-white rounded-2xl w-full max-w-md p-6 md:p-7 relative shadow-2xl font-poppins">
                            <button
                                className="absolute top-3 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold transition"
                                onClick={() =>
                                    setManageDocumentNoPreviewDoc(null)
                                }
                            >
                                ×
                            </button>

                            <h3 className="text-xl font-bold text-navy pr-8 mb-3">
                                Preview Not Available
                            </h3>

                            <p className="text-sm text-gray-600 leading-6 mb-5">
                                This file does not have preview support here.
                                Download it to view.
                            </p>

                            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 mb-5">
                                <p className="text-sm font-medium text-gray-800 break-all">
                                    {manageDocumentNoPreviewDoc.fileName ||
                                        manageDocumentNoPreviewDoc.title ||
                                        "Document"}
                                </p>
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    onClick={() =>
                                        setManageDocumentNoPreviewDoc(null)
                                    }
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex items-center px-4 py-2 rounded-lg bg-navy text-white text-sm font-medium hover:bg-navy-2 transition-colors disabled:opacity-50"
                                    onClick={() =>
                                        handleManageDocumentDownload(
                                            manageDocumentNoPreviewDoc,
                                        )
                                    }
                                    disabled={manageDocumentDownloading}
                                >
                                    {manageDocumentDownloading
                                        ? "Downloading..."
                                        : "Download"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showModal && campaignDetails && (
                    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center px-2">
                        <div className="bg-white rounded-2xl w-full max-w-6xl p-6 md:p-8 relative max-h-[90vh] overflow-y-auto font-poppins shadow-2xl">
                            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-navy">
                                {hasDraftChanges()
                                    ? "Review Edit Changes"
                                    : "Campaign Details"}

                                {/* On-Hold Status Message: Only show in Campaign Details mode, below action buttons */}
                                {!hasDraftChanges() &&
                                    campaignDetails.status === "onhold" && (
                                        <div className="mt-5 mb-6 p-3 rounded-lg bg-yellow-100 border-l-4 border-yellow-500 text-yellow-900 shadow flex justify-center items-center">
                                            <div className="flex flex-row items-center gap-2 justify-center w-full">
                                                <FaExclamationTriangle className="h-5 w-5 flex-shrink-0 text-yellow-600" />
                                                <span className="block mb-0 text-center text-sm">
                                                    This campaign is currently{" "}
                                                    <b>on-hold</b>. To make a
                                                    change, you must first
                                                    cancel the on-hold status.
                                                </span>
                                            </div>
                                        </div>
                                    )}
                            </h3>

                            {/* Action Buttons - show for all except deleted; show Disable for rejected */}
                            {campaignDetails.status !== "deleted" && (
                                <div className="flex flex-wrap justify-end gap-2 mb-6">
                                    {/* Deleted campaigns only expose notes; disabled campaigns expose notes + enable. */}
                                    {campaignDetails.status === "disabled" ||
                                    campaignDetails.is_disabled === 1 ? (
                                        <>
                                            <button
                                                className="flex items-center border border-gray-500 text-gray-700 px-3 py-1.5 text-sm rounded hover:bg-gray-200"
                                                onClick={() =>
                                                    setShowNoteBox(!showNoteBox)
                                                }
                                            >
                                                <span className="mr-1">
                                                    {showNoteBox ? (
                                                        <FaTimes />
                                                    ) : (
                                                        <FaPlus />
                                                    )}
                                                </span>
                                                Internal Notes
                                            </button>
                                            {campaignDetails.disabled_edit_enabled !==
                                                1 && (
                                                <button
                                                    className="flex items-center border border-navy text-navy px-3 py-1.5 text-sm rounded hover:bg-navy hover:text-white"
                                                    onClick={() =>
                                                        handleEnableDisabledEdit(
                                                            campaignDetails.id,
                                                        )
                                                    }
                                                >
                                                    <FaPencilAlt className="mr-1" />{" "}
                                                    Enable Edit
                                                </button>
                                            )}
                                            {hasDraftChanges() && (
                                                <button
                                                    className="flex items-center border border-green-600 text-green-600 px-3 py-1.5 text-sm rounded hover:bg-green-600 hover:text-white"
                                                    onClick={() =>
                                                        handleCampaignApproval(
                                                            campaignDetails.id,
                                                            "approve",
                                                        )
                                                    }
                                                >
                                                    <FaCheck className="mr-1" />{" "}
                                                    Accept changes
                                                </button>
                                            )}
                                            <button
                                                className="flex items-center border border-green-600 text-green-600 px-3 py-1.5 text-sm rounded hover:bg-green-600 hover:text-white"
                                                onClick={() =>
                                                    handleEnableCampaign(
                                                        campaignDetails.id,
                                                    )
                                                }
                                            >
                                                <FaCheck className="mr-1" />{" "}
                                                Enable
                                            </button>
                                        </>
                                    ) : campaignDetails.status ===
                                      "rejected" ? (
                                        <>
                                            <div className="relative">
                                                <button
                                                    className="aqi-application-nc-button flex items-center px-4 py-2 text-sm rounded-md"
                                                    onClick={() =>
                                                        setShowActionsDropdown(
                                                            (prev) => !prev,
                                                        )
                                                    }
                                                >
                                                    <FaEdit className="mr-2" />{" "}
                                                    Actions
                                                </button>
                                                {showActionsDropdown && (
                                                    <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                                        <button
                                                            className="w-full text-left px-4 py-3 text-sm hover:bg-gray-100 transition text-gray-500 flex items-center"
                                                            onClick={() => {
                                                                setShowActionsDropdown(
                                                                    false,
                                                                );
                                                                handleDisableCampaign(
                                                                    campaignDetails.id,
                                                                );
                                                            }}
                                                        >
                                                            <FaPause className="mr-2" />{" "}
                                                            Disable
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                className="flex items-center border border-gray-500 text-gray-700 px-3 py-1.5 text-sm rounded hover:bg-gray-200"
                                                onClick={() =>
                                                    setShowNoteBox(!showNoteBox)
                                                }
                                            >
                                                <span className="mr-1">
                                                    {showNoteBox ? (
                                                        <FaTimes />
                                                    ) : (
                                                        <FaPlus />
                                                    )}
                                                </span>
                                                Internal Notes
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {/* Actions Dropdown for Close Offering, Withdraw, Delete, Disable */}
                                            <div className="relative">
                                                <button
                                                    className="aqi-application-nc-button flex items-center px-4 py-2 text-sm rounded-md"
                                                    onClick={() =>
                                                        setShowActionsDropdown(
                                                            (prev) => !prev,
                                                        )
                                                    }
                                                >
                                                    <FaEdit className="mr-2" />{" "}
                                                    Actions
                                                </button>
                                                {showActionsDropdown && (
                                                    <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                                        {campaignDetails?.status ===
                                                            "approved" &&
                                                            campaignDetails?.north_capital_offering_id && (
                                                                <button
                                                                    className="w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition border-b border-gray-100 text-navy flex items-center"
                                                                    onClick={() => {
                                                                        setShowActionsDropdown(
                                                                            false,
                                                                        );
                                                                        handleAddDocumentsForOffering(
                                                                            true,
                                                                        );
                                                                    }}
                                                                >
                                                                    <FaEye className="mr-2" />
                                                                    Manage
                                                                    Documents
                                                                </button>
                                                            )}
                                                        {isDocuSignIntegrated &&
                                                            campaignDetails?.status ===
                                                            "approved" &&
                                                            campaignDetails?.north_capital_offering_id && (
                                                                <button
                                                                    className="w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition border-b border-gray-100 text-navy flex items-center"
                                                                    onClick={() => {
                                                                        setShowActionsDropdown(
                                                                            false,
                                                                        );
                                                                        handleManageSubscriptions();
                                                                    }}
                                                                >
                                                                    <FaEye className="mr-2" />
                                                                    Manage
                                                                    Subscriptions
                                                                </button>
                                                            )}
                                                        {campaignDetails?.status ===
                                                            "approved" &&
                                                            campaignDetails?.north_capital_offering_id && (
                                                                <button
                                                                    className="w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition border-b border-gray-100 text-navy flex items-center"
                                                                    onClick={() => {
                                                                        setShowActionsDropdown(
                                                                            false,
                                                                        );
                                                                        handleViewPurchaseHistory();
                                                                    }}
                                                                >
                                                                    <FaEye className="mr-2" />
                                                                    Investment
                                                                    History
                                                                </button>
                                                            )}
                                                        {campaignDetails?.status ===
                                                            "approved" &&
                                                            campaignDetails?.north_capital_offering_id && (
                                                                <button
                                                                    className="w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition border-b border-gray-100 text-navy flex items-center"
                                                                    onClick={() => {
                                                                        setShowActionsDropdown(
                                                                            false,
                                                                        );
                                                                        handleViewPurchaseHistory();
                                                                    }}
                                                                >
                                                                    <FaBan className="mr-2" />{" "}
                                                                    Withdraw
                                                                    Offering
                                                                </button>
                                                            )}
                                                        {/* Disable */}
                                                        {campaignDetails.status ===
                                                            "completed" && (
                                                            <button
                                                                className="w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition border-b border-gray-100 text-navy flex items-center"
                                                                onClick={() => {
                                                                    setShowActionsDropdown(
                                                                        false,
                                                                    );
                                                                    handleCompletedCampaignVisibility(
                                                                        campaignDetails,
                                                                    );
                                                                }}
                                                            >
                                                                {Number(
                                                                    campaignDetails.is_hidden_from_live_site,
                                                                ) === 1 ? (
                                                                    <FaEye className="mr-2" />
                                                                ) : (
                                                                    <FaEyeSlash className="mr-2" />
                                                                )}
                                                                {Number(
                                                                    campaignDetails.is_hidden_from_live_site,
                                                                ) === 1
                                                                    ? "Show on Live Site"
                                                                    : "Hide from Live Site"}
                                                            </button>
                                                        )}

                                                        <button
                                                            className="w-full text-left px-4 py-3 text-sm hover:bg-gray-100 transition text-gray-500 flex items-center"
                                                            onClick={() => {
                                                                setShowActionsDropdown(
                                                                    false,
                                                                );
                                                                handleDisableCampaign(
                                                                    campaignDetails.id,
                                                                );
                                                            }}
                                                        >
                                                            <FaPause className="mr-2" />{" "}
                                                            Disable Campaign
                                                        </button>

                                                        {/* Close Offering */}
                                                        {campaignDetails?.status ===
                                                            "approved" &&
                                                            campaignDetails?.north_capital_offering_id && (
                                                                <button
                                                                    className="w-full text-left px-4 py-3 text-sm hover:bg-red-50 transition border-b border-gray-100 text-red-600 flex items-center"
                                                                    onClick={() => {
                                                                        setShowActionsDropdown(
                                                                            false,
                                                                        );
                                                                        handleCloseOffering(
                                                                            campaignDetails.id,
                                                                        );
                                                                    }}
                                                                >
                                                                    <FaTimes className="mr-2" />{" "}
                                                                    Close
                                                                    Offering
                                                                </button>
                                                            )}

                                                        {/* Delete */}
                                                        {campaignDetails.status !==
                                                            "deleted" &&
                                                            campaignDetails.status !==
                                                                "cancelled" &&
                                                            campaignDetails.status !==
                                                                "withdrawn" &&
                                                            !hasDraftChanges() &&
                                                            campaignDetails.status !==
                                                                "onhold" &&
                                                            campaignDetails.status !==
                                                                "pending" &&
                                                            campaignDetails.is_deleted !==
                                                                1 && (
                                                                <button
                                                                    className="w-full text-left px-4 py-3 text-sm hover:bg-orange-50 transition border-b border-gray-100 text-orange-600 flex items-center"
                                                                    onClick={() => {
                                                                        setShowActionsDropdown(
                                                                            false,
                                                                        );
                                                                        handleWithdrawCampaign(
                                                                            campaignDetails.id,
                                                                        );
                                                                    }}
                                                                >
                                                                    <FaTrash className="mr-2" />{" "}
                                                                    Delete
                                                                    Campaign
                                                                </button>
                                                            )}
                                                    </div>
                                                )}
                                            </div>
                                            {/* Approve Withdrawal button - show only when entrepreneur has requested withdrawal */}
                                            {campaignDetails.withdrawal_requested ==
                                                1 && (
                                                <button
                                                    className="flex items-center border border-yellow-600 text-yellow-700 px-3 py-1.5 text-sm rounded hover:bg-yellow-100 hover:text-yellow-800"
                                                    onClick={() =>
                                                        handleApproveWithdrawal(
                                                            campaignDetails.id,
                                                        )
                                                    }
                                                    style={{ marginRight: 8 }}
                                                >
                                                    <FaUndoAlt className="mr-2" />{" "}
                                                    Approve Withdrawal
                                                </button>
                                            )}
                                            {/* Internal Notes button - always show */}
                                            <button
                                                className="flex items-center border border-gray-500 text-gray-700 px-3 py-1.5 text-sm rounded hover:bg-gray-200"
                                                onClick={() =>
                                                    setShowNoteBox(!showNoteBox)
                                                }
                                            >
                                                <span className="mr-1">
                                                    {showNoteBox ? (
                                                        <FaTimes />
                                                    ) : (
                                                        <FaPlus />
                                                    )}
                                                </span>
                                                Internal Notes
                                            </button>
                                            {/* Cancel On-Hold button - show only if status is onhold */}
                                            {campaignDetails.status ===
                                                "onhold" && (
                                                <button
                                                    className="flex items-center border border-yellow-600 text-yellow-600 px-4 py-2 text-sm rounded-md transition hover:bg-yellow-600 hover:text-white"
                                                    onClick={() =>
                                                        handleCancelOnHold(
                                                            campaignDetails.id,
                                                        )
                                                    }
                                                >
                                                    <FaUndoAlt className="mr-2" />{" "}
                                                    Cancel On-Hold
                                                </button>
                                            )}
                                            {/* Cancel Investments button - show only when campaign is withdrawn */}
                                            {campaignDetails.status ===
                                                "withdrawn" && (
                                                <button
                                                    className="flex items-center border border-red-500 text-red-600 px-3 py-1.5 text-sm rounded hover:bg-red-100 hover:text-red-700"
                                                    onClick={() =>
                                                        setShowCancelInvestmentsBox(
                                                            !showCancelInvestmentsBox,
                                                        )
                                                    }
                                                >
                                                    <FaBan className="mr-2" />{" "}
                                                    Cancel Investments
                                                </button>
                                            )}
                                            {/* Reject button - only show if not onhold and not withdrawn */}
                                            {campaignDetails.status !==
                                                "onhold" &&
                                                campaignDetails.status !==
                                                    "withdrawn" && (
                                                    <button
                                                        className="flex items-center border border-red-600 text-red-600 px-4 py-2 text-sm rounded-md transition hover:bg-red-600 hover:text-white"
                                                        onClick={() =>
                                                            handleCampaignApproval(
                                                                campaignDetails.id,
                                                                "reject",
                                                            )
                                                        }
                                                    >
                                                        <FaTimes className="mr-2" />{" "}
                                                        Reject
                                                    </button>
                                                )}
                                            {/* On-Hold button - show only when status is pending and not reviewing draft changes */}
                                            {!hasDraftChanges() &&
                                                campaignDetails.status ===
                                                    "pending" && (
                                                    <button
                                                        className="flex items-center border border-yellow-500 text-yellow-700 px-4 py-2 text-sm rounded-md transition hover:bg-yellow-500 hover:text-white"
                                                        onClick={() =>
                                                            setShowOnHoldNote(
                                                                !showOnHoldNote,
                                                            )
                                                        }
                                                    >
                                                        <span className="mr-2">
                                                            {showOnHoldNote ? (
                                                                <FaTimes />
                                                            ) : (
                                                                <FaPause />
                                                            )}
                                                        </span>
                                                        On-Hold
                                                    </button>
                                                )}
                                            {/* Approve button - show if: 1. Reviewing draft changes (regardless of current status), OR 2. Not onhold, not withdrawn, and not already approved */}
                                            {(hasDraftChanges() ||
                                                (campaignDetails.status !==
                                                    "onhold" &&
                                                    campaignDetails.status !==
                                                        "withdrawn" &&
                                                    campaignDetails.status !==
                                                        "approved")) && (
                                                <button
                                                    className="flex items-center border border-green-600 text-green-600 px-4 py-2 text-sm rounded-md transition hover:bg-green-600 hover:text-white"
                                                    onClick={() =>
                                                        handleCampaignApproval(
                                                            campaignDetails.id,
                                                            "approve",
                                                        )
                                                    }
                                                >
                                                    <FaCheck className="mr-2" />{" "}
                                                    {hasDraftChanges()
                                                        ? "Update"
                                                        : "Approve"}
                                                </button>
                                            )}
                                            {/* Assign North Capital IDs button - always visible when NC ID exists */}
                                            {campaignDetails?.north_capital_offering_id && (
                                                <button
                                                    className="aqi-application-nc-button flex items-center px-4 py-2 text-sm rounded-md"
                                                    onClick={() => {
                                                        if (!showNCForm) {
                                                            openNCForm();
                                                        } else {
                                                            setShowNCForm(
                                                                false,
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {showNCForm ? (
                                                        <FaTimes className="mr-2" />
                                                    ) : (
                                                        <FaLink className="mr-2" />
                                                    )}
                                                    {showNCForm
                                                        ? "Hide"
                                                        : "View"}{" "}
                                                    NC IDs
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {showOnHoldNote && (
                                <div className="mb-6">
                                    <div className="rounded-xl border border-yellow-200 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-sm p-4">
                                        <label
                                            htmlFor="onHoldNote"
                                            className="block text-yellow-700 font-semibold mb-2 text-base tracking-wide"
                                        >
                                            On-Hold Notes
                                        </label>
                                        <textarea
                                            id="onHoldNote"
                                            rows={4}
                                            placeholder="Enter internal note for putting this campaign on hold..."
                                            className="w-full border border-yellow-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200 bg-white resize-none shadow-inner"
                                            value={onHoldNote}
                                            onChange={(e) =>
                                                setOnHoldNote(e.target.value)
                                            }
                                            maxLength={1000}
                                        />
                                        <div className="flex justify-between items-center mt-2">
                                            <span className="text-xs text-gray-400">
                                                {onHoldNote.length}/1000
                                            </span>
                                            <button
                                                className="bg-yellow-500 text-white px-5 py-2 rounded-lg font-semibold shadow hover:bg-yellow-600 transition-colors duration-150 text-sm"
                                                onClick={() =>
                                                    handleCampaignApproval(
                                                        campaignDetails.id,
                                                        "on-hold",
                                                    )
                                                }
                                                disabled={!onHoldNote.trim()}
                                            >
                                                Submit Note &amp; Put On-Hold
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* On Hold Note Section - display whenever campaign is on hold, even if note is empty */}
                            {campaignDetails &&
                                campaignDetails.status === "onhold" && (
                                    <section className="mb-6">
                                        <div className="flex justify-between items-center mb-2">
                                            <h4 className="font-semibold text-yellow-700 text-lg border-b pb-1 mb-2 tracking-wide flex items-center gap-2">
                                                <span className="inline-block w-1.5 h-5 bg-yellow-500 rounded-sm mr-2"></span>
                                                On Hold Note
                                            </h4>
                                            {/* Debug info for the current on hold note */}
                                            {console.error(
                                                "Rendering on-hold note section. Current note:",
                                                displayedOnHoldNote,
                                            )}
                                            {console.error(
                                                "Raw on_hold_note from campaignDetails:",
                                                campaignDetails.on_hold_note,
                                            )}
                                        </div>
                                        <div className="relative bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-4 shadow-sm">
                                            <div className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-yellow-200 flex items-center justify-center text-yellow-800 font-bold text-lg shadow">
                                                    <FaPause className="text-sm" />
                                                </div>
                                                <div className="flex-1">
                                                    {/* Try to show the note from multiple possible sources */}
                                                    {displayedOnHoldNote ? (
                                                        <p className="text-gray-800 text-base leading-relaxed mb-1 whitespace-pre-line">
                                                            {
                                                                displayedOnHoldNote
                                                            }
                                                        </p>
                                                    ) : campaignDetails.on_hold_note ? (
                                                        <p className="text-gray-800 text-base leading-relaxed mb-1 whitespace-pre-line">
                                                            {
                                                                campaignDetails.on_hold_note
                                                            }
                                                        </p>
                                                    ) : (
                                                        <p className="text-gray-500 italic">
                                                            No on-hold note was
                                                            provided.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                )}

                            {/* Internal Notes Section - now matches AccountApproval */}
                            <section className="mb-6">
                                {showNoteBox && internalNotes.length > 0 && (
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className="font-semibold text-navy text-lg border-b pb-1 mb-2 tracking-wide flex items-center gap-2">
                                            <span className="inline-block w-1.5 h-5 bg-navy rounded-sm mr-2"></span>
                                            Internal Notes
                                        </h4>
                                    </div>
                                )}
                                {/*
                                {internalNotes.length > 0 && (
                                    <div className="mb-6">
                                        <div className="space-y-4">
                                            {internalNotes.map((note) => (
                                                <div
                                                    key={note.id}
                                                    className="relative bg-gradient-to-br from-[#eaf6ff] to-[#F5F7FA] border border-[#b6d6ee] rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200 group"
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#c2d7e2] flex items-center justify-center text-navy font-bold text-lg shadow group-hover:bg-navy group-hover:text-[#c2d7e2] transition-colors duration-200">
                                                            {note.username
                                                                ? note.username
                                                                      .charAt(0)
                                                                      .toUpperCase()
                                                                : "U"}
                                                        </div>
                                                        <div className="flex-1">
                                                            {editingNoteId ===
                                                            note.id ? (
                                                                <div className="space-y-2">
                                                                    <textarea
                                                                        rows="3"
                                                                        className="w-full border border-[#c2d7e2] rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy focus:border-navy transition-all duration-200 bg-white resize-none"
                                                                        value={
                                                                            editingNoteText
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            setEditingNoteText(
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }
                                                                        maxLength={
                                                                            1000
                                                                        }
                                                                    />
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-xs text-gray-400">
                                                                            {
                                                                                editingNoteText.length
                                                                            }
                                                                            /1000
                                                                        </span>
                                                                        <div className="flex gap-2">
                                                                            <button
                                                                                onClick={() =>
                                                                                    handleSaveEditedNote(
                                                                                        note.id,
                                                                                    )
                                                                                }
                                                                                className="bg-navy text-white px-3 py-1 rounded-lg font-semibold shadow hover:bg-navy-2 transition-colors duration-150 text-xs flex items-center gap-1"
                                                                            >
                                                                                <FaSave className="text-xs" />{" "}
                                                                                Save
                                                                            </button>
                                                                            <button
                                                                                onClick={
                                                                                    handleCancelEdit
                                                                                }
                                                                                className="bg-gray-500 text-white px-3 py-1 rounded-lg font-semibold shadow hover:bg-gray-600 transition-colors duration-150 text-xs"
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div className="flex justify-between items-start">
                                                                        <p className="text-gray-800 text-base leading-relaxed mb-1 whitespace-pre-line flex-1 pr-2">
                                                                            {
                                                                                note.note
                                                                            }
                                                                        </p>
                                                                        <button
                                                                            onClick={() =>
                                                                                handleEditNote(
                                                                                    note.id,
                                                                                    note.note,
                                                                                )
                                                                            }
                                                                            className="text-navy hover:text-navy-2 transition-colors duration-150 opacity-0 group-hover:opacity-100 flex-shrink-0"
                                                                            title="Edit note"
                                                                        >
                                                                            <FaEdit className="text-sm" />
                                                                        </button>
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                                                        <span className="inline-block w-1 h-1 bg-navy rounded-full"></span>
                                                                        <span className="font-medium text-navy">
                                                                            {note.username ||
                                                                                "Unknown"}
                                                                        </span>
                                                                        <span className="text-gray-400">
                                                                            on{" "}
                                                                            {new Date(
                                                                                note.created_on,
                                                                            ).toLocaleDateString()}
                                                                        </span>
                                                                        {note.updated_on &&
                                                                            note.updated_on !==
                                                                                note.created_on && (
                                                                                <span className="text-gray-400 italic">
                                                                                    (edited{" "}
                                                                                    {new Date(
                                                                                        note.updated_on,
                                                                                    ).toLocaleDateString()}

                                                                                    )
                                                                                </span>
                                                                            )}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {/* Add note box */}
                                {showNoteBox && (
                                    <div className="mb-6">
                                        <div className="rounded-xl border border-[#b6d6ee] bg-gradient-to-br from-[#F5F7FA] to-[#eaf6ff] shadow-sm p-4">
                                            <label
                                                htmlFor="internal-note-textarea"
                                                className="block text-navy font-semibold mb-2 text-base tracking-wide"
                                            >
                                                Add Internal Note
                                            </label>
                                            <textarea
                                                id="internal-note-textarea"
                                                rows="4"
                                                placeholder="Enter your internal note here..."
                                                className="w-full border border-[#c2d7e2] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy focus:border-navy transition-all duration-200 bg-white resize-none shadow-inner"
                                                value={internalNote}
                                                onChange={(e) =>
                                                    setInternalNote(
                                                        e.target.value,
                                                    )
                                                }
                                                maxLength={1000}
                                            />
                                            <div className="flex justify-between items-center mt-2">
                                                <span className="text-xs text-gray-400">
                                                    {internalNote.length}/1000
                                                </span>
                                                <button
                                                    onClick={
                                                        handleSubmitInternalNote
                                                    }
                                                    className="aqi-primary-action flex items-center gap-1 rounded-lg bg-navy px-5 py-2 text-sm font-semibold text-white shadow transition-colors duration-150 hover:bg-navy-2"
                                                >
                                                    Save Note
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </section>

                            {/* North Capital IDs Section - show when button is clicked */}
                            {showNCForm && (
                                <section className="mb-6">
                                    <div className="rounded-xl border border-navy bg-gradient-to-br from-[#eaf6ff] to-[#F5F7FA] shadow-sm p-4">
                                        <h4 className="font-semibold text-navy text-lg border-b pb-2 mb-4 tracking-wide flex items-center gap-2">
                                            <span className="inline-block w-1.5 h-5 bg-navy rounded-sm mr-2"></span>
                                            North Capital IDs
                                        </h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-navy mb-2">
                                                    Issuer ID
                                                </label>
                                                <input
                                                    type="text"
                                                    value={
                                                        campaignDetails.north_capital_issuer_id ||
                                                        ""
                                                    }
                                                    readOnly
                                                    className="w-full px-3 py-2 border border-navy rounded-lg text-sm bg-gray-100 text-navy font-semibold cursor-not-allowed"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-navy mb-2">
                                                    Offering ID
                                                </label>
                                                <input
                                                    type="text"
                                                    value={
                                                        campaignDetails.north_capital_offering_id ||
                                                        ""
                                                    }
                                                    readOnly
                                                    className="w-full px-3 py-2 border border-navy rounded-lg text-sm bg-gray-100 text-navy font-semibold cursor-not-allowed"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Campaign Info Section */}
                            <section className="mb-6">
                                <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                                    Basic Info
                                </h4>
                                {hasDraftChanges() ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                        {/* Before Column */}
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                Before
                                            </h4>
                                            <p>
                                                <strong>Campaign Name:</strong>{" "}
                                                {campaignDetails.campaign_name}
                                            </p>
                                            <p>
                                                <strong>Company Name:</strong>{" "}
                                                {campaignDetails.company_name}
                                            </p>
                                            <p>
                                                <strong>City:</strong>{" "}
                                                {campaignDetails.company_city}
                                            </p>
                                            <p>
                                                <strong>State:</strong>{" "}
                                                {campaignDetails.company_state}
                                            </p>
                                            <p>
                                                <strong>Status:</strong>{" "}
                                                {campaignDetails.status}
                                            </p>
                                            <p>
                                                <strong>Created At:</strong>{" "}
                                                {new Date(
                                                    campaignDetails.created_at,
                                                ).toLocaleString()}
                                            </p>
                                            {/* <p><strong>Updated At:</strong> {new Date(campaignDetails.updated_at).toLocaleString()}</p>
                      <p><strong>Submitted:</strong> {campaignDetails.submitted ? "Yes" : "No"}</p> */}
                                        </div>

                                        {/* After Column */}
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                After
                                            </h4>
                                            <p
                                                className={highlightChange(
                                                    "campaign_name",
                                                )}
                                            >
                                                <strong>Campaign Name:</strong>{" "}
                                                {campaignDraft?.campaign_name}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "company_name",
                                                )}
                                            >
                                                <strong>Company Name:</strong>{" "}
                                                {campaignDraft?.company_name}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "company_city",
                                                )}
                                            >
                                                <strong>City:</strong>{" "}
                                                {campaignDraft?.company_city}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "company_state",
                                                )}
                                            >
                                                <strong>State:</strong>{" "}
                                                {campaignDraft?.company_state}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "status",
                                                )}
                                            >
                                                <strong>Status:</strong>{" "}
                                                {campaignDraft?.status}
                                            </p>
                                            <p>
                                                <strong>Created At:</strong>{" "}
                                                {new Date(
                                                    campaignDetails.created_at,
                                                ).toLocaleString()}
                                            </p>
                                            {/* <p><strong>Updated At:</strong> {campaignDraft?.updated_at ? new Date(campaignDraft.updated_at).toLocaleString() : new Date(campaignDetails.updated_at).toLocaleString()}</p>
                      <p><strong>Submitted:</strong> {campaignDraft?.submitted !== undefined ? (campaignDraft.submitted ? "Yes" : "No") : (campaignDetails.submitted ? "Yes" : "No")}</p> */}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                        <p>
                                            <strong>Campaign Name:</strong>{" "}
                                            {campaignDetails.campaign_name}
                                        </p>
                                        <p>
                                            <strong>Company Name:</strong>{" "}
                                            {campaignDetails.company_name}
                                        </p>
                                        <p>
                                            <strong>City:</strong>{" "}
                                            {campaignDetails.company_city}
                                        </p>
                                        <p>
                                            <strong>State:</strong>{" "}
                                            {campaignDetails.company_state}
                                        </p>
                                        <p>
                                            <strong>Status:</strong>{" "}
                                            {campaignDetails.status}
                                        </p>
                                        <p>
                                            <strong>Created At:</strong>{" "}
                                            {new Date(
                                                campaignDetails.created_at,
                                            ).toLocaleString()}
                                        </p>
                                        <p>
                                            <strong>Updated At:</strong>{" "}
                                            {new Date(
                                                campaignDetails.updated_at,
                                            ).toLocaleString()}
                                        </p>
                                        <p>
                                            <strong>Submitted:</strong>{" "}
                                            {campaignDetails.submitted
                                                ? "Yes"
                                                : "No"}
                                        </p>
                                    </div>
                                )}
                            </section>

                            {/* Description */}
                            <section className="mb-6">
                                <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                                    Campaign Content
                                </h4>
                                {hasDraftChanges() ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                        {/* Before Column */}
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                Before
                                            </h4>
                                            <p>
                                                <strong>Description:</strong>{" "}
                                                {
                                                    campaignDetails.campaign_description
                                                }
                                            </p>
                                            <p>
                                                <strong>Overview Pitch:</strong>{" "}
                                                {campaignDetails.overview_pitch}
                                            </p>
                                            <p>
                                                <strong>Call to Action:</strong>{" "}
                                                {campaignDetails.call_to_action}
                                            </p>
                                            <p>
                                                <strong>Problem:</strong>{" "}
                                                {campaignDetails.pitch_problem}
                                            </p>
                                            <p>
                                                <strong>Solution:</strong>{" "}
                                                {campaignDetails.pitch_solution}
                                            </p>
                                            <p>
                                                <strong>Investors Info:</strong>{" "}
                                                {campaignDetails.investors_info}
                                            </p>
                                        </div>
                                        {/* After Column */}
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                After
                                            </h4>
                                            <p
                                                className={highlightChange(
                                                    "campaign_description",
                                                )}
                                            >
                                                <strong>Description:</strong>{" "}
                                                {
                                                    campaignDraft?.campaign_description
                                                }
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "overview_pitch",
                                                )}
                                            >
                                                <strong>Overview Pitch:</strong>{" "}
                                                {campaignDraft?.overview_pitch}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "call_to_action",
                                                )}
                                            >
                                                <strong>Call to Action:</strong>{" "}
                                                {campaignDraft?.call_to_action}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "pitch_problem",
                                                )}
                                            >
                                                <strong>Problem:</strong>{" "}
                                                {campaignDraft?.pitch_problem}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "pitch_solution",
                                                )}
                                            >
                                                <strong>Solution:</strong>{" "}
                                                {campaignDraft?.pitch_solution}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "investors_info",
                                                )}
                                            >
                                                <strong>Investors Info:</strong>{" "}
                                                {campaignDraft?.investors_info}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2 text-sm text-gray-700 leading-relaxed">
                                        <p>
                                            <strong>Description:</strong>{" "}
                                            {
                                                campaignDetails.campaign_description
                                            }
                                        </p>
                                        <p>
                                            <strong>Overview Pitch:</strong>{" "}
                                            {campaignDetails.overview_pitch}
                                        </p>
                                        <p>
                                            <strong>Call to Action:</strong>{" "}
                                            {campaignDetails.call_to_action}
                                        </p>
                                        <p>
                                            <strong>Problem:</strong>{" "}
                                            {campaignDetails.pitch_problem}
                                        </p>
                                        <p>
                                            <strong>Solution:</strong>{" "}
                                            {campaignDetails.pitch_solution}
                                        </p>
                                        <p>
                                            <strong>Investors Info:</strong>{" "}
                                            {campaignDetails.investors_info}
                                        </p>
                                    </div>
                                )}
                            </section>
                            {/* Social Media Feed */}
                            <section className="mb-6">
                                <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                                    Social Media Feed
                                </h4>
                                <div className="space-y-2 text-sm text-gray-700 leading-relaxed">
                                    {(campaignDraft?.facebook_link ||
                                        campaignDetails?.facebook_link) && (
                                        <p>
                                            <strong>Facebook:</strong>{" "}
                                            <a
                                                href={
                                                    campaignDraft?.facebook_link ||
                                                    campaignDetails?.facebook_link
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {campaignDraft?.facebook_link ||
                                                    campaignDetails?.facebook_link}
                                            </a>
                                        </p>
                                    )}
                                    {(campaignDraft?.linkedin_link ||
                                        campaignDetails?.linkedin_link) && (
                                        <p>
                                            <strong>LinkedIn:</strong>{" "}
                                            <a
                                                href={
                                                    campaignDraft?.linkedin_link ||
                                                    campaignDetails?.linkedin_link
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {campaignDraft?.linkedin_link ||
                                                    campaignDetails?.linkedin_link}
                                            </a>
                                        </p>
                                    )}
                                    {(campaignDraft?.twitter_link ||
                                        campaignDetails?.twitter_link) && (
                                        <p>
                                            <strong>Twitter:</strong>{" "}
                                            <a
                                                href={
                                                    campaignDraft?.twitter_link ||
                                                    campaignDetails?.twitter_link
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {campaignDraft?.twitter_link ||
                                                    campaignDetails?.twitter_link}
                                            </a>
                                        </p>
                                    )}
                                    {(campaignDraft?.tiktok_link ||
                                        campaignDetails?.tiktok_link) && (
                                        <p>
                                            <strong>TikTok:</strong>{" "}
                                            <a
                                                href={
                                                    campaignDraft?.tiktok_link ||
                                                    campaignDetails?.tiktok_link
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {campaignDraft?.tiktok_link ||
                                                    campaignDetails?.tiktok_link}
                                            </a>
                                        </p>
                                    )}
                                    {(campaignDraft?.youtube_link ||
                                        campaignDetails?.youtube_link) && (
                                        <p>
                                            <strong>YouTube:</strong>{" "}
                                            <a
                                                href={
                                                    campaignDraft?.youtube_link ||
                                                    campaignDetails?.youtube_link
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {campaignDraft?.youtube_link ||
                                                    campaignDetails?.youtube_link}
                                            </a>
                                        </p>
                                    )}
                                    {(campaignDraft?.instagram_link ||
                                        campaignDetails?.instagram_link) && (
                                        <p>
                                            <strong>Instagram:</strong>{" "}
                                            <a
                                                href={
                                                    campaignDraft?.instagram_link ||
                                                    campaignDetails?.instagram_link
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {campaignDraft?.instagram_link ||
                                                    campaignDetails?.instagram_link}
                                            </a>
                                        </p>
                                    )}
                                </div>
                            </section>
                            {/* Financial Info */}
                            <section className="mb-6">
                                <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                                    Investment Details
                                </h4>
                                {hasDraftChanges() ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                        {/* Before Column */}
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                Before
                                            </h4>
                                            <p>
                                                <strong>Goal Hit:</strong>{" "}
                                                {campaignDetails.fundRaisingGoal
                                                    ? `$${Number(
                                                          campaignDetails.fundRaisingGoal,
                                                      ).toLocaleString()}`
                                                    : "N/A"}
                                            </p>
                                            <p>
                                                <strong>Min Investment:</strong>{" "}
                                                {campaignDetails.investment_min ||
                                                campaignDetails.investment_minimum
                                                    ? `$${Number(campaignDetails.investment_min || campaignDetails.investment_minimum).toLocaleString()}`
                                                    : "N/A"}
                                            </p>
                                            <p>
                                                <strong>Minimum Target:</strong>{" "}
                                                {(campaignDetails.minimumRaised !==
                                                    undefined &&
                                                    campaignDetails.minimumRaised !==
                                                        null) ||
                                                (campaignDetails.minimum_raised !==
                                                    undefined &&
                                                    campaignDetails.minimum_raised !==
                                                        null)
                                                    ? `$${Number(campaignDetails.minimumRaised ?? campaignDetails.minimum_raised).toLocaleString()}`
                                                    : "N/A"}
                                            </p>
                                            <p>
                                                <strong>Max Amount:</strong>{" "}
                                                {campaignDetails.max_amount
                                                    ? `$${Number(campaignDetails.max_amount).toLocaleString()}`
                                                    : "N/A"}
                                            </p>
                                            <p>
                                                <strong>Unit Price:</strong>{" "}
                                                {campaignDetails.unit_price
                                                    ? `$${Number(campaignDetails.unit_price).toLocaleString()}`
                                                    : "N/A"}
                                            </p>
                                            <p>
                                                <strong>
                                                    Campaign Start Date:
                                                </strong>{" "}
                                                {campaignDetails.campaign_start_date
                                                    ? new Date(
                                                          campaignDetails.campaign_start_date,
                                                      ).toLocaleDateString()
                                                    : "N/A"}
                                            </p>
                                            <p>
                                                <strong>
                                                    Campaign End Date:
                                                </strong>{" "}
                                                {campaignDetails.end_date_formatted ||
                                                    "N/A"}
                                            </p>
                                        </div>
                                        {/* After Column */}
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                After
                                            </h4>
                                            <p
                                                className={highlightChange(
                                                    "fundRaisingGoal",
                                                )}
                                            >
                                                <strong>Goal Hit:</strong>{" "}
                                                {campaignDraft?.fundRaisingGoal
                                                    ? `$${Number(
                                                          campaignDraft.fundRaisingGoal,
                                                      ).toLocaleString()}`
                                                    : "N/A"}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "investment_minimum",
                                                )}
                                            >
                                                <strong>Min Investment:</strong>{" "}
                                                {campaignDraft?.investment_minimum ||
                                                campaignDraft?.investment_min
                                                    ? `$${Number(campaignDraft.investment_minimum || campaignDraft.investment_min).toLocaleString()}`
                                                    : "N/A"}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "minimum_raised",
                                                )}
                                            >
                                                <strong>Minimum Target:</strong>{" "}
                                                {(campaignDraft?.minimumRaised !==
                                                    undefined &&
                                                    campaignDraft?.minimumRaised !==
                                                        null) ||
                                                (campaignDraft?.minimum_raised !==
                                                    undefined &&
                                                    campaignDraft?.minimum_raised !==
                                                        null)
                                                    ? `$${Number(campaignDraft?.minimumRaised ?? campaignDraft?.minimum_raised).toLocaleString()}`
                                                    : "N/A"}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "max_amount",
                                                )}
                                            >
                                                <strong>Max Amount:</strong>{" "}
                                                {campaignDraft?.max_amount
                                                    ? `$${Number(campaignDraft.max_amount).toLocaleString()}`
                                                    : "N/A"}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "unit_price",
                                                )}
                                            >
                                                <strong>Unit Price:</strong>{" "}
                                                {campaignDraft?.unit_price
                                                    ? `$${Number(campaignDraft.unit_price).toLocaleString()}`
                                                    : "N/A"}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "campaign_start_date",
                                                )}
                                            >
                                                <strong>
                                                    Campaign Start Date:
                                                </strong>{" "}
                                                {campaignDraft?.campaign_start_date
                                                    ? new Date(
                                                          campaignDraft.campaign_start_date,
                                                      ).toLocaleDateString()
                                                    : "N/A"}
                                            </p>
                                            <p
                                                className={highlightChange(
                                                    "end_date",
                                                )}
                                            >
                                                <strong>
                                                    Campaign End Date:
                                                </strong>{" "}
                                                {campaignDraft?.end_date_formatted ||
                                                    "N/A"}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                        {/* Goal Hit */}
                                        <p className="flex items-center gap-2 flex-wrap">
                                            <strong>Goal Hit:</strong>{" "}
                                            {editingInvestmentField ===
                                            "fundRaisingGoal" ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        className="border rounded px-2 py-0.5 text-sm w-32"
                                                        value={
                                                            editingInvestmentValue
                                                        }
                                                        onChange={(e) =>
                                                            setEditingInvestmentValue(
                                                                e.target.value,
                                                            )
                                                        }
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() =>
                                                            handleSaveInvestmentField(
                                                                "fundRaisingGoal",
                                                            )
                                                        }
                                                        className="text-green-600 hover:text-green-800"
                                                        title="Save"
                                                    >
                                                        <FaCheck size={12} />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            setEditingInvestmentField(
                                                                null,
                                                            )
                                                        }
                                                        className="text-red-500 hover:text-red-700"
                                                        title="Cancel"
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {campaignDetails.fundRaisingGoal
                                                        ? `$${Number(campaignDetails.fundRaisingGoal).toLocaleString()}`
                                                        : "N/A"}
                                                    <button
                                                        onClick={() => {
                                                            setEditingInvestmentField(
                                                                "fundRaisingGoal",
                                                            );
                                                            setEditingInvestmentValue(
                                                                campaignDetails.fundRaisingGoal ||
                                                                    "",
                                                            );
                                                        }}
                                                        className="text-gray-400 hover:text-blue-600 ml-1"
                                                        title="Edit"
                                                    >
                                                        <FaPencilAlt
                                                            size={11}
                                                        />
                                                    </button>
                                                </>
                                            )}
                                        </p>
                                        {/* Minimum Investment */}
                                        <p className="flex items-center gap-2 flex-wrap">
                                            <strong>Minimum Investment:</strong>{" "}
                                            {editingInvestmentField ===
                                            "investment_min" ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        className="border rounded px-2 py-0.5 text-sm w-32"
                                                        value={
                                                            editingInvestmentValue
                                                        }
                                                        onChange={(e) =>
                                                            setEditingInvestmentValue(
                                                                e.target.value,
                                                            )
                                                        }
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() =>
                                                            handleSaveInvestmentField(
                                                                "investment_min",
                                                            )
                                                        }
                                                        className="text-green-600 hover:text-green-800"
                                                        title="Save"
                                                    >
                                                        <FaCheck size={12} />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            setEditingInvestmentField(
                                                                null,
                                                            )
                                                        }
                                                        className="text-red-500 hover:text-red-700"
                                                        title="Cancel"
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {campaignDetails.investment_minimum ||
                                                    campaignDetails.investment_min
                                                        ? `$${Number(campaignDetails.investment_minimum || campaignDetails.investment_min).toLocaleString()}`
                                                        : "N/A"}
                                                    <button
                                                        onClick={() => {
                                                            setEditingInvestmentField(
                                                                "investment_min",
                                                            );
                                                            setEditingInvestmentValue(
                                                                campaignDetails.investment_minimum ||
                                                                    campaignDetails.investment_min ||
                                                                    "",
                                                            );
                                                        }}
                                                        className="text-gray-400 hover:text-blue-600 ml-1"
                                                        title="Edit"
                                                    >
                                                        <FaPencilAlt
                                                            size={11}
                                                        />
                                                    </button>
                                                </>
                                            )}
                                        </p>
                                        {/* Minimum Target */}
                                        <p className="flex items-center gap-2 flex-wrap">
                                            <strong>Minimum Target:</strong>{" "}
                                            {editingInvestmentField ===
                                            "minimum_raised" ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        className="border rounded px-2 py-0.5 text-sm w-32"
                                                        value={
                                                            editingInvestmentValue
                                                        }
                                                        onChange={(e) =>
                                                            setEditingInvestmentValue(
                                                                e.target.value,
                                                            )
                                                        }
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() =>
                                                            handleSaveInvestmentField(
                                                                "minimum_raised",
                                                            )
                                                        }
                                                        className="text-green-600 hover:text-green-800"
                                                        title="Save"
                                                    >
                                                        <FaCheck size={12} />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            setEditingInvestmentField(
                                                                null,
                                                            )
                                                        }
                                                        className="text-red-500 hover:text-red-700"
                                                        title="Cancel"
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {(campaignDetails.minimumRaised !==
                                                        undefined &&
                                                        campaignDetails.minimumRaised !==
                                                            null) ||
                                                    (campaignDetails.minimum_raised !==
                                                        undefined &&
                                                        campaignDetails.minimum_raised !==
                                                            null)
                                                        ? `$${Number(campaignDetails.minimumRaised ?? campaignDetails.minimum_raised).toLocaleString()}`
                                                        : "N/A"}
                                                    <button
                                                        onClick={() => {
                                                            setEditingInvestmentField(
                                                                "minimum_raised",
                                                            );
                                                            setEditingInvestmentValue(
                                                                campaignDetails.minimumRaised ??
                                                                    campaignDetails.minimum_raised ??
                                                                    "",
                                                            );
                                                        }}
                                                        className="text-gray-400 hover:text-blue-600 ml-1"
                                                        title="Edit"
                                                    >
                                                        <FaPencilAlt
                                                            size={11}
                                                        />
                                                    </button>
                                                </>
                                            )}
                                        </p>
                                        {/* Max Amount */}
                                        <p className="flex items-center gap-2 flex-wrap">
                                            <strong>Max Amount:</strong>{" "}
                                            {editingInvestmentField ===
                                            "max_amount" ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        className="border rounded px-2 py-0.5 text-sm w-32"
                                                        value={
                                                            editingInvestmentValue
                                                        }
                                                        onChange={(e) =>
                                                            setEditingInvestmentValue(
                                                                e.target.value,
                                                            )
                                                        }
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() =>
                                                            handleSaveInvestmentField(
                                                                "max_amount",
                                                            )
                                                        }
                                                        className="text-green-600 hover:text-green-800"
                                                        title="Save"
                                                    >
                                                        <FaCheck size={12} />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            setEditingInvestmentField(
                                                                null,
                                                            )
                                                        }
                                                        className="text-red-500 hover:text-red-700"
                                                        title="Cancel"
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {campaignDetails.max_amount
                                                        ? `$${Number(campaignDetails.max_amount).toLocaleString()}`
                                                        : "N/A"}
                                                    <button
                                                        onClick={() => {
                                                            setEditingInvestmentField(
                                                                "max_amount",
                                                            );
                                                            setEditingInvestmentValue(
                                                                campaignDetails.max_amount ||
                                                                    "",
                                                            );
                                                        }}
                                                        className="text-gray-400 hover:text-blue-600 ml-1"
                                                        title="Edit"
                                                    >
                                                        <FaPencilAlt
                                                            size={11}
                                                        />
                                                    </button>
                                                </>
                                            )}
                                        </p>
                                        {/* Unit Price */}
                                        <p className="flex items-center gap-2 flex-wrap">
                                            <strong>Unit Price:</strong>{" "}
                                            {editingInvestmentField ===
                                            "unit_price" ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        className="border rounded px-2 py-0.5 text-sm w-32"
                                                        value={
                                                            editingInvestmentValue
                                                        }
                                                        onChange={(e) =>
                                                            setEditingInvestmentValue(
                                                                e.target.value,
                                                            )
                                                        }
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() =>
                                                            handleSaveInvestmentField(
                                                                "unit_price",
                                                            )
                                                        }
                                                        className="text-green-600 hover:text-green-800"
                                                        title="Save"
                                                    >
                                                        <FaCheck size={12} />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            setEditingInvestmentField(
                                                                null,
                                                            )
                                                        }
                                                        className="text-red-500 hover:text-red-700"
                                                        title="Cancel"
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {campaignDetails.unit_price
                                                        ? `$${Number(campaignDetails.unit_price).toLocaleString()}`
                                                        : "N/A"}
                                                    <button
                                                        onClick={() => {
                                                            setEditingInvestmentField(
                                                                "unit_price",
                                                            );
                                                            setEditingInvestmentValue(
                                                                campaignDetails.unit_price ||
                                                                    "",
                                                            );
                                                        }}
                                                        className="text-gray-400 hover:text-blue-600 ml-1"
                                                        title="Edit"
                                                    >
                                                        <FaPencilAlt
                                                            size={11}
                                                        />
                                                    </button>
                                                </>
                                            )}
                                        </p>
                                        {/* Campaign Start Date */}
                                        <p className="flex items-center gap-2 flex-wrap">
                                            <strong>
                                                Campaign Start Date:
                                            </strong>{" "}
                                            {editingInvestmentField ===
                                            "campaign_start_date" ? (
                                                <>
                                                    <input
                                                        type="date"
                                                        className="border rounded px-2 py-0.5 text-sm"
                                                        value={
                                                            editingInvestmentValue
                                                        }
                                                        onChange={(e) =>
                                                            setEditingInvestmentValue(
                                                                e.target.value,
                                                            )
                                                        }
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() =>
                                                            handleSaveInvestmentField(
                                                                "campaign_start_date",
                                                            )
                                                        }
                                                        className="text-green-600 hover:text-green-800"
                                                        title="Save"
                                                    >
                                                        <FaCheck size={12} />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            setEditingInvestmentField(
                                                                null,
                                                            )
                                                        }
                                                        className="text-red-500 hover:text-red-700"
                                                        title="Cancel"
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {campaignDetails.campaign_start_date
                                                        ? new Date(
                                                              campaignDetails.campaign_start_date,
                                                          ).toLocaleDateString()
                                                        : "N/A"}
                                                    <button
                                                        onClick={() => {
                                                            setEditingInvestmentField(
                                                                "campaign_start_date",
                                                            );
                                                            setEditingInvestmentValue(
                                                                campaignDetails.campaign_start_date
                                                                    ? campaignDetails.campaign_start_date.split(
                                                                          "T",
                                                                      )[0]
                                                                    : "",
                                                            );
                                                        }}
                                                        className="text-gray-400 hover:text-blue-600 ml-1"
                                                        title="Edit"
                                                    >
                                                        <FaPencilAlt
                                                            size={11}
                                                        />
                                                    </button>
                                                </>
                                            )}
                                        </p>
                                        {/* Previous Funds Raised */}
                                        <p className="flex items-center gap-2 flex-wrap">
                                            <strong>
                                                Previous Funds Raised:
                                            </strong>{" "}
                                            {editingInvestmentField ===
                                            "previous_funds_raised" ? (
                                                <>
                                                    <input
                                                        type="text"
                                                        className="border rounded px-2 py-0.5 text-sm w-32"
                                                        value={
                                                            editingInvestmentValue
                                                        }
                                                        onChange={(e) =>
                                                            setEditingInvestmentValue(
                                                                e.target.value,
                                                            )
                                                        }
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() =>
                                                            handleSaveInvestmentField(
                                                                "previous_funds_raised",
                                                            )
                                                        }
                                                        className="text-green-600 hover:text-green-800"
                                                        title="Save"
                                                    >
                                                        <FaCheck size={12} />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            setEditingInvestmentField(
                                                                null,
                                                            )
                                                        }
                                                        className="text-red-500 hover:text-red-700"
                                                        title="Cancel"
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {campaignDetails.previous_funds_raised
                                                        ? `$${campaignDetails.previous_funds_raised}`
                                                        : "N/A"}
                                                    <button
                                                        onClick={() => {
                                                            setEditingInvestmentField(
                                                                "previous_funds_raised",
                                                            );
                                                            setEditingInvestmentValue(
                                                                campaignDetails.previous_funds_raised ||
                                                                    "",
                                                            );
                                                        }}
                                                        className="text-gray-400 hover:text-blue-600 ml-1"
                                                        title="Edit"
                                                    >
                                                        <FaPencilAlt
                                                            size={11}
                                                        />
                                                    </button>
                                                </>
                                            )}
                                        </p>
                                        {/* Funding History */}
                                        <p className="flex items-center gap-2 flex-wrap">
                                            <strong>Funding History:</strong>{" "}
                                            {editingInvestmentField ===
                                            "funding_history" ? (
                                                <>
                                                    <input
                                                        type="text"
                                                        className="border rounded px-2 py-0.5 text-sm w-48"
                                                        value={
                                                            editingInvestmentValue
                                                        }
                                                        onChange={(e) =>
                                                            setEditingInvestmentValue(
                                                                e.target.value,
                                                            )
                                                        }
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() =>
                                                            handleSaveInvestmentField(
                                                                "funding_history",
                                                            )
                                                        }
                                                        className="text-green-600 hover:text-green-800"
                                                        title="Save"
                                                    >
                                                        <FaCheck size={12} />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            setEditingInvestmentField(
                                                                null,
                                                            )
                                                        }
                                                        className="text-red-500 hover:text-red-700"
                                                        title="Cancel"
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {campaignDetails.funding_history
                                                        ? typeof campaignDetails.funding_history ===
                                                          "string"
                                                            ? campaignDetails.funding_history
                                                            : JSON.stringify(
                                                                  campaignDetails.funding_history,
                                                              )
                                                        : "N/A"}
                                                    <button
                                                        onClick={() => {
                                                            setEditingInvestmentField(
                                                                "funding_history",
                                                            );
                                                            setEditingInvestmentValue(
                                                                campaignDetails.funding_history
                                                                    ? typeof campaignDetails.funding_history ===
                                                                      "string"
                                                                        ? campaignDetails.funding_history
                                                                        : JSON.stringify(
                                                                              campaignDetails.funding_history,
                                                                          )
                                                                    : "",
                                                            );
                                                        }}
                                                        className="text-gray-400 hover:text-blue-600 ml-1"
                                                        title="Edit"
                                                    >
                                                        <FaPencilAlt
                                                            size={11}
                                                        />
                                                    </button>
                                                </>
                                            )}
                                        </p>
                                        {/* Campaign End Date */}
                                        <p className="flex items-center gap-2 flex-wrap">
                                            <strong>Campaign End Date:</strong>{" "}
                                            {editingInvestmentField ===
                                            "end_date" ? (
                                                <>
                                                    <input
                                                        type="date"
                                                        className="border rounded px-2 py-0.5 text-sm"
                                                        value={
                                                            editingInvestmentValue
                                                        }
                                                        onChange={(e) =>
                                                            setEditingInvestmentValue(
                                                                e.target.value,
                                                            )
                                                        }
                                                        autoFocus
                                                    />
                                                    <button
                                                        onClick={() =>
                                                            handleSaveInvestmentField(
                                                                "end_date",
                                                            )
                                                        }
                                                        className="text-green-600 hover:text-green-800"
                                                        title="Save"
                                                    >
                                                        <FaCheck size={12} />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            setEditingInvestmentField(
                                                                null,
                                                            )
                                                        }
                                                        className="text-red-500 hover:text-red-700"
                                                        title="Cancel"
                                                    >
                                                        <FaTimes size={12} />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {campaignDetails.end_date_formatted ||
                                                        "N/A"}
                                                    <button
                                                        onClick={() => {
                                                            setEditingInvestmentField(
                                                                "end_date",
                                                            );
                                                            setEditingInvestmentValue(
                                                                campaignDetails.end_date
                                                                    ? campaignDetails.end_date.split(
                                                                          "T",
                                                                      )[0]
                                                                    : "",
                                                            );
                                                        }}
                                                        className="text-gray-400 hover:text-blue-600 ml-1"
                                                        title="Edit"
                                                    >
                                                        <FaPencilAlt
                                                            size={11}
                                                        />
                                                    </button>
                                                </>
                                            )}
                                        </p>
                                    </div>
                                )}
                            </section>

                            {/* Social Links */}
                            {campaignDetails.socialLinks && (
                                <section className="mb-6">
                                    <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                                        Social Links
                                    </h4>
                                    {hasDraftChanges() ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                            {/* Before Column */}
                                            <div>
                                                <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                    Before
                                                </h4>
                                                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                                                    {Object.entries(
                                                        campaignDetails.socialLinks,
                                                    ).map(([key, value]) =>
                                                        value ? (
                                                            <li key={key}>
                                                                <strong>
                                                                    {key}:
                                                                </strong>{" "}
                                                                <a
                                                                    href={value}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-blue-600 underline"
                                                                >
                                                                    {value}
                                                                </a>
                                                            </li>
                                                        ) : null,
                                                    )}
                                                </ul>
                                            </div>
                                            {/* After Column */}
                                            <div>
                                                <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                    After{" "}
                                                </h4>
                                                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                                                    {campaignDraft?.socialLinks &&
                                                        Object.entries(
                                                            campaignDraft.socialLinks,
                                                        ).map(([key, value]) =>
                                                            value ? (
                                                                <li
                                                                    key={key}
                                                                    className={highlightChange(
                                                                        `socialLinks.${key}`,
                                                                    )}
                                                                >
                                                                    <strong>
                                                                        {key}:
                                                                    </strong>{" "}
                                                                    <a
                                                                        href={
                                                                            value
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-blue-600 underline"
                                                                    >
                                                                        {value}
                                                                    </a>
                                                                </li>
                                                            ) : null,
                                                        )}
                                                </ul>
                                            </div>
                                        </div>
                                    ) : (
                                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                                            {Object.entries(
                                                campaignDetails.socialLinks,
                                            ).map(([key, value]) =>
                                                value ? (
                                                    <li key={key}>
                                                        <strong>{key}:</strong>{" "}
                                                        <a
                                                            href={value}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 underline"
                                                        >
                                                            {value}
                                                        </a>
                                                    </li>
                                                ) : null,
                                            )}
                                        </ul>
                                    )}
                                </section>
                            )}
                            {/* Pitch Slides */}
                            {campaignDetails.pitch_slides &&
                                campaignDetails.pitch_slides.length > 0 && (
                                    <section className="mb-6">
                                        <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                                            Pitch Slides
                                        </h4>
                                        {hasDraftChanges() &&
                                        campaignDraft?.pitch_slides &&
                                        campaignDraft.pitch_slides.length >
                                            0 ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                                {/* Before Column */}
                                                <div>
                                                    <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                        Before
                                                    </h4>
                                                    <div className="grid grid-cols-1 gap-4">
                                                        {campaignDetails.pitch_slides.map(
                                                            (slide, idx) => {
                                                                const isImage =
                                                                    /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                                                        slide.file,
                                                                    );
                                                                const isPDF =
                                                                    /\.pdf$/i.test(
                                                                        slide.file,
                                                                    );
                                                                return (
                                                                    <div
                                                                        key={
                                                                            idx
                                                                        }
                                                                        className="p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm flex flex-col items-center min-h-[260px]"
                                                                    >
                                                                        {isImage ? (
                                                                            <img
                                                                                src={normalizeAssetUrl(
                                                                                    slide.file,
                                                                                )}
                                                                                alt={`Slide ${
                                                                                    idx +
                                                                                    1
                                                                                }`}
                                                                                className="h-40 w-40 object-contain mb-2 rounded border"
                                                                                onError={(
                                                                                    e,
                                                                                ) => {
                                                                                    e.target.onerror =
                                                                                        null;
                                                                                    e.target.style.display =
                                                                                        "none";
                                                                                }}
                                                                            />
                                                                        ) : isPDF ? (
                                                                            <iframe
                                                                                src={normalizeAssetUrl(
                                                                                    slide.file,
                                                                                )}
                                                                                title={`PDF Slide ${
                                                                                    idx +
                                                                                    1
                                                                                }`}
                                                                                width="100%"
                                                                                height="180"
                                                                                className="border rounded mb-2"
                                                                            />
                                                                        ) : (
                                                                            <div className="text-red-600 font-semibold mb-2">
                                                                                Unsupported
                                                                                file
                                                                                type
                                                                            </div>
                                                                        )}
                                                                        <p className="text-sm text-gray-700 mt-2 text-center">
                                                                            {
                                                                                slide.overview
                                                                            }
                                                                        </p>
                                                                    </div>
                                                                );
                                                            },
                                                        )}
                                                    </div>
                                                </div>
                                                {/* After Column */}
                                                <div>
                                                    <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                        After
                                                    </h4>
                                                    <div className="grid grid-cols-1 gap-4">
                                                        {campaignDraft.pitch_slides.map(
                                                            (slide, idx) => {
                                                                const mediaType =
                                                                    getMediaType(
                                                                        slide.file,
                                                                    );
                                                                const highlightClass =
                                                                    highlightMediaChange(
                                                                        "pitch_slides",
                                                                        idx,
                                                                        idx,
                                                                    );
                                                                return (
                                                                    <div
                                                                        key={
                                                                            idx
                                                                        }
                                                                        className={`p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm flex flex-col items-center min-h-[260px] ${highlightClass}`}
                                                                    >
                                                                        {mediaType ===
                                                                        "image" ? (
                                                                            <img
                                                                                src={normalizeAssetUrl(
                                                                                    slide.file,
                                                                                )}
                                                                                alt={`Slide ${
                                                                                    idx +
                                                                                    1
                                                                                }`}
                                                                                className="h-40 w-40 object-contain mb-2 rounded border"
                                                                                onError={(
                                                                                    e,
                                                                                ) => {
                                                                                    e.target.onerror =
                                                                                        null;
                                                                                    e.target.style.display =
                                                                                        "none";
                                                                                }}
                                                                            />
                                                                        ) : mediaType ===
                                                                          "pdf" ? (
                                                                            <iframe
                                                                                src={normalizeAssetUrl(
                                                                                    slide.file,
                                                                                )}
                                                                                title={`PDF Slide ${
                                                                                    idx +
                                                                                    1
                                                                                }`}
                                                                                width="100%"
                                                                                height="180"
                                                                                className="border rounded mb-2"
                                                                            />
                                                                        ) : mediaType ===
                                                                          "video" ? (
                                                                            <video
                                                                                src={normalizeAssetUrl(
                                                                                    slide.file,
                                                                                )}
                                                                                controls
                                                                                className="h-40 w-40 object-contain mb-2 rounded border"
                                                                                onError={(
                                                                                    e,
                                                                                ) => {
                                                                                    e.target.onerror =
                                                                                        null;
                                                                                    e.target.style.display =
                                                                                        "none";
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <div className="text-red-600 font-semibold mb-2">
                                                                                Unsupported
                                                                                file
                                                                                type
                                                                            </div>
                                                                        )}
                                                                        <p className="text-sm text-gray-700 mt-2 text-center">
                                                                            {
                                                                                slide.overview
                                                                            }
                                                                        </p>
                                                                    </div>
                                                                );
                                                            },
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {campaignDetails.pitch_slides.map(
                                                    (slide, idx) => {
                                                        const mediaType =
                                                            getMediaType(
                                                                slide.file,
                                                            );
                                                        return (
                                                            <div
                                                                key={idx}
                                                                className="p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm flex flex-col items-center min-h-[260px]"
                                                            >
                                                                {mediaType ===
                                                                "image" ? (
                                                                    <img
                                                                        src={normalizeAssetUrl(
                                                                            slide.file,
                                                                        )}
                                                                        alt={`Slide ${
                                                                            idx +
                                                                            1
                                                                        }`}
                                                                        className="h-40 w-40 object-contain mb-2 rounded border"
                                                                        onError={(
                                                                            e,
                                                                        ) => {
                                                                            e.target.onerror =
                                                                                null;
                                                                            e.target.style.display =
                                                                                "none";
                                                                        }}
                                                                    />
                                                                ) : mediaType ===
                                                                  "pdf" ? (
                                                                    <iframe
                                                                        src={normalizeAssetUrl(
                                                                            slide.file,
                                                                        )}
                                                                        title={`PDF Slide ${
                                                                            idx +
                                                                            1
                                                                        }`}
                                                                        width="100%"
                                                                        height="180"
                                                                        className="border rounded mb-2"
                                                                    />
                                                                ) : mediaType ===
                                                                  "video" ? (
                                                                    <video
                                                                        src={normalizeAssetUrl(
                                                                            slide.file,
                                                                        )}
                                                                        controls
                                                                        className="h-40 w-40 object-contain mb-2 rounded border"
                                                                        onError={(
                                                                            e,
                                                                        ) => {
                                                                            e.target.onerror =
                                                                                null;
                                                                            e.target.style.display =
                                                                                "none";
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="text-red-600 font-semibold mb-2">
                                                                        Unsupported
                                                                        file
                                                                        type
                                                                    </div>
                                                                )}
                                                                <p className="text-sm text-gray-700 mt-2 text-center">
                                                                    {
                                                                        slide.overview
                                                                    }
                                                                </p>
                                                            </div>
                                                        );
                                                    },
                                                )}
                                            </div>
                                        )}
                                    </section>
                                )}

                            {/* Founders / Team */}
                            {campaignDetails.founders &&
                                campaignDetails.founders.length > 0 && (
                                    <section className="mb-6">
                                        <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                                            Founders
                                        </h4>
                                        {hasDraftChanges() &&
                                        campaignDraft?.founders &&
                                        campaignDraft.founders.length > 0 ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                                {/* Before Column */}
                                                <div>
                                                    <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                        Before
                                                    </h4>
                                                    <div className="grid grid-cols-1 gap-4">
                                                        {campaignDetails.founders.map(
                                                            (founder, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    className="p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm"
                                                                >
                                                                    <img
                                                                        src={normalizeAssetUrl(
                                                                            founder.file,
                                                                        )}
                                                                        alt={`Founder ${
                                                                            idx +
                                                                            1
                                                                        }`}
                                                                        className="h-60 w-full object-contain mb-2 rounded"
                                                                    />
                                                                    <p className="font-semibold">
                                                                        {
                                                                            founder.name
                                                                        }
                                                                    </p>
                                                                    <p className="text-sm text-gray-600">
                                                                        {
                                                                            founder.about
                                                                        }
                                                                    </p>
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                                {/* After Column */}
                                                <div>
                                                    <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                        After
                                                    </h4>
                                                    <div className="grid grid-cols-1 gap-4">
                                                        {campaignDraft.founders.map(
                                                            (founder, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    className="p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm"
                                                                >
                                                                    <img
                                                                        src={normalizeAssetUrl(
                                                                            founder.file,
                                                                        )}
                                                                        alt={`Founder ${
                                                                            idx +
                                                                            1
                                                                        }`}
                                                                        className="h-60 w-full object-contain mb-2 rounded"
                                                                    />
                                                                    <p className="font-semibold">
                                                                        {
                                                                            founder.name
                                                                        }
                                                                    </p>
                                                                    <p className="text-sm text-gray-600">
                                                                        {
                                                                            founder.about
                                                                        }
                                                                    </p>
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {campaignDetails.founders.map(
                                                    (founder, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm"
                                                        >
                                                            <img
                                                                src={normalizeAssetUrl(
                                                                    founder.file,
                                                                )}
                                                                alt={`Founder ${
                                                                    idx + 1
                                                                }`}
                                                                className="h-60 w-full object-contain mb-2 rounded"
                                                            />
                                                            <p className="font-semibold">
                                                                {founder.name}
                                                            </p>
                                                            <p className="text-sm text-gray-600">
                                                                {founder.about}
                                                            </p>
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                    </section>
                                )}

                            {/* Form C Documents */}
                            <section
                                id="form-c-documents-section"
                                className="mb-6"
                            >
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2">
                                        Form C
                                    </h4>
                                    <button
                                        onClick={() =>
                                            setShowFormCUploadBox(
                                                !showFormCUploadBox,
                                            )
                                        }
                                        className="flex items-center gap-1 bg-navy text-white px-3 py-1 rounded-lg text-sm font-medium hover:bg-navy-2 transition-colors"
                                    >
                                        {showFormCUploadBox
                                            ? "Cancel"
                                            : "Upload Form C"}{" "}
                                        {!showFormCUploadBox && (
                                            <FaPlus size={12} />
                                        )}
                                    </button>
                                </div>

                                {(campaignDraft?.legal_uploads
                                    ?.edgar_filing_url ||
                                    campaignDetails?.legal_uploads
                                        ?.edgar_filing_url) && (
                                    <div className="mb-4 rounded-lg border border-navy/20 bg-[#f0f6fb] px-4 py-3">
                                        <a
                                            href={
                                                campaignDraft?.legal_uploads
                                                    ?.edgar_filing_url ||
                                                campaignDetails.legal_uploads
                                                    .edgar_filing_url
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 font-medium text-navy underline"
                                        >
                                            <FaLink size={14} />
                                            View Form C SEC filing
                                        </a>
                                    </div>
                                )}

                                {/* Form C Upload Box */}
                                {showFormCUploadBox && (
                                    <div className="mb-6">
                                        <div className="rounded-xl border border-[#b6d6ee] bg-gradient-to-br from-[#F5F7FA] to-[#eaf6ff] shadow-sm p-4">
                                            <form onSubmit={handleFormCUpload}>
                                                <label
                                                    htmlFor="form-c-file-input"
                                                    className="block text-navy font-semibold mb-2 text-base tracking-wide"
                                                >
                                                    Upload Form C Documents
                                                </label>
                                                <p className="text-gray-600 text-sm mb-3">
                                                    Upload legal Form C
                                                    documents for this campaign.
                                                    Accepted formats: PDF, DOC,
                                                    DOCX, JPG, PNG.
                                                </p>
                                                <input
                                                    type="file"
                                                    id="form-c-file-input"
                                                    className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4 file:rounded-md
                            file:border-0 file:text-sm file:font-semibold
                            file:bg-navy file:text-white
                            hover:file:bg-navy-2
                            transition-colors"
                                                    onChange={(e) =>
                                                        setFormCFiles(
                                                            e.target.files,
                                                        )
                                                    }
                                                    multiple
                                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                    disabled={formCUploading}
                                                />
                                                <div className="flex justify-end mt-3">
                                                    <button
                                                        type="submit"
                                                        className={`bg-navy text-white px-5 py-2 rounded-lg font-semibold shadow hover:bg-navy-2 transition-colors duration-150 text-sm flex items-center gap-1 ${
                                                            formCUploading
                                                                ? "opacity-70 cursor-not-allowed"
                                                                : ""
                                                        }`}
                                                        disabled={
                                                            formCUploading
                                                        }
                                                    >
                                                        {formCUploading
                                                            ? "Uploading..."
                                                            : "Upload"}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                )}

                                {hasDraftChanges() &&
                                campaignDraft?.legal_uploads ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                        {/* Before Column */}
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                Before
                                            </h4>
                                            <div className="grid grid-cols-1 gap-4">
                                                {campaignDetails.legal_uploads &&
                                                campaignDetails.legal_uploads
                                                    .form_c &&
                                                Array.isArray(
                                                    campaignDetails
                                                        .legal_uploads.form_c,
                                                ) &&
                                                campaignDetails.legal_uploads
                                                    .form_c.length > 0 ? (
                                                    campaignDetails.legal_uploads.form_c.map(
                                                        (filePath, idx) => {
                                                            // Extract filename from path
                                                            // const fileName =
                                                            //     filePath
                                                            //         .split("/")
                                                            //         .pop();
                                                            // Use REACT_APP_BACKEND_URL for frontend
                                                            const url = getLegalDocumentUrl(filePath);
                                                            const isImage =
                                                                /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                                                    url,
                                                                );
                                                            const isPDF =
                                                                /\.pdf$/i.test(
                                                                    url,
                                                                );
                                                            // Highlight removed files in red
                                                            const isRemoved = !(
                                                                campaignDraft
                                                                    ?.legal_uploads
                                                                    ?.form_c &&
                                                                hasMatchingLegalDocument(
                                                                    campaignDraft.legal_uploads.form_c,
                                                                    filePath,
                                                                )
                                                            );
                                                            return (
                                                                <div
                                                                    key={idx}
                                                                    className={`p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm flex flex-col items-center min-h-[260px]${
                                                                        isRemoved
                                                                            ? " bg-red-100"
                                                                            : ""
                                                                    }`}
                                                                >
                                                                    {isImage ? (
                                                                        <img
                                                                            src={
                                                                                url
                                                                            }
                                                                            alt={`Document ${
                                                                                idx +
                                                                                1
                                                                            }`}
                                                                            className="h-40 w-40 object-contain mb-2 rounded border"
                                                                            onError={(
                                                                                e,
                                                                            ) => {
                                                                                e.target.onerror =
                                                                                    null;
                                                                                e.target.style.display =
                                                                                    "none";
                                                                            }}
                                                                        />
                                                                    ) : isPDF ? (
                                                                        <iframe
                                                                            src={
                                                                                url
                                                                            }
                                                                            title={`PDF Document ${
                                                                                idx +
                                                                                1
                                                                            }`}
                                                                            width="100%"
                                                                            height="180"
                                                                            className="border rounded mb-2"
                                                                        />
                                                                    ) : (
                                                                        <div className="text-blue-600 font-semibold mb-2 flex items-center justify-center h-40">
                                                                            <a
                                                                                href={
                                                                                    url
                                                                                }
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="flex flex-col items-center"
                                                                            >
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    className="h-10 w-10 mb-2"
                                                                                    fill="none"
                                                                                    viewBox="0 0 24 24"
                                                                                    stroke="currentColor"
                                                                                >
                                                                                    <path
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        strokeWidth={
                                                                                            2
                                                                                        }
                                                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                                                    />
                                                                                </svg>
                                                                                View
                                                                                Document
                                                                            </a>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        },
                                                    )
                                                ) : campaignDraft
                                                      ?.legal_uploads
                                                      ?.edgar_filing_url ||
                                                  campaignDetails
                                                      ?.legal_uploads
                                                      ?.edgar_filing_url ? null : (
                                                    <p className="text-gray-500 italic">
                                                        No legal documents
                                                        uploaded
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {/* After Column */}
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                After
                                            </h4>
                                            <div className="grid grid-cols-1 gap-4">
                                                {campaignDraft?.legal_uploads
                                                    ?.form_c &&
                                                Array.isArray(
                                                    campaignDraft.legal_uploads
                                                        .form_c,
                                                ) &&
                                                campaignDraft.legal_uploads
                                                    .form_c.length > 0 ? (
                                                    campaignDraft.legal_uploads.form_c.map(
                                                        (filePath, idx) => {
                                                            // Extract filename from path
                                                            // const fileName =
                                                            //     filePath
                                                            //         .split("/")
                                                            //         .pop();
                                                            // Use REACT_APP_BACKEND_URL for frontend
                                                            const url = getLegalDocumentUrl(filePath);
                                                            const isImage =
                                                                /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                                                    url,
                                                                );
                                                            const isPDF =
                                                                /\.pdf$/i.test(
                                                                    url,
                                                                );
                                                            // Highlight new files in blue
                                                            const isNew = !(
                                                                campaignDetails
                                                                    ?.legal_uploads
                                                                    ?.form_c &&
                                                                hasMatchingLegalDocument(
                                                                    campaignDetails.legal_uploads.form_c,
                                                                    filePath,
                                                                )
                                                            );
                                                            return (
                                                                <div
                                                                    key={idx}
                                                                    className={`p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm flex flex-col items-center min-h-[260px]${
                                                                        isNew
                                                                            ? " bg-[#C2D7E2]"
                                                                            : ""
                                                                    }`}
                                                                >
                                                                    {isImage ? (
                                                                        <img
                                                                            src={
                                                                                url
                                                                            }
                                                                            alt={`Document ${
                                                                                idx +
                                                                                1
                                                                            }`}
                                                                            className="h-40 w-40 object-contain mb-2 rounded border"
                                                                            onError={(
                                                                                e,
                                                                            ) => {
                                                                                e.target.onerror =
                                                                                    null;
                                                                                e.target.style.display =
                                                                                    "none";
                                                                            }}
                                                                        />
                                                                    ) : isPDF ? (
                                                                        <iframe
                                                                            src={
                                                                                url
                                                                            }
                                                                            title={`PDF Document ${
                                                                                idx +
                                                                                1
                                                                            }`}
                                                                            width="100%"
                                                                            height="180"
                                                                            className="border rounded mb-2"
                                                                        />
                                                                    ) : (
                                                                        <div className="text-blue-600 font-semibold mb-2 flex items-center justify-center h-40">
                                                                            <a
                                                                                href={
                                                                                    url
                                                                                }
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="flex flex-col items-center"
                                                                            >
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    className="h-10 w-10 mb-2"
                                                                                    fill="none"
                                                                                    viewBox="0 0 24 24"
                                                                                    stroke="currentColor"
                                                                                >
                                                                                    <path
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        strokeWidth={
                                                                                            2
                                                                                        }
                                                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                                                    />
                                                                                </svg>
                                                                                View
                                                                                Document
                                                                            </a>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        },
                                                    )
                                                ) : campaignDraft
                                                      ?.legal_uploads
                                                      ?.edgar_filing_url ||
                                                  campaignDetails
                                                      ?.legal_uploads
                                                      ?.edgar_filing_url ? null : (
                                                    <p className="text-gray-500 italic">
                                                        No legal documents
                                                        uploaded
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {campaignDetails.legal_uploads &&
                                        campaignDetails.legal_uploads.form_c &&
                                        Array.isArray(
                                            campaignDetails.legal_uploads
                                                .form_c,
                                        ) &&
                                        campaignDetails.legal_uploads.form_c
                                            .length > 0 ? (
                                            campaignDetails.legal_uploads.form_c.map(
                                                (filePath, idx) => {
                                                    // Extract filename from path
                                                    // const fileName = filePath
                                                    //     .split("/")
                                                    //     .pop();
                                                    // Use REACT_APP_BACKEND_URL for frontend
                                                    const url = getLegalDocumentUrl(filePath);

                                                    const isImage =
                                                        /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                                            url,
                                                        );
                                                    const isPDF =
                                                        /\.pdf$/i.test(url);

                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm flex flex-col items-center min-h-[260px]"
                                                        >
                                                            {isImage ? (
                                                                <img
                                                                    src={url}
                                                                    alt={`Document ${
                                                                        idx + 1
                                                                    }`}
                                                                    className="h-40 w-40 object-contain mb-2 rounded border"
                                                                    onError={(
                                                                        e,
                                                                    ) => {
                                                                        e.target.onerror =
                                                                            null;
                                                                        e.target.style.display =
                                                                            "none";
                                                                    }}
                                                                />
                                                            ) : isPDF ? (
                                                                <iframe
                                                                    src={url}
                                                                    title={`PDF Document ${
                                                                        idx + 1
                                                                    }`}
                                                                    width="100%"
                                                                    height="180"
                                                                    className="border rounded mb-2"
                                                                />
                                                            ) : (
                                                                <div className="text-blue-600 font-semibold mb-2 flex items-center justify-center h-40">
                                                                    <a
                                                                        href={
                                                                            url
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="flex flex-col items-center"
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-10 w-10 mb-2"
                                                                            fill="none"
                                                                            viewBox="0 0 24 24"
                                                                            stroke="currentColor"
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                strokeWidth={
                                                                                    2
                                                                                }
                                                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                                            />
                                                                        </svg>
                                                                        View
                                                                        Document
                                                                    </a>
                                                                </div>
                                                            )}

                                                            <div className="mt-2 flex justify-center">
                                                                <button
                                                                    onClick={() =>
                                                                        handleDeleteFormCDocument(
                                                                            getLegalDocumentPath(filePath),
                                                                        )
                                                                    }
                                                                    className="bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1 rounded-md text-xs flex items-center gap-1 transition-colors"
                                                                >
                                                                    <FaTimes
                                                                        size={
                                                                            12
                                                                        }
                                                                    />{" "}
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                },
                                            )
                                        ) : campaignDraft?.legal_uploads
                                              ?.edgar_filing_url ||
                                          campaignDetails?.legal_uploads
                                              ?.edgar_filing_url ? null : (
                                            <p className="text-gray-500 italic">
                                                No legal documents uploaded
                                            </p>
                                        )}
                                    </div>
                                )}
                            </section>

                            {/* Subscription Agreement Documents */}
                            <section
                                id="subscription-agreement-documents-section"
                                className="mb-6"
                            >
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2">
                                        Subscription Agreement
                                    </h4>
                                    <button
                                        onClick={() =>
                                            setShowSubAgreementUploadBox(
                                                !showSubAgreementUploadBox,
                                            )
                                        }
                                        className="flex items-center gap-1 bg-navy text-white px-3 py-1 rounded-lg text-sm font-medium hover:bg-navy-2 transition-colors"
                                    >
                                        {showSubAgreementUploadBox
                                            ? "Cancel"
                                            : "Upload Subscription Agreement"}{" "}
                                        {!showSubAgreementUploadBox && (
                                            <FaPlus size={12} />
                                        )}
                                    </button>
                                </div>

                                {/* Subscription Agreement Upload Box */}
                                {showSubAgreementUploadBox && (
                                    <div className="mb-6">
                                        <div className="rounded-xl border border-[#b6d6ee] bg-gradient-to-br from-[#F5F7FA] to-[#eaf6ff] shadow-sm p-4">
                                            <form
                                                onSubmit={
                                                    handleSubAgreementUpload
                                                }
                                            >
                                                <label
                                                    htmlFor="sub-agreement-file-input"
                                                    className="block text-navy font-semibold mb-2 text-base tracking-wide"
                                                >
                                                    Upload Subscription
                                                    Agreement Documents
                                                </label>
                                                <p className="text-gray-600 text-sm mb-3">
                                                    Upload Subscription
                                                    Agreement documents for this
                                                    campaign. Accepted formats:
                                                    PDF, DOC, DOCX, JPG, PNG.
                                                </p>
                                                <input
                                                    type="file"
                                                    id="sub-agreement-file-input"
                                                    className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4 file:rounded-md
                            file:border-0 file:text-sm file:font-semibold
                            file:bg-navy file:text-white
                            hover:file:bg-navy-2
                            transition-colors"
                                                    onChange={(e) =>
                                                        setSubAgreementFiles(
                                                            e.target.files,
                                                        )
                                                    }
                                                    multiple
                                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                    disabled={
                                                        subAgreementUploading
                                                    }
                                                />
                                                <div className="flex justify-end mt-3">
                                                    <button
                                                        type="submit"
                                                        className={`bg-navy text-white px-5 py-2 rounded-lg font-semibold shadow hover:bg-navy-2 transition-colors duration-150 text-sm flex items-center gap-1 ${
                                                            subAgreementUploading
                                                                ? "opacity-70 cursor-not-allowed"
                                                                : ""
                                                        }`}
                                                        disabled={
                                                            subAgreementUploading
                                                        }
                                                    >
                                                        {subAgreementUploading
                                                            ? "Uploading..."
                                                            : "Upload"}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                )}

                                {hasDraftChanges() &&
                                campaignDraft?.legal_uploads ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                        {/* Before Column */}
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                Before
                                            </h4>
                                            <div className="grid grid-cols-1 gap-4">
                                                {campaignDetails.legal_uploads &&
                                                campaignDetails.legal_uploads
                                                    .subscription_agreement &&
                                                Array.isArray(
                                                    campaignDetails
                                                        .legal_uploads
                                                        .subscription_agreement,
                                                ) &&
                                                campaignDetails.legal_uploads
                                                    .subscription_agreement
                                                    .length > 0 ? (
                                                    campaignDetails.legal_uploads.subscription_agreement.map(
                                                        (filePath, idx) => {
                                                            const url = getLegalDocumentUrl(filePath);
                                                            const isImage =
                                                                /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                                                    url,
                                                                );
                                                            const isPDF =
                                                                /\.pdf$/i.test(
                                                                    url,
                                                                );
                                                            const isRemoved = !(
                                                                campaignDraft
                                                                    ?.legal_uploads
                                                                    ?.subscription_agreement &&
                                                                hasMatchingLegalDocument(
                                                                    campaignDraft.legal_uploads.subscription_agreement,
                                                                    filePath,
                                                                )
                                                            );
                                                            return (
                                                                <div
                                                                    key={idx}
                                                                    className={`p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm flex flex-col items-center min-h-[260px]${isRemoved ? " bg-red-100" : ""}`}
                                                                >
                                                                    {isImage ? (
                                                                        <img
                                                                            src={
                                                                                url
                                                                            }
                                                                            alt={`Document ${idx + 1}`}
                                                                            className="h-40 w-40 object-contain mb-2 rounded border"
                                                                            onError={(
                                                                                e,
                                                                            ) => {
                                                                                e.target.onerror =
                                                                                    null;
                                                                                e.target.style.display =
                                                                                    "none";
                                                                            }}
                                                                        />
                                                                    ) : isPDF ? (
                                                                        <iframe
                                                                            src={
                                                                                url
                                                                            }
                                                                            title={`PDF Document ${idx + 1}`}
                                                                            width="100%"
                                                                            height="180"
                                                                            className="border rounded mb-2"
                                                                        />
                                                                    ) : (
                                                                        <div className="text-blue-600 font-semibold mb-2 flex items-center justify-center h-40">
                                                                            <a
                                                                                href={
                                                                                    url
                                                                                }
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="flex flex-col items-center"
                                                                            >
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    className="h-10 w-10 mb-2"
                                                                                    fill="none"
                                                                                    viewBox="0 0 24 24"
                                                                                    stroke="currentColor"
                                                                                >
                                                                                    <path
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        strokeWidth={
                                                                                            2
                                                                                        }
                                                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                                                    />
                                                                                </svg>
                                                                                View
                                                                                Document
                                                                            </a>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        },
                                                    )
                                                ) : (
                                                    <p className="text-gray-500 italic">
                                                        No subscription
                                                        agreement documents
                                                        uploaded
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {/* After Column */}
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                After
                                            </h4>
                                            <div className="grid grid-cols-1 gap-4">
                                                {campaignDraft?.legal_uploads
                                                    ?.subscription_agreement &&
                                                Array.isArray(
                                                    campaignDraft.legal_uploads
                                                        .subscription_agreement,
                                                ) &&
                                                campaignDraft.legal_uploads
                                                    .subscription_agreement
                                                    .length > 0 ? (
                                                    campaignDraft.legal_uploads.subscription_agreement.map(
                                                        (filePath, idx) => {
                                                            const url = getLegalDocumentUrl(filePath);
                                                            const isImage =
                                                                /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                                                    url,
                                                                );
                                                            const isPDF =
                                                                /\.pdf$/i.test(
                                                                    url,
                                                                );
                                                            const isNew = !(
                                                                campaignDetails
                                                                    ?.legal_uploads
                                                                    ?.subscription_agreement &&
                                                                hasMatchingLegalDocument(
                                                                    campaignDetails.legal_uploads.subscription_agreement,
                                                                    filePath,
                                                                )
                                                            );
                                                            return (
                                                                <div
                                                                    key={idx}
                                                                    className={`p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm flex flex-col items-center min-h-[260px]${isNew ? " bg-[#C2D7E2]" : ""}`}
                                                                >
                                                                    {isImage ? (
                                                                        <img
                                                                            src={
                                                                                url
                                                                            }
                                                                            alt={`Document ${idx + 1}`}
                                                                            className="h-40 w-40 object-contain mb-2 rounded border"
                                                                            onError={(
                                                                                e,
                                                                            ) => {
                                                                                e.target.onerror =
                                                                                    null;
                                                                                e.target.style.display =
                                                                                    "none";
                                                                            }}
                                                                        />
                                                                    ) : isPDF ? (
                                                                        <iframe
                                                                            src={
                                                                                url
                                                                            }
                                                                            title={`PDF Document ${idx + 1}`}
                                                                            width="100%"
                                                                            height="180"
                                                                            className="border rounded mb-2"
                                                                        />
                                                                    ) : (
                                                                        <div className="text-blue-600 font-semibold mb-2 flex items-center justify-center h-40">
                                                                            <a
                                                                                href={
                                                                                    url
                                                                                }
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="flex flex-col items-center"
                                                                            >
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    className="h-10 w-10 mb-2"
                                                                                    fill="none"
                                                                                    viewBox="0 0 24 24"
                                                                                    stroke="currentColor"
                                                                                >
                                                                                    <path
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        strokeWidth={
                                                                                            2
                                                                                        }
                                                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                                                    />
                                                                                </svg>
                                                                                View
                                                                                Document
                                                                            </a>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        },
                                                    )
                                                ) : (
                                                    <p className="text-gray-500 italic">
                                                        No subscription
                                                        agreement documents
                                                        uploaded
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {campaignDetails.legal_uploads &&
                                        campaignDetails.legal_uploads
                                            .subscription_agreement &&
                                        Array.isArray(
                                            campaignDetails.legal_uploads
                                                .subscription_agreement,
                                        ) &&
                                        campaignDetails.legal_uploads
                                            .subscription_agreement.length >
                                            0 ? (
                                            campaignDetails.legal_uploads.subscription_agreement.map(
                                                (filePath, idx) => {
                                                    const url = getLegalDocumentUrl(filePath);
                                                    const isImage =
                                                        /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                                            url,
                                                        );
                                                    const isPDF =
                                                        /\.pdf$/i.test(url);
                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm flex flex-col items-center min-h-[260px]"
                                                        >
                                                            {isImage ? (
                                                                <img
                                                                    src={url}
                                                                    alt={`Document ${idx + 1}`}
                                                                    className="h-40 w-40 object-contain mb-2 rounded border"
                                                                    onError={(
                                                                        e,
                                                                    ) => {
                                                                        e.target.onerror =
                                                                            null;
                                                                        e.target.style.display =
                                                                            "none";
                                                                    }}
                                                                />
                                                            ) : isPDF ? (
                                                                <iframe
                                                                    src={url}
                                                                    title={`PDF Document ${idx + 1}`}
                                                                    width="100%"
                                                                    height="180"
                                                                    className="border rounded mb-2"
                                                                />
                                                            ) : (
                                                                <div className="text-blue-600 font-semibold mb-2 flex items-center justify-center h-40">
                                                                    <a
                                                                        href={
                                                                            url
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="flex flex-col items-center"
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            className="h-10 w-10 mb-2"
                                                                            fill="none"
                                                                            viewBox="0 0 24 24"
                                                                            stroke="currentColor"
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                strokeWidth={
                                                                                    2
                                                                                }
                                                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                                            />
                                                                        </svg>
                                                                        View
                                                                        Document
                                                                    </a>
                                                                </div>
                                                            )}
                                                            <div className="mt-2 flex justify-center">
                                                                <button
                                                                    onClick={() =>
                                                                        handleDeleteSubAgreementDocument(
                                                                            getLegalDocumentPath(filePath),
                                                                        )
                                                                    }
                                                                    className="bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1 rounded-md text-xs flex items-center gap-1 transition-colors"
                                                                >
                                                                    <FaTimes
                                                                        size={
                                                                            12
                                                                        }
                                                                    />{" "}
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                },
                                            )
                                        ) : (
                                            <p className="text-gray-500 italic">
                                                No subscription agreement
                                                documents uploaded
                                            </p>
                                        )}
                                    </div>
                                )}
                            </section>

                            {/* Reviewed/Audited Financial Documents */}
                            <section className="mb-6">
                                <div className="mb-4 border-b border-gray-300 pb-2">
                                    <h4 className="text-lg font-semibold text-navy md:text-xl">
                                        Reviewed/Audited Financials
                                    </h4>
                                </div>
                                {(() => {
                                    const documents =
                                        campaignDraft?.legal_uploads
                                            ?.financials?.length > 0
                                            ? campaignDraft.legal_uploads
                                                  .financials
                                            : campaignDetails?.legal_uploads
                                                  ?.financials || [];

                                    return documents.length > 0 ? (
                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                            {documents.map((document, idx) => {
                                                const url =
                                                    getLegalDocumentUrl(
                                                        document,
                                                    );
                                                const isImage =
                                                    /\.(jpg|jpeg|png|gif|webp)(?:[?#].*)?$/i.test(
                                                        url,
                                                    );
                                                const isPDF =
                                                    /\.pdf(?:[?#].*)?$/i.test(
                                                        url,
                                                    );
                                                const name =
                                                    getLegalDocumentName(
                                                        document,
                                                        `Financial document ${idx + 1}`,
                                                    );

                                                return (
                                                    <div
                                                        key={`${getLegalDocumentPath(document)}-${idx}`}
                                                        className="flex min-h-[260px] flex-col items-center rounded-md border border-gray-200 bg-gray-50 p-3 shadow-sm"
                                                    >
                                                        {isImage ? (
                                                            <img
                                                                src={url}
                                                                alt={name}
                                                                className="mb-2 h-40 w-40 rounded border object-contain"
                                                            />
                                                        ) : isPDF ? (
                                                            <iframe
                                                                src={url}
                                                                title={name}
                                                                width="100%"
                                                                height="180"
                                                                className="mb-2 rounded border"
                                                            />
                                                        ) : (
                                                            <div className="flex h-40 items-center justify-center text-blue-600">
                                                                <FaEye size={32} />
                                                            </div>
                                                        )}
                                                        <a
                                                            href={url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="mt-2 max-w-full truncate font-medium text-navy underline"
                                                        >
                                                            {name}
                                                        </a>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="italic text-gray-500">
                                            No reviewed or audited financial
                                            documents uploaded
                                        </p>
                                    );
                                })()}
                            </section>

                            {/* Updates */}
                            <section className="mb-6">
                                <div className="flex items-center justify-between border-b border-gray-300 pb-2 mb-4">
                                    <h4 className="text-lg md:text-xl font-semibold text-navy">
                                        Campaign Updates
                                    </h4>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-gray-500">
                                            {getCampaignUpdates().length} update
                                            {getCampaignUpdates().length === 1
                                                ? ""
                                                : "s"}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowAdminUpdateForm(
                                                    (prev) => !prev,
                                                )
                                            }
                                            className="flex items-center gap-2 bg-navy text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-navy-2"
                                        >
                                            {showAdminUpdateForm ? (
                                                <FaTimes />
                                            ) : (
                                                <FaPlus />
                                            )}
                                            {showAdminUpdateForm
                                                ? "Cancel"
                                                : "Add Update"}
                                        </button>
                                    </div>
                                </div>

                                {showAdminUpdateForm && (
                                    <div className="mb-5 rounded-lg border border-[#c2d7e2] bg-[#F5F7FA] p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-navy mb-2">
                                                    Title
                                                </label>
                                                <input
                                                    type="text"
                                                    value={adminUpdateTitle}
                                                    onChange={(e) =>
                                                        setAdminUpdateTitle(
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="Update title"
                                                    className="w-full px-3 py-2 border border-[#c2d7e2] rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-navy mb-2">
                                                    URL
                                                </label>
                                                <div className="relative">
                                                    <FaLink className="absolute left-3 top-3 text-navy text-xs" />
                                                    <input
                                                        type="text"
                                                        value={adminUpdateUrl}
                                                        onChange={(e) =>
                                                            setAdminUpdateUrl(
                                                                e.target.value,
                                                            )
                                                        }
                                                        placeholder="https://example.com"
                                                        className="w-full pl-8 pr-3 py-2 border border-[#c2d7e2] rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <label className="block text-sm font-semibold text-navy mb-2">
                                                Description
                                            </label>
                                            <textarea
                                                rows="4"
                                                value={adminUpdateDescription}
                                                onChange={(e) =>
                                                    setAdminUpdateDescription(
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="Share the update details..."
                                                className="w-full px-3 py-2 border border-[#c2d7e2] rounded-lg text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-navy"
                                            />
                                        </div>
                                        {adminUpdateMedia && (
                                            <div className="mt-4 rounded-lg border border-[#d8e6ee] bg-white p-3">
                                                <p className="text-xs font-semibold text-navy mb-2">
                                                    Media Preview
                                                </p>
                                                {getMediaType(
                                                    adminUpdateMedia,
                                                ) === "image" ? (
                                                    <img
                                                        src={adminUpdateMedia}
                                                        alt="Update media preview"
                                                        className="max-h-40 max-w-full object-contain rounded"
                                                    />
                                                ) : getMediaType(
                                                      adminUpdateMedia,
                                                  ) === "video" ? (
                                                    <video
                                                        src={adminUpdateMedia}
                                                        controls
                                                        className="max-h-40 max-w-full rounded"
                                                    />
                                                ) : getMediaType(
                                                      adminUpdateMedia,
                                                  ) === "pdf" ? (
                                                    <iframe
                                                        src={adminUpdateMedia}
                                                        title="Update media preview"
                                                        width="100%"
                                                        height="150"
                                                        className="border rounded"
                                                    />
                                                ) : (
                                                    <a
                                                        href={adminUpdateMedia}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-navy underline text-sm"
                                                    >
                                                        Open media
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    ref={
                                                        adminUpdateFileInputRef
                                                    }
                                                    type="file"
                                                    className="hidden"
                                                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                                                    onChange={
                                                        handleAdminUpdateMediaChange
                                                    }
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        adminUpdateFileInputRef.current?.click()
                                                    }
                                                    disabled={adminUpdateSaving}
                                                    className="flex items-center gap-2 border border-navy text-navy px-3 py-2 text-sm rounded hover:bg-navy hover:text-white disabled:opacity-60"
                                                >
                                                    <FaUpload /> Media
                                                </button>
                                                {adminUpdateFileName && (
                                                    <span className="text-xs text-gray-600 truncate max-w-xs">
                                                        {adminUpdateFileName}
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleAddAdminUpdate}
                                                disabled={adminUpdateSaving}
                                                className="bg-navy text-white px-5 py-2 rounded-lg font-semibold shadow hover:bg-navy-2 transition-colors duration-150 text-sm disabled:opacity-60"
                                            >
                                                {adminUpdateSaving
                                                    ? "Saving..."
                                                    : "Save Update"}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {getCampaignUpdates().length === 0 ? (
                                    <p className="text-sm text-gray-500 italic">
                                        No campaign updates have been added yet.
                                    </p>
                                ) : hasDraftChanges() &&
                                  campaignDraft?.updates &&
                                  campaignDraft.updates.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                Before
                                            </h4>
                                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                                                {campaignDetails.updates.map(
                                                    (update, idx) => {
                                                        const isRemoved =
                                                            !campaignDraft.updates.some(
                                                                (u) =>
                                                                    JSON.stringify(
                                                                        u,
                                                                    ) ===
                                                                    JSON.stringify(
                                                                        update,
                                                                    ),
                                                            );
                                                        return (
                                                            <li
                                                                key={idx}
                                                                className={
                                                                    isRemoved
                                                                        ? "bg-red-100"
                                                                        : ""
                                                                }
                                                            >
                                                                {renderCampaignUpdate(
                                                                    update,
                                                                )}
                                                            </li>
                                                        );
                                                    },
                                                )}
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                After
                                            </h4>
                                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                                                {campaignDraft.updates.map(
                                                    (update, idx) => {
                                                        const isNew =
                                                            !campaignDetails.updates.some(
                                                                (u) =>
                                                                    JSON.stringify(
                                                                        u,
                                                                    ) ===
                                                                    JSON.stringify(
                                                                        update,
                                                                    ),
                                                            );
                                                        return (
                                                            <li
                                                                key={idx}
                                                                className={
                                                                    isNew
                                                                        ? "bg-[#C2D7E2]"
                                                                        : ""
                                                                }
                                                            >
                                                                {renderCampaignUpdate(
                                                                    update,
                                                                )}
                                                            </li>
                                                        );
                                                    },
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                ) : (
                                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                                        {campaignDetails.updates.map(
                                            (update, idx) => (
                                                <li key={idx}>
                                                    {renderCampaignUpdate(
                                                        update,
                                                    )}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                )}
                            </section>
                            {/* Background Image */}
                            {campaignDetails.campaign_background_image && (
                                <section className="mb-6">
                                    <h4 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                                        Campaign Background
                                    </h4>
                                    {hasDraftChanges() &&
                                    campaignDraft?.campaign_background_image ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
                                            {/* Before Column */}
                                            <div>
                                                <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                    Before
                                                </h4>
                                                <div className="w-full rounded-md border border-gray-200 p-3 bg-gray-50 shadow-sm">
                                                    {campaignDetails.campaign_background_image &&
                                                    !imageErrorMap[
                                                        normalizeAssetUrl(
                                                            campaignDetails.campaign_background_image,
                                                        )
                                                    ] ? (
                                                        <img
                                                            src={normalizeAssetUrl(
                                                                campaignDetails.campaign_background_image,
                                                            )}
                                                            alt="Campaign Background"
                                                            className="w-full h-72 object-contain rounded"
                                                            onError={(e) => {
                                                                e.target.onerror =
                                                                    null;
                                                                e.target.style.display =
                                                                    "none";
                                                                setImageErrorMap(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        [normalizeAssetUrl(
                                                                            campaignDetails.campaign_background_image,
                                                                        )]:
                                                                            true,
                                                                    }),
                                                                );
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="text-gray-400 italic text-center py-16">
                                                            No background image
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {/* After Column */}
                                            <div>
                                                <h4 className="text-lg font-semibold text-gray-700 mb-2">
                                                    After
                                                </h4>
                                                <div className="w-full rounded-md border border-gray-200 p-3 bg-gray-50 shadow-sm">
                                                    {campaignDraft.campaign_background_image &&
                                                    !imageErrorMap[
                                                        normalizeAssetUrl(
                                                            campaignDraft.campaign_background_image,
                                                        )
                                                    ] ? (
                                                        <img
                                                            src={normalizeAssetUrl(
                                                                campaignDraft.campaign_background_image,
                                                            )}
                                                            alt="Campaign Background (Draft)"
                                                            className="w-full h-72 object-contain rounded"
                                                            onError={(e) => {
                                                                e.target.onerror =
                                                                    null;
                                                                e.target.style.display =
                                                                    "none";
                                                                setImageErrorMap(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        [normalizeAssetUrl(
                                                                            campaignDraft.campaign_background_image,
                                                                        )]:
                                                                            true,
                                                                    }),
                                                                );
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="text-gray-400 italic text-center py-16">
                                                            No background image
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="w-full rounded-md border border-gray-200 p-3 bg-gray-50 shadow-sm">
                                            {campaignDetails.campaign_background_image &&
                                            !imageErrorMap[
                                                normalizeAssetUrl(
                                                    campaignDetails.campaign_background_image,
                                                )
                                            ] ? (
                                                <img
                                                    src={normalizeAssetUrl(
                                                        campaignDetails.campaign_background_image,
                                                    )}
                                                    alt="Campaign Background"
                                                    className="w-full h-72 object-contain rounded"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.style.display =
                                                            "none";
                                                        setImageErrorMap(
                                                            (prev) => ({
                                                                ...prev,
                                                                [normalizeAssetUrl(
                                                                    campaignDetails.campaign_background_image,
                                                                )]: true,
                                                            }),
                                                        );
                                                    }}
                                                />
                                            ) : (
                                                <div className="text-gray-400 italic text-center py-16">
                                                    No background image
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </section>
                            )}

                            {/* Close Button */}
                            <button
                                className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold transition"
                                onClick={() => closeCampaignModal()}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}

                {/* DataTable */}
                <DataTable
                    columns={columns}
                    data={filteredCampaigns}
                    onRowClicked={async (row) => {
                        setSelectedCampaign(row);
                        try {
                            console.error("Clicked on row:", row);
                            const res = await axios.get(
                                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${row.id}/with-draft`,
                                { withCredentials: true },
                            );
                            console.error(
                                "Campaign details response:",
                                res.data.campaign,
                            );

                            // Log specifically the on_hold_note
                            if (
                                res.data.campaign &&
                                res.data.campaign.status === "onhold"
                            ) {
                                console.error(
                                    "Campaign is on-hold, on_hold_note:",
                                    res.data.campaign.on_hold_note,
                                );
                                setDisplayedOnHoldNote(
                                    res.data.campaign.on_hold_note || "",
                                );
                            }

                            setCampaignDetails(
                                normalizeCampaignLegalUploads(
                                    res.data.campaign,
                                ),
                            );
                            setCampaignDraft(
                                normalizeCampaignLegalUploads(res.data.draft),
                            );
                            setAdminUpdateTitle("");
                            setAdminUpdateDescription("");
                            setAdminUpdateUrl("");
                            setAdminUpdateMedia("");
                            setAdminUpdateFileName("");
                            setShowAdminUpdateForm(false);
                            // Clear any lingering inline-edit state from other modals
                            setEditingInvestmentField(null);
                            setEditingInvestmentValue("");
                            setEditingNoteId(null);
                            setEditingNoteText("");
                            setShowModal(true);
                            fetchInternalNotes(row.id);
                        } catch {
                            notifyUser(null, "error", "Failed to fetch campaign details", "toast", null, null, null, null, null, false, false, "top-center");
                        }
                    }}
                    customStyles={{
                        ...customStyles,
                        table: {
                            style: {
                                borderRadius: "8px",
                                overflowX: "hidden",
                            },
                        },
                        rows: {
                            style: {
                                cursor: "pointer",
                                minHeight: "50px",
                                "&:hover": {
                                    backgroundColor: "#E9F4F9",
                                    borderLeft: "2px solid var(--navy)",
                                },
                            },
                        },
                    }}
                    conditionalRowStyles={[
                        {
                            // Deleted: red always takes precedence
                            when: (row) => row.is_deleted === 1,
                            style: {
                                backgroundColor: "#ffeaea",
                                fontWeight: "500",
                                boxShadow: "0 0 5px rgba(255, 77, 79, 0.15)",
                                transform: "translateX(2px)",
                                "&:hover": {
                                    backgroundColor: "#ffeaea",
                                    borderLeft: "2px solid #ff4d4f",
                                },
                            },
                        },
                        {
                            // Only selected (not deleted)
                            when: (row) =>
                                selectedCampaign &&
                                row.id === selectedCampaign.id &&
                                row.is_deleted !== 1,
                            style: {
                                backgroundColor: "#e6f7ff",
                                borderLeft: "4px solid var(--navy)",
                                color: "var(--navy)",
                                fontWeight: "500",
                                boxShadow: "0 0 5px rgb(var(--navy-rgb) / 0.2)",
                                transform: "translateX(2px)",
                                "&:hover": {
                                    backgroundColor: "#e6f7ff",
                                    borderLeft: "4px solid var(--navy)",
                                },
                            },
                        },
                    ]}
                    striped
                    pointerOnHover
                    pagination={false}
                />
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
                <span>
                    Showing{" "}
                    {campaigns.length === 0
                        ? 0
                        : (currentPage - 1) * perPage + 1}{" "}
                    to {Math.min(currentPage * perPage, campaigns.length)} of{" "}
                    {campaigns.length} records
                </span>
                <div className="flex gap-2 text-lg items-center">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                        className="disabled:opacity-40"
                    >
                        <FaAngleDoubleLeft />
                    </button>
                    <button
                        disabled={currentPage === 1}
                        onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        className="disabled:opacity-40"
                    >
                        <FaAngleLeft />
                    </button>
                    <span className="text-sm px-2">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        disabled={currentPage === totalPages}
                        onClick={() =>
                            setCurrentPage((prev) =>
                                Math.min(prev + 1, totalPages),
                            )
                        }
                        className="disabled:opacity-40"
                    >
                        <FaAngleRight />
                    </button>
                    <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className="disabled:opacity-40"
                    >
                        <FaAngleDoubleRight />
                    </button>
                </div>
            </div>

            {/* NC Document Selection Modal */}
            {/* Cancel Investments Modal */}
            {showCancelInvestmentsBox && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-red-700 flex items-center gap-2">
                                <FaBan className="text-red-500" /> Cancel All
                                Investments
                            </h3>
                            <button
                                className="text-gray-400 hover:text-gray-600 text-xl"
                                onClick={() => {
                                    setShowCancelInvestmentsBox(false);
                                    setCancelInvestmentReason("");
                                    setCancelInvestmentNotes("");
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-5 space-y-4">
                            <p className="text-sm text-gray-500">
                                This will cancel all active investments for this
                                campaign. Please provide a reason before
                                proceeding.
                            </p>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Reason{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. Campaign withdrawn by administrator"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                                    value={cancelInvestmentReason}
                                    onChange={(e) =>
                                        setCancelInvestmentReason(
                                            e.target.value,
                                        )
                                    }
                                    maxLength={500}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Notes{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    rows="4"
                                    placeholder="Any additional information regarding the cancellation..."
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 resize-none"
                                    value={cancelInvestmentNotes}
                                    onChange={(e) =>
                                        setCancelInvestmentNotes(e.target.value)
                                    }
                                    maxLength={1000}
                                />
                                <div className="flex justify-end mt-1">
                                    <span className="text-xs text-gray-400">
                                        {cancelInvestmentNotes.length}/1000
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
                            <button
                                className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors duration-150"
                                onClick={() => {
                                    setShowCancelInvestmentsBox(false);
                                    setCancelInvestmentReason("");
                                    setCancelInvestmentNotes("");
                                }}
                            >
                                Abort
                            </button>
                            <button
                                className="bg-red-600 text-white px-5 py-2 rounded-lg font-semibold shadow hover:bg-red-700 transition-colors duration-150 text-sm flex items-center gap-2"
                                onClick={() =>
                                    handleCancelCampaignInvestments(
                                        campaignDetails.id,
                                        cancelInvestmentReason,
                                        cancelInvestmentNotes,
                                    )
                                }
                            >
                                <FaBan /> Cancel Investments
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

CampaignApproval.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
    }),
};
export default CampaignApproval;
